/**
 * i18n Context für Planivio
 * 
 * ✅ v4.0.0 - Landing Page Updates
 *    - periodSoon Übersetzungen für Pricing
 *    - Tasks Feature auf Landing Page
 *    - Planivio Rebranding
 * ✅ v3.0.0 - Tasks als separates Modul
 * 
 * @version 4.0.0 - Landing Page Updates
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// ===========================
// ÜBERSETZUNGEN INLINE
// ===========================
const translations = {
  // ═══════════════════════════════════════════════════════════════════════════
  // 🇩🇪 DEUTSCH
  // ═══════════════════════════════════════════════════════════════════════════
  de: {
    app: {
      name: "Planivio",
      tagline: "Dein intelligenter Haushalts-Manager",
      loading: "Lädt Planivio...",
    },

    nav: {
      modules: {
        dashboard: "Dashboard",
        calendar: "Kalender",
        assistant: "Assistent",
        notes: "Notizen",
        tasks: "Aufgaben",
        shopping: "Einkauf",
        recipes: "Rezepte",
        household: "Haushalt",
        budget: "Budget"
      }
    },

    header: {
      logout: "Abmelden",
      myHousehold: "Mein Haushalt",
      user: "Benutzer",
      settings: "Einstellungen",
      profile: "Profil",
      login: "Anmelden"
    },

    auth: {
      login: "Anmelden",
      logout: "Abmelden",
      register: "Registrieren",
      getStarted: "Jetzt starten",
      welcomeBack: "Willkommen zurück"
    },

    mobile: {
      swipeHint: "Wischen (80px+) oder Pfeile zum Navigieren"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // 🤖 ASSISTANT
    // ═══════════════════════════════════════════════════════════════════════
    assistant: {
      title: "Planivio Assistent",
      subtitle: "Dein intelligenter Haushaltshelfer",
      thinking: "Denkt nach...",
      loading: "Lädt...",
      documents: "Dokumente",
      messages: "Nachrichten",
      welcome: "Wie kann ich dir helfen?",
      welcomeHint: "Schreibe eine Nachricht oder lade Dokumente hoch",
      pleaseLogin: "Bitte anmelden",
      suggestions: "Vorschläge",
      suggestion1: "Was kann ich heute kochen?",
      suggestion2: "Erstelle eine Einkaufsliste",
      suggestion3: "Gib mir Haushaltstipps",
      placeholder: "Nachricht eingeben...",
      send: "Senden",
      filesSelected: "Datei(en) ausgewählt",
      upload: "Hochladen",
      attachDocuments: "Dokumente hochladen",
      dropDocuments: "Dokumente hier ablegen",
      documentsUploaded: "Dokument(e) hochgeladen",
      uploadFailed: "Upload fehlgeschlagen",
      clearChat: "Chat leeren",
      clearDocuments: "Dokumente löschen",
      ragDocuments: "RAG-Dokumente",
      noResponse: "Keine Antwort erhalten",
      error: "Ein Fehler ist aufgetreten"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // 🌙 THEME
    // ═══════════════════════════════════════════════════════════════════════
    theme: {
      light: "Hell",
      dark: "Dunkel",
      system: "System",
      toggle: "Design wechseln",
      lightMode: "Hellmodus aktivieren",
      darkMode: "Dunkelmodus aktivieren"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // 🛒 SHOPPING
    // ═══════════════════════════════════════════════════════════════════════
    shopping: {
      title: "Einkaufslisten",
      newList: "Neue Liste",
      list: "Liste",
      lists: "Listen",
      fromRecipes: "aus Rezepten",
      createList: "Liste erstellen",
      editList: "Liste bearbeiten",
      deleteList: "Liste löschen",
      addItem: "Artikel hinzufügen",
      editItem: "Artikel bearbeiten",
      deleteItem: "Artikel entfernen",
      checkAll: "Alle abhaken",
      uncheckAll: "Alle zurücksetzen",
      clearChecked: "Erledigte entfernen",
      form: {
        name: "Listenname",
        namePlaceholder: "Name eingeben...",
        description: "Beschreibung",
        descriptionPlaceholder: "Optionale Beschreibung...",
        assignedTo: "Zugewiesen an",
        dueDate: "Fälligkeitsdatum",
        itemName: "Artikelname",
        itemNamePlaceholder: "Artikel eingeben...",
        quantity: "Menge",
        unit: "Einheit",
        category: "Kategorie",
        priority: "Priorität"
      },
      itemName: "Artikelname",
      quantity: "Menge",
      unit: "Einheit",
      category: "Kategorie",
      categories: {
        dairy: "Milchprodukte",
        bakery: "Brot & Backwaren",
        produce: "Obst & Gemüse",
        meat: "Fleisch & Fisch",
        frozen: "Tiefkühl",
        beverages: "Getränke",
        household: "Haushalt",
        other: "Sonstiges"
      },
      units: {
        piece: "Stück",
        liter: "L",
        gram: "g",
        kilogram: "kg",
        pack: "Packung",
        bottle: "Flasche"
      },
      priority: {
        high: "Hoch",
        normal: "Normal",
        medium: "Mittel",
        low: "Niedrig"
      },
      status: {
        items: "Artikel",
        checked: "erledigt",
        remaining: "übrig"
      },
      emptyList: "Keine Listen vorhanden",
      noItems: "Keine Artikel in dieser Liste",
      createFirstList: "Erstelle deine erste Einkaufsliste!",
      loading: "Lade Einkaufslisten...",
      tooManyAttempts: "Zu viele Ladeversuche. Bitte Seite neu laden.",
      notLoggedIn: "Nicht angemeldet. Bitte einloggen.",
      notAuthorized: "Nicht autorisiert. Bitte neu einloggen.",
      errorLoading: "Fehler beim Laden der Listen",
      errorCreating: "Fehler beim Erstellen",
      errorUpdating: "Fehler beim Aktualisieren",
      errorDeleting: "Fehler beim Löschen",
      deleteConfirm: "Liste wirklich löschen?",
      deleteItemConfirm: "Artikel löschen?",
      weeklyPurchase: "Wocheneinkauf",
      groceriesForWeek: "Lebensmittel für die ganze Woche"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // 🍳 RECIPES
    // ═══════════════════════════════════════════════════════════════════════
    recipes: {
      title: "Rezepte",
      recipesCount: "Rezepte",
      createRecipe: "Rezept erstellen",
      newRecipe: "Neues Rezept",
      editRecipe: "Rezept bearbeiten",
      addToList: "Zur Einkaufsliste",
      toShoppingList: "Zu Einkaufsliste",
      searchRecipes: "Rezepte durchsuchen",
      viewMode: {
        cards: "Karten",
        list: "Liste"
      },
      categories: {
        mainDish: "Hauptgericht",
        appetizer: "Vorspeise",
        dessert: "Dessert",
        snack: "Snack",
        breakfast: "Frühstück"
      },
      difficulty: {
        easy: "Leicht",
        medium: "Mittel",
        hard: "Schwer"
      },
      filters: {
        allCategories: "Alle Kategorien",
        allDifficulties: "Alle Schwierigkeiten"
      },
      ingredients: "Zutaten",
      ingredientsList: "Zutaten",
      instructions: "Zubereitung",
      servings: "Portionen",
      minutes: "Min",
      prepTime: "Zubereitungszeit",
      cookTime: "Kochzeit",
      form: {
        name: "Name",
        namePlaceholder: "Rezeptname...",
        description: "Beschreibung",
        descriptionPlaceholder: "Kurze Beschreibung...",
        category: "Kategorie",
        difficulty: "Schwierigkeit",
        servings: "Portionen",
        prepTime: "Vorbereitungszeit (Min)",
        cookTime: "Kochzeit (Min)",
        ingredients: "Zutaten",
        addIngredient: "Zutat hinzufügen",
        instructions: "Anleitung",
        addStep: "Schritt hinzufügen"
      },
      noRecipes: "Keine Rezepte vorhanden",
      noRecipesYet: "Noch keine Rezepte vorhanden",
      noRecipesFound: "Keine Rezepte gefunden",
      createFirstRecipe: "Erstelle dein erstes Rezept!",
      tryOtherFilters: "Versuche andere Filter",
      loading: "Lade Rezepte...",
      tooManyAttempts: "Zu viele Ladeversuche. Bitte Seite neu laden.",
      notLoggedIn: "Nicht angemeldet. Bitte einloggen.",
      howManyServings: "Für wie viele Portionen?",
      shoppingListCreated: "Einkaufsliste erstellt",
      errorCreating: "Fehler beim Erstellen",
      errorDeleting: "Fehler beim Löschen",
      errorCreatingShoppingList: "Fehler beim Erstellen der Einkaufsliste",
      confirmDelete: "Rezept löschen?"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // 📅 CALENDAR
    // ═══════════════════════════════════════════════════════════════════════
    calendar: {
      title: "Kalender",
      appointments: "Termine",
      views: {
        day: "Tag",
        week: "Woche",
        month: "Monat",
        agenda: "Agenda"
      },
      today: "Heute",
      new: "Neu",
      export: "Export",
      sync: "Sync",
      enableNotifications: "Erinnerungen aktivieren?",
      notificationHint: "Erhalten Sie Benachrichtigungen für anstehende Termine",
      activate: "Aktivieren",
      later: "Später",
      notificationsEnabled: "Benachrichtigungen aktiviert!",
      notificationsDenied: "Benachrichtigungen wurden abgelehnt.",
      loading: "Lade Kalender...",
      pleaseLogin: "Bitte einloggen um Termine zu sehen",
      noAppointmentsToExport: "Keine Termine zum Exportieren vorhanden",
      swipeHint: "Wischen zum Navigieren",
      errorLoading: "Fehler beim Laden der Termine",
      errorSaving: "Fehler beim Speichern",
      errorDeleting: "Fehler beim Löschen",
      confirmDelete: "Termin wirklich löschen?",
      newEvent: "Neuer Termin",
      editEvent: "Termin bearbeiten",
      eventTitle: "Titel",
      eventDate: "Datum",
      eventTime: "Uhrzeit",
      eventDescription: "Beschreibung",
      eventLocation: "Ort",
      allDay: "Ganztägig",
      reminder: "Erinnerung",
      recurring: "Wiederkehrend",
      recurrence: {
        none: "Keine Wiederholung",
        daily: "Täglich",
        weekly: "Wöchentlich",
        monthly: "Monatlich",
        yearly: "Jährlich"
      },
      noEvents: "Keine Termine"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // 💰 BUDGET
    // ═══════════════════════════════════════════════════════════════════════
    budget: {
      title: "Budget & Finanzen",
      tabs: {
        overview: "Übersicht",
        transactions: "Transaktionen",
        categories: "Kategorien",
        limits: "Limits"
      },
      income: "Einnahmen",
      expenses: "Ausgaben",
      balance: "Bilanz",
      savingsRate: "Sparrate",
      transaction: "Transaktion",
      dateRange: {
        currentWeek: "Aktuelle Woche",
        currentMonth: "Aktueller Monat",
        lastMonth: "Letzter Monat",
        lastYear: "Letztes Jahr",
        all: "Alle Zeit"
      },
      addTransaction: "Transaktion",
      addCategory: "Kategorie",
      addLimit: "Limit",
      noCategories: "Noch keine Kategorien vorhanden",
      setupCategoriesHint: "Erstelle 65+ Standard-Kategorien mit einem Klick!",
      setupCategories: "Standard-Kategorien erstellen",
      categoriesCreated: "Standard-Kategorien erstellt!",
      noTransactionsInPeriod: "Keine Transaktionen im ausgewählten Zeitraum",
      noLimits: "Keine Limits definiert",
      confirmSetupCategories: "Möchtest du 65+ Standard-Kategorien erstellen?",
      confirmDeleteCategory: "Kategorie löschen?",
      confirmDeleteTransaction: "Transaktion löschen?",
      confirmDeleteLimit: "Limit löschen?",
      loading: "Lade Budget-Daten...",
      pleaseLogin: "Bitte einloggen...",
      description: "Beschreibung",
      amount: "Betrag",
      date: "Datum",
      type: "Typ",
      category: "Kategorie",
      monthly: "Monatlich",
      yearly: "Jährlich",
      types: {
        income: "Einnahme",
        expense: "Ausgabe",
        all: "Alle"
      }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // 📝 NOTES
    // ═══════════════════════════════════════════════════════════════════════
    notes: {
      title: "Notizen",
      notes: "Notizen",
      newNote: "Neue Notiz",
      editNote: "Notiz bearbeiten",
      categories: {
        personal: "Persönlich",
        work: "Arbeit",
        ideas: "Ideen",
        important: "Wichtig"
      },
      filters: {
        allCategories: "Alle Kategorien"
      },
      form: {
        title: "Titel",
        titlePlaceholder: "Titel eingeben...",
        content: "Inhalt",
        contentPlaceholder: "Notiz eingeben...",
        category: "Kategorie"
      },
      noNotes: "Keine Notizen vorhanden",
      noResults: "Keine Ergebnisse gefunden",
      loading: "Lade Notizen...",
      errorLoading: "Fehler beim Laden",
      confirmDeleteNote: "Notiz löschen?",
      viewMode: {
        grid: "Raster",
        list: "Liste"
      }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // ✅ TASKS
    // ═══════════════════════════════════════════════════════════════════════
    tasks: {
      title: "Aufgaben",
      tasks: "Aufgaben",
      newTask: "Neue Aufgabe",
      editTask: "Aufgabe bearbeiten",
      categories: {
        personal: "Persönlich",
        work: "Arbeit",
        household: "Haushalt",
        shopping: "Einkauf"
      },
      priority: {
        low: "Niedrig",
        medium: "Mittel",
        high: "Hoch"
      },
      status: {
        open: "Offen",
        done: "Erledigt",
        overdue: "Überfällig",
        all: "Alle"
      },
      filters: {
        allCategories: "Alle Kategorien",
        allPriorities: "Alle Prioritäten",
        allStatus: "Alle Status"
      },
      form: {
        title: "Titel",
        titlePlaceholder: "Aufgabe eingeben...",
        description: "Beschreibung",
        descriptionPlaceholder: "Details zur Aufgabe...",
        category: "Kategorie",
        priority: "Priorität",
        dueDate: "Fälligkeitsdatum",
        dueTime: "Uhrzeit",
        assignedTo: "Zugewiesen an"
      },
      noTasks: "Keine Aufgaben vorhanden",
      noResults: "Keine Ergebnisse gefunden",
      loading: "Lade Aufgaben...",
      errorLoading: "Fehler beim Laden",
      confirmDeleteTask: "Aufgabe löschen?",
      viewMode: {
        grid: "Raster",
        list: "Liste"
      },
      stats: {
        total: "Gesamt",
        open: "Offen",
        completed: "Erledigt",
        highPriority: "Hohe Priorität",
        today: "Heute fällig"
      }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // 🏠 HOUSEHOLD
    // ═══════════════════════════════════════════════════════════════════════
    household: {
      title: "Haushalts-Management",
      members: "Mitglieder",
      tabs: {
        members: "Mitglieder",
        statistics: "Statistiken"
      },
      inviteMember: "Mitglied einladen",
      newMember: "Neues Mitglied",
      addMember: "Mitglied hinzufügen",
      remove: "Entfernen",
      settings: "Einstellungen",
      roles: {
        admin: "Administrator",
        member: "Mitglied",
        child: "Kind",
        guest: "Gast"
      },
      form: {
        name: "Name",
        namePlaceholder: "Name eingeben...",
        role: "Rolle",
        avatar: "Avatar"
      },
      statistics: "Haushalts-Statistiken",
      stats: {
        shoppingLists: "Einkaufslisten",
        notes: "Notizen",
        tasks: "Aufgaben",
        recipes: "Rezepte",
        appointments: "Termine"
      },
      loading: "Lade Haushalt...",
      noMembers: "Noch keine Mitglieder vorhanden",
      errorLoading: "Fehler beim Laden",
      notLoggedIn: "Nicht angemeldet",
      confirmRemoveMember: "Mitglied entfernen?"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // 📊 DASHBOARD
    // ═══════════════════════════════════════════════════════════════════════
    dashboard: {
      title: "Dashboard",
      welcome: "Willkommen",
      welcomeBack: "Willkommen, {{name}}!",
      subtitle: "Hier ist euer Haushalt im Überblick",
      open: "offen",
      today: "Heute",
      tomorrow: "Morgen",
      balance: "Bilanz",
      current: "Aktuell",
      widgets: {
        shopping: "Einkaufsliste",
        tasks: "Aufgaben",
        appointments: "Termine",
        budget: "Budget"
      },
      shoppingList: "Einkaufsliste",
      task: "Aufgabe",
      appointment: "Termin",
      allDay: "Ganztägig",
      of: "von",
      viewAll: "Alle Listen",
      viewAllTasks: "Alle Aufgaben",
      viewAllAppointments: "Alle Termine",
      manageBudget: "Budget verwalten",
      createBudget: "Budget erstellen",
      noLists: "Keine Listen",
      noOpenTasks: "Keine offenen Aufgaben",
      noAppointments: "Keine Termine",
      noBudget: "Kein Budget",
      quickAccess: "Schnellzugriff",
      quickActions: "Schnellaktionen",
      recentActivity: "Letzte Aktivitäten",
      loading: "Lade Dashboard...",
      retry: "Erneut versuchen",
      noToken: "Kein Token erhalten",
      stats: {
        openTasks: "Offene Aufgaben",
        shoppingItems: "Einkaufsartikel",
        upcomingEvents: "Anstehende Termine",
        monthlyBudget: "Monatsbudget"
      }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // 🔧 COMMON
    // ═══════════════════════════════════════════════════════════════════════
    common: {
      save: "Speichern",
      cancel: "Abbrechen",
      delete: "Löschen",
      edit: "Bearbeiten",
      add: "Hinzufügen",
      close: "Schließen",
      confirm: "Bestätigen",
      back: "Zurück",
      next: "Weiter",
      search: "Suchen",
      filter: "Filtern",
      sort: "Sortieren",
      loading: "Lädt...",
      error: "Fehler",
      success: "Erfolg",
      warning: "Warnung",
      info: "Info",
      yes: "Ja",
      no: "Nein",
      all: "Alle",
      none: "Keine",
      required: "Pflichtfeld",
      optional: "Optional"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // 📄 PAGES
    // ═══════════════════════════════════════════════════════════════════════
    pages: {
      landing: {
        hero: {
          title: "Dein Haushalt. Perfekt organisiert.",
          subtitle: "Planivio vereint Einkaufslisten, Rezepte, Kalender und Budget in einer App."
        },
        cta: "Kostenlos starten"
      },
      features: { 
        title: "Funktionen",
        // Navigation & Hero
        nav: {
          backHome: "Zurück zur Startseite",
          startNow: "Jetzt starten"
        },
        hero: {
          title: "Alle Funktionen auf einen Blick",
          subtitle: "Entdecke, wie Planivio deinen Familienalltag vereinfacht.",
          allAvailable: "Alle Module bereits verfügbar"
        },
        badge: {
          allAvailable: "Alle Module verfügbar"
        },
        screenshot: {
          comingSoon: "Screenshot kommt bald"
        },
        useCases: {
          title: "Anwendungsfälle"
        },
        // Einführungssektion
        intro: {
          problem: {
            title: "Kennst du das?",
            text: "Termine im Kopf, Einkaufsliste auf dem Zettel, Rezepte im Browser, Budget in Excel, To-Dos im Chat. Du jonglierst täglich 5-7 Apps – und trotzdem vergisst jemand die Milch. Das ist Mental Load. Und in 71% der Haushalte trägt eine Person diese unsichtbare Last allein."
          },
          solution: {
            title: "PLANIVIO ändert das.",
            text: "Eine Plattform. Alles vernetzt. Die KI erkennt Zusammenhänge automatisch: Geburtstag im Kalender → Kuchenrezept → Zutaten auf der Einkaufsliste → bestes Angebot gefunden. Weniger Stress. Mehr Zeit. Für das, was wirklich zählt."
          },
          stats: {
            mentalLoad: "der Haushalte tragen die Last allein",
            apps: "Apps jonglierst du täglich",
            households: "Haushalte in Deutschland"
          },
          flow: {
            calendar: "Kalender",
            recipes: "Rezept",
            shopping: "Einkauf",
            budget: "Angebot"
          },
          trust: {
            euHosting: "EU-Hosting",
            dsgvo: "DSGVO-konform",
            madeInGermany: "Made in Germany"
          }
        },
        cta: {
          title: "Bereit für mehr Organisation?",
          subtitle: "Starte jetzt kostenlos und entdecke alle Funktionen.",
          feedbackHelps: "Dein Feedback hilft uns, Planivio noch besser zu machen!",
          startNow: "Jetzt kostenlos starten"
        },
        // Module
        modules: {
          // ✅ NEU: Dashboard Modul
          dashboard: {
            title: "Dashboard",
            tagline: "Deine Kommandozentrale",
            desc: "Das Dashboard ist dein zentraler Anlaufpunkt. Hier siehst du auf einen Blick alle wichtigen Termine, Aufgaben und Einkäufe des Tages.",
            features: {
              overview: "Tagesübersicht mit anstehenden Terminen und Aufgaben",
              quickActions: "Ein-Klick-Zugang zu häufigen Aktionen",
              widgets: "Kompakte Vorschau aller Module",
              stats: "Statistik-Karten mit schnellen Zahlen",
              greeting: "Personalisierte Begrüßung"
            },
            useCases: {
              morning: "Starte jeden Morgen mit einem klaren Überblick über deinen Tag",
              quickAccess: "Schneller Zugriff auf alles Wichtige ohne langes Suchen"
            },
            tutorial: {
              title: "📊 So nutzt du das Dashboard",
              steps: {
                step1: {
                  title: "🌅 Mamas Morgen-Routine",
                  desc: "👩 Mama Müller trinkt ihren Kaffee und öffnet Planivio. Auf dem Dashboard sieht sie sofort: 📅 3 Termine heute, ✅ 5 offene Aufgaben, 🛒 12 Artikel auf der Einkaufsliste. 'Perfekt, so vergesse ich nichts!' denkt sie und plant ihren Tag."
                },
                step2: {
                  title: "👨 Papas schneller Überblick",
                  desc: "👨 Papa Müller hat wenig Zeit vor der Arbeit. Er tippt auf das Kalender-Widget und sieht: 🏥 10:00 Arzttermin für 👧 Lisa. 'Fast vergessen!' Er setzt sich eine Erinnerung und ist beruhigt. Jedes Widget ist ein Shortcut zum entsprechenden Modul."
                },
                step3: {
                  title: "👧 Lisa trägt etwas ein",
                  desc: "👧 Lisa (12) will zum Schwimmtraining. Sie tippt auf ➕ 'Neuer Termin' direkt vom Dashboard - kein Umweg nötig! Auch für ➕ Aufgaben, ➕ Einkäufe oder ➕ Notizen gibt es Schnellaktionen. Die ganze Familie kann so blitzschnell Dinge eintragen."
                },
                step4: {
                  title: "🌙 Abends zusammen planen",
                  desc: "👨‍👩‍👧‍👦 Am Abend schaut die Familie gemeinsam auf die Timeline: 🕐 08:00 Schule, 🕙 10:00 Arzt, 🕑 14:00 Schwimmtraining, 🕕 18:00 Abendessen. 'So wissen alle, was morgen ansteht!' sagt 👩 Mama zufrieden."
                }
              }
            }
          },
          shopping: {
            title: "Einkaufslisten",
            tagline: "Nie wieder etwas vergessen",
            desc: "Erstelle und teile Einkaufslisten mit deiner Familie. Artikel werden automatisch kategorisiert und synchronisiert.",
            features: {
              categorization: "Automatische Kategorisierung nach Supermarkt-Abteilungen",
              multipleLists: "Mehrere Listen für verschiedene Läden",
              sync: "Echtzeit-Synchronisation mit allen Familienmitgliedern",
              offline: "Offline-Modus für den Einkauf",
              recurring: "Wiederkehrende Artikel automatisch hinzufügen"
            },
            useCases: {
              family: "Die ganze Familie kann Artikel hinzufügen - Papa sieht sofort, was Mama eingetragen hat",
              mobile: "Im Supermarkt einfach abhaken - auch ohne Internet"
            },
            tutorial: {
              title: "🛒 So nutzt du die Einkaufslisten",
              steps: {
                step1: {
                  title: "📝 Mama erstellt die Wochenliste",
                  desc: "👩 Mama Müller plant das Wochenende. Sie tippt auf ➕ 'Neue Liste' und nennt sie '🎉 Grillparty Samstag'. Für verschiedene Anlässe kann sie separate Listen anlegen: 🥬 Wocheneinkauf, 🔨 Baumarkt, 🎂 Geburtstag..."
                },
                step2: {
                  title: "👧 Lisa fügt Artikel hinzu",
                  desc: "👧 Lisa öffnet die Einkaufsliste und tippt: 'Milch' → 🥛 wird automatisch ins Kühlregal sortiert! Sie fügt hinzu: 🍞 Brot (Backwaren), 🧀 Käse (Kühlregal), 🍎 Äpfel (Obst). '2x Milch' oder '500g Hackfleisch' für Mengen eingeben."
                },
                step3: {
                  title: "👨 Papa im Supermarkt",
                  desc: "👨 Papa steht im REWE. Er öffnet die Liste und hakt ab: ✅ Milch... ✅ Brot... 'Oh, kein WLAN hier!' Kein Problem - 📱 die App funktioniert offline! Sobald er wieder online ist, synchronisiert sich alles automatisch."
                },
                step4: {
                  title: "🔄 Familie sieht alles live",
                  desc: "👩 Mama sitzt zuhause und sieht: 'Oh, 👨 Papa hat die Milch schon!' Sie fügt schnell '🧈 Butter' hinzu - 👨 Papa sieht es sofort auf seinem Handy im Supermarkt! 💡 Echtzeit-Sync für die ganze Familie."
                },
                step5: {
                  title: "🏠 Nach dem Einkauf",
                  desc: "👨 Papa kommt heim. 👩 Mama tippt auf 'Erledigte entfernen' - die Liste ist wieder leer für nächste Woche. Die Statistik zeigt: 💰 'Diese Woche 12,50€ gespart durch Angebote!' Die Familie freut sich."
                }
              }
            }
          },
          recipes: {
            title: "Rezepte",
            tagline: "Deine Lieblingsrezepte an einem Ort",
            desc: "Speichere Familienrezepte, importiere aus dem Web und füge Zutaten direkt zur Einkaufsliste hinzu.",
            features: {
              create: "Eigene Rezepte erstellen und bearbeiten",
              toShopping: "Zutaten mit einem Klick zur Einkaufsliste",
              categories: "Kategorien und Tags für schnelles Finden",
              portions: "Portionen anpassen - Mengen werden umgerechnet",
              share: "Rezepte mit Familie und Freunden teilen"
            },
            useCases: {
              grandma: "Omas Geheimrezept digital aufbewahren und nie wieder verlieren",
              shopping: "Sonntagsbraten geplant? Alle Zutaten automatisch auf der Einkaufsliste"
            },
            tutorial: {
              title: "🍳 So nutzt du die Rezepte",
              steps: {
                step1: {
                  title: "👵 Omas Geheimrezept sichern",
                  desc: "👩 Mama besucht 👵 Oma und bekommt endlich das Rezept für den berühmten Apfelkuchen! Sie tippt auf ➕ 'Neues Rezept': Name, Kategorie 🍰 Kuchen, Zeit ⏱️ 60 Min, Schwierigkeit ⭐⭐ Mittel. 'Jetzt geht es nie wieder verloren!'"
                },
                step2: {
                  title: "📝 Zutaten eintragen",
                  desc: "👩 Mama tippt die Zutaten ein: 🍎 5 Äpfel, 🥚 3 Eier, 🧈 100g Butter, 🌾 200g Mehl, 🍬 150g Zucker. 💡 Tipp: Mit Mengenangaben wie '500g Hackfleisch' funktioniert später die automatische Umrechnung!"
                },
                step3: {
                  title: "👨‍🍳 Schritt für Schritt",
                  desc: "'Wie war das nochmal?' 👨 Papa will den Kuchen nachmachen. Er öffnet das Rezept und folgt den Schritten: 1️⃣ Teig rühren → 2️⃣ Äpfel schneiden → 3️⃣ Zusammenfügen → 4️⃣ 45 Min backen. 🎉 Perfekt gelungen!"
                },
                step4: {
                  title: "👨‍👩‍👧‍👦 Mehr Gäste? Kein Problem!",
                  desc: "🎉 Die Müllers erwarten 8 Gäste statt 4! 👩 Mama ändert die Portionen von 4 auf 8 - alle Mengen werden automatisch verdoppelt: 🍎 10 Äpfel, 🥚 6 Eier, 🧈 200g Butter. Keine Kopfrechnen mehr!"
                },
                step5: {
                  title: "🛒 Direkt zur Einkaufsliste",
                  desc: "👩 Mama tippt auf '🛒 Zur Einkaufsliste' - alle Zutaten landen automatisch auf der Einkaufsliste! 👨 Papa sieht sie sofort auf seinem Handy. 'Ich bringe die Äpfel mit!' schreibt er. So einfach geht Einkaufsplanung."
                },
                step6: {
                  title: "🌍 Rezepte aus der Community",
                  desc: "👧 Lisa stöbert in der Community: ⭐⭐⭐⭐⭐ 'Schnelle Pasta für Kinder', ⭐⭐⭐⭐ 'Veganer Burger'. Sie tippt auf ❤️ und das Rezept landet in der Familiensammlung. 'Mama, können wir das mal machen?'"
                }
              }
            }
          },
          ai: {
            title: "KI-Assistent",
            tagline: "Dein smarter Haushaltshelfer",
            desc: "Frag den KI-Assistenten nach Rezeptideen, Meal-Planning oder Haushaltstipps.",
            features: {
              suggestions: "Rezeptvorschläge basierend auf vorhandenen Zutaten",
              mealPlanning: "Automatische Wochenplanung für Mahlzeiten",
              tips: "Personalisierte Haushaltstipps",
              optimization: "Einkaufslisten-Optimierung nach Supermarkt-Layout",
              natural: "Natürliche Spracheingabe - frag einfach!"
            },
            useCases: {
              ingredients: "\"Was kann ich mit Kartoffeln, Käse und Schinken kochen?\" - sofort Ideen bekommen",
              planning: "\"Plane gesunde Mahlzeiten für die Woche\" - fertig ist der Essensplan"
            },
            tutorial: {
              title: "🤖 So nutzt du den KI-Assistenten",
              steps: {
                step1: {
                  title: "😩 Mamas Alltagsproblem",
                  desc: "👩 Mama steht ratlos vor dem Kühlschrank: 'Nur noch Reis, Hähnchen und Paprika... Was koche ich bloß?' Sie tippt auf 🤖 und fragt den Assistenten. Der antwortet sofort mit drei passenden Rezeptideen!"
                },
                step2: {
                  title: "💬 Einfach fragen",
                  desc: "👨 Papa tippt: 'Was kann ich mit dem machen, was wir haben?' Der Assistent versteht natürliche Sprache! Andere Fragen: 'Gib mir 5 schnelle Abendessen', 'Wie lange hält Hackfleisch?', 'Plane das Essen für die Woche'."
                },
                step3: {
                  title: "🍛 Sofort Ideen bekommen",
                  desc: "🤖 Der Assistent antwortet: '🍛 Gebratener Reis (20 Min, einfach), 🍲 Hähnchen-Paprika-Pfanne (25 Min), 🥘 Asia-Bowl (30 Min).' 👩 Mama tippt auf ein Rezept - alle Schritte werden angezeigt. 'Perfekt, das machen wir!'"
                },
                step4: {
                  title: "📅 Wochenplan erstellen lassen",
                  desc: "👨‍👩‍👧‍👦 Sonntagabend: 'Was essen wir diese Woche?' 👩 Mama fragt den Assistenten: 'Plane Essen für 4 Personen, unter 30 Min pro Gericht.' 🤖 Antwort: Mo 🍝 Pasta, Di 🥗 Salat, Mi 🍲 Suppe, Do 🍕 Pizza, Fr 🐟 Fisch... Fertig geplant!"
                },
                step5: {
                  title: "💡 Hilfe im Haushalt",
                  desc: "👧 Lisa: 'Mama, ich hab Cola auf dem Teppich verschüttet!' 😱 Mama fragt schnell den Assistenten: 'Wie entferne ich Cola-Flecken?' 🤖 Antwort: 'Sofort mit kaltem Wasser abtupfen, dann...' Der Assistent hilft bei allem - von Flecken bis Sparentipps!"
                }
              }
            }
          },
          calendar: {
            title: "Kalender",
            tagline: "Familie im Blick",
            desc: "Ein gemeinsamer Kalender für alle Termine, Aktivitäten und Erinnerungen der Familie.",
            features: {
              shared: "Gemeinsamer Familienkalender",
              colorCoding: "Farbcodierung pro Familienmitglied",
              reminders: "Erinnerungen per Push-Notification",
              recurring: "Wiederkehrende Termine (Müllabfuhr, Sportverein...)",
              views: "Tages-, Wochen- und Monatsansicht"
            },
            useCases: {
              appointments: "Arzttermine, Elternabende, Sporttraining - alles auf einen Blick",
              birthdays: "Nie wieder einen Geburtstag vergessen"
            },
            tutorial: {
              title: "📅 So nutzt du den Kalender",
              steps: {
                step1: {
                  title: "😰 Das Chaos der Müllers",
                  desc: "'Wann war nochmal der Elternabend?' 👨 Papa und 👩 Mama stehen ratlos da. Nie wieder! Im gemeinsamen Kalender sehen jetzt alle sofort: 👩 Mamas Termine (🔵 blau), 👨 Papas Termine (🟢 grün), 👧 Lisas Aktivitäten (🟣 lila)."
                },
                step2: {
                  title: "👀 Verschiedene Ansichten",
                  desc: "👩 Mama mag die Monatsansicht für den Überblick. 👨 Papa bevorzugt die Woche für seine Arbeitstermine. 👧 Lisa schaut in die Tagesansicht, um ihre Schulstunden zu planen. Jeder wählt, was am besten passt!"
                },
                step3: {
                  title: "➕ Termin eintragen",
                  desc: "👧 Lisa hat einen 🏥 Arzttermin. Sie tippt auf ➕ und trägt ein: Titel, Datum 17.01., Zeit 10:00-11:00, Ort 'Dr. Schmidt'. Sie wählt 🟣 lila als ihre Farbe. Sofort sehen 👨 Papa und 👩 Mama den Termin auch!"
                },
                step4: {
                  title: "🔄 Regelmäßige Termine",
                  desc: "👧 Lisa hat jeden Mittwoch 🏊 Schwimmtraining. Statt 52x einzutragen, aktiviert sie 'Wiederholen: Wöchentlich'. 🗑️ Müllabfuhr? Alle 2 Wochen. 🎂 Omas Geburtstag? Jährlich. Einmal einrichten, nie wieder vergessen!"
                },
                step5: {
                  title: "🔔 Erinnerung hilft",
                  desc: "👨 Papa vergisst oft Termine. Er stellt für den Elternabend eine Erinnerung: 📱 1 Tag vorher und 1 Stunde vorher. Am Tag piept sein Handy: 'Morgen: Elternabend!' Er ist vorbereitet und 👩 Mama ist zufrieden."
                },
                step6: {
                  title: "📱 Überall synchronisiert",
                  desc: "👩 Mama nutzt auch Google Calendar auf der Arbeit. Kein Problem! Sie exportiert den Planivio-Kalender - alle Termine erscheinen auch dort. 👨‍👩‍👧‍👦 Die Familie ist jetzt immer und überall synchron!"
                }
              }
            }
          },
          budget: {
            title: "Budget",
            tagline: "Finanzen im Griff",
            desc: "Behalte den Überblick über Haushaltsausgaben und spare gezielt für gemeinsame Ziele.",
            features: {
              categorize: "Ausgaben nach Kategorien erfassen",
              budgets: "Monatsbudgets festlegen und überwachen",
              charts: "Übersichtliche Diagramme und Statistiken",
              goals: "Sparziele definieren und verfolgen",
              stats: "Vergleich mit Vormonaten"
            },
            useCases: {
              overview: "Endlich wissen, wohin das Geld jeden Monat geht",
              vacation: "Gemeinsam auf den Sommerurlaub sparen"
            },
            tutorial: {
              title: "💰 So nutzt du das Budget",
              steps: {
                step1: {
                  title: "😟 Wo bleibt das Geld?",
                  desc: "'Am Monatsende ist nie Geld übrig!' klagt 👩 Mama. Sie öffnet das Budget-Modul und sieht zum ersten Mal klar: 📊 Einnahmen 3.500€, Ausgaben 2.890€. 'Endlich verstehe ich, wohin unser Geld geht!'"
                },
                step2: {
                  title: "🧾 Ausgaben erfassen",
                  desc: "👨 Papa kommt vom Einkaufen: 45,99€ bei REWE. Er tippt auf ➕, wählt Betrag und Kategorie 🛒 Lebensmittel. 'Wocheneinkauf' als Beschreibung. Fertig! Jede Ausgabe wird so einfach festgehalten."
                },
                step3: {
                  title: "🔍 Die Überraschung",
                  desc: "👩 Mama schaut die Statistik an: 🛒 Lebensmittel 45%, 🚗 Auto 22%, 🎉 Freizeit 15%... 'Was? Wir geben so viel für Freizeit aus?' Die farbige Übersicht zeigt, wo das Geld wirklich hingeht. Augenöffnend!"
                },
                step4: {
                  title: "🎯 Budget setzen",
                  desc: "👨‍👩‍👧‍👦 Familienrat: 'Maximal 2.500€ diesen Monat!' Das Budget ist gesetzt. Der Fortschrittsbalken zeigt: 75% verbraucht, noch 625€ für 8 Tage. 'Das schaffen wir!' sagt 👧 Lisa motiviert."
                },
                step5: {
                  title: "📈 Trends erkennen",
                  desc: "👩 Mama vergleicht die Monate: Dezember war teuer 🎄, Januar sparsamer. 'Im Februar haben wir 15% weniger ausgegeben!' Die Familie lernt aus den Trends und plant besser."
                },
                step6: {
                  title: "🏖️ Gemeinsam sparen",
                  desc: "👧 Lisa träumt vom 🏖️ Sommerurlaub. Sie erstellt ein Sparziel: 2.000€ bis Juni. Jeden Monat legt die Familie etwas zurück. Der Balken wächst: 60% erreicht! 'Noch 4 Monate, wir schaffen das!' 🎉"
                }
              }
            }
          },
          notes: {
            title: "Notizen",
            tagline: "Wichtiges festhalten",
            desc: "Geteilte Notizen für alles, was die Familie wissen sollte.",
            features: {
              shared: "Gemeinsame Notizen für alle",
              folders: "Ordner für bessere Organisation",
              search: "Volltextsuche in allen Notizen",
              attachments: "Bilder und Dateien anhängen",
              pin: "Wichtige Notizen anpinnen"
            },
            useCases: {
              wifi: "WLAN-Passwort, Türcodes, wichtige Nummern - alles griffbereit",
              contacts: "Kontaktdaten von Babysitter, Handwerker, Nachbarn"
            },
            tutorial: {
              title: "📝 So nutzt du die Notizen",
              steps: {
                step1: {
                  title: "🤔 Wo war nochmal...?",
                  desc: "👦 Max hat einen Freund eingeladen und der fragt nach dem WLAN-Passwort. 'Keine Ahnung, frag Mama!' 👩 Mama seufzt - 'Das steht auf einem Zettel... irgendwo.' Zeit für eine digitale Lösung!"
                },
                step2: {
                  title: "📝 Wichtiges festhalten",
                  desc: "👩 Mama erstellt eine Notiz '🔐 WLAN & Passwörter': Netzwerk, Passwort, Router-Standort. 'Ab jetzt findet jeder alles!' 👨 Papa ergänzt die Notfallnummern: Arzt, Apotheke, Nachbarin Frau Schmidt."
                },
                step3: {
                  title: "📁 Ordnung mit Ordnern",
                  desc: "👩 Mama organisiert: 📁 Haushalt (WLAN, Türcodes, Zählerstände), 📁 Kontakte (Babysitter, Handwerker, Schule), 📁 Urlaub (Packliste, Flugdaten). 'So findet jeder sofort, was er braucht!'"
                },
                step4: {
                  title: "📌 Wichtiges anpinnen",
                  desc: "'Das WLAN-Passwort brauchen wir ständig!' 👩 Mama pinnt die wichtigsten Notizen an 📌. Jetzt erscheinen sie ganz oben - 🔐 WLAN, 📞 Notfallnummern, 🏠 Türcode. Kein Suchen mehr!"
                }
              }
            }
          },
          tasks: {
            title: "Aufgaben",
            tagline: "Haushalt organisieren",
            desc: "Verteile Haushaltsaufgaben fair und behalte den Überblick, wer was erledigt.",
            features: {
              create: "Aufgaben erstellen und zuweisen",
              recurring: "Wiederkehrende Aufgaben (wöchentlich, monatlich)",
              assign: "Aufgaben an Familienmitglieder verteilen",
              reminders: "Erinnerungen vor Fälligkeit",
              progress: "Fortschritt und Statistiken einsehen"
            },
            useCases: {
              trash: "\"Müll rausbringen\" - jeden Dienstag automatisch für Papa",
              kids: "Kinder lernen Verantwortung mit eigenen kleinen Aufgaben"
            },
            tutorial: {
              title: "✅ So nutzt du die Aufgaben",
              steps: {
                step1: {
                  title: "😤 Das Chaos",
                  desc: "'Wer hat den Müll NICHT rausgebracht?!' 👩 Mama ist genervt. 'Ich dachte, Papa macht das!' - 'Ich dachte, Max macht das!' 👨‍👩‍👧‍👦 Die Müllers brauchen klare Zuständigkeiten. Zeit für die Aufgabenverwaltung!"
                },
                step2: {
                  title: "📋 Aufgaben verteilen",
                  desc: "👩 Mama erstellt die Haushaltsaufgaben: 🗑️ Müll rausbringen → 👨 Papa (jeden Dienstag), 🧹 Staubsaugen → 👦 Max (Samstag), 🍽️ Spülmaschine → 👧 Lisa (täglich). Jeder weiß, was zu tun ist!"
                },
                step3: {
                  title: "🔄 Wiederkehrend einstellen",
                  desc: "'Müll ist jeden Dienstag!' 👨 Papa aktiviert 'Wiederholen' → Wöchentlich → Dienstag. Die Aufgabe erscheint automatisch jede Woche neu. Keine Diskussion mehr, wer dran ist! ⏰ Erinnerung 1 Stunde vorher."
                },
                step4: {
                  title: "✅ Erledigt & Belohnung",
                  desc: "👦 Max hakt seine Aufgabe ab ✅. Die Statistik zeigt: Max diese Woche 5 Aufgaben! 👩 Mama lobt: 'Toll gemacht!' Am Monatsende: 👧 Lisa führt mit 28 erledigten Aufgaben - sie bekommt den 🏆 Fleißpreis!"
                }
              }
            }
          },
          // ✅ NEU: Haushalt Modul
          household: {
            title: "Haushalt",
            tagline: "Gemeinsam verwalten",
            desc: "Verwalte deinen Haushalt und lade Familienmitglieder ein. Alles unter einem digitalen Dach.",
            features: {
              create: "Haushalt erstellen und einrichten",
              invite: "Mitglieder per Code oder Link einladen",
              roles: "Admin- und Mitglieder-Berechtigungen",
              profiles: "Familienmitglieder-Profile mit Foto und Rolle",
              activity: "Aktivitätsübersicht - wer hat was beigetragen"
            },
            useCases: {
              family: "Die ganze Familie unter einem digitalen Dach versammeln",
              wg: "Auch für WGs perfekt - fair teilen und organisieren"
            },
            tutorial: {
              title: "🏠 So nutzt du die Haushaltsverwaltung",
              steps: {
                step1: {
                  title: "🏡 Die Familie vernetzt sich",
                  desc: "👩 Mama hat Planivio entdeckt und erstellt den Haushalt '🏠 Familie Müller'. 'Das wird unser digitales Zuhause!' Sie wählt einen Namen, den alle wiedererkennen. Der Haushalt ist angelegt - jetzt fehlen nur noch die anderen!"
                },
                step2: {
                  title: "📨 Alle einladen",
                  desc: "👩 Mama teilt den Einladungscode 🔑 per WhatsApp: 'Hey alle! Tretet unserem Planivio-Haushalt bei!' 👨 Papa, 👦 Max und 👧 Lisa klicken den Link und sind sofort dabei. Sogar 👵 Oma bekommt einen Zugang!"
                },
                step3: {
                  title: "👤 Profile einrichten",
                  desc: "Jeder richtet sein Profil ein: 👨 Papa wählt 🔵 Blau als Kalenderfarbe, 👦 Max nimmt 🟢 Grün. Mit Foto und Name sieht jeder sofort, wer was eingetragen hat. 'Cool, ich hab ein eigenes Profil!' freut sich 👧 Lisa."
                },
                step4: {
                  title: "👨‍👩‍👧‍👦 Team Müller",
                  desc: "Die Übersicht zeigt alle Mitglieder: 👨 Papa, 👩 Mama (beide 👑 Admin), 👦 Max und 👧 Lisa (Mitglieder). Die Kinder können Einträge erstellen, aber nur Eltern können löschen. 'Endlich sind wir digital verbunden!' 🎉"
                }
              }
            }
          }
        }
      },
      impressum: { title: "Impressum" },
      datenschutz: { title: "Datenschutz" },
      agb: { title: "AGB" },
      faq: { title: "FAQ" }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // 💬 FEEDBACK
    // ═══════════════════════════════════════════════════════════════════════
    feedback: {
      title: "Feedback",
      placeholder: "Dein Feedback...",
      submit: "Absenden",
      thanks: "Danke für dein Feedback!",
      types: {
        bug: "Fehler melden",
        feature: "Feature-Wunsch",
        general: "Allgemeines Feedback"
      }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // 🍪 COOKIES
    // ═══════════════════════════════════════════════════════════════════════
    cookies: {
      message: "Wir verwenden Cookies, um dein Erlebnis zu verbessern.",
      accept: "Akzeptieren",
      decline: "Ablehnen",
      settings: "Einstellungen"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // 📧 EMAIL
    // ═══════════════════════════════════════════════════════════════════════
    email: {
      verification: {
        title: "E-Mail bestätigen",
        message: "Bitte bestätige deine E-Mail-Adresse",
        resend: "Erneut senden"
      }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // 🌍 LANGUAGE
    // ═══════════════════════════════════════════════════════════════════════
    language: {
      select: "Sprache",
      de: "Deutsch",
      en: "English",
      fr: "Français"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // ⏰ TIME
    // ═══════════════════════════════════════════════════════════════════════
    time: {
      today: "Heute",
      yesterday: "Gestern",
      tomorrow: "Morgen",
      daysAgo: "vor {{count}} Tagen",
      hoursAgo: "vor {{count}} Stunden",
      minutesAgo: "vor {{count}} Minuten",
      justNow: "Gerade eben"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // 🎯 LANDING PAGE - ✅ v4: UPDATED
    // ═══════════════════════════════════════════════════════════════════════
    landing: {
      betaBanner: {
        text: "Jetzt kostenlos testen – Dein Feedback formt die App!"
      },
      nav: {
        features: "Features",
        priceCompare: "Preisvergleich",
        privacy: "Datenschutz",
        pricing: "Preise",
        login: "Zur App",
        experience: "Erlebe PLANIVIO"
      },
      hero: {
        titlePart1: "Schluss mit dem",
        titleGradient: "Chaos im Kopf.",
        betaBadge: "Beta-Phase – Jetzt kostenlos testen!",
        title: "Schluss mit dem Chaos im Kopf.",
        lead: "Einkaufslisten auf Zetteln. Termine im Kopf. Rezepte in drei verschiedenen Apps.",
        subtitle: "PLANIVIO bringt alles an einen Ort – für mehr Zeit mit deiner Familie.",
        ctaStart: "Kostenlos ausprobieren",
        ctaFeatures: "So funktioniert's"
      },
      stats: {
        apps: "Apps nutzt eine Familie für Haushalt & Organisation",
        oneApp: "App für alles – das ist PLANIVIO",
        priceDiff: "Preisunterschiede zwischen Supermärkten möglich*",
        euServer: "Server in Deutschland, 100% DSGVO-konform",
        source: "* Verbraucherzentrale NRW, Marktcheck 2023"
      },
      priceCompare: {
        comingSoon: "Coming Soon",
        title: "Finde den günstigsten Supermarkt",
        subtitle: "Preise vergleichen lohnt sich – die Unterschiede zwischen Supermärkten sind oft größer als gedacht.",
        cheapest: "Günstigster",
        disclaimer: "* Beispielhafte Darstellung eines Wocheneinkaufs (4 Personen). Tatsächliche Preise variieren je nach Produkt, Region und Zeitpunkt.",
        sourceText: "Das Sparpotenzial ist real: Ein Marktcheck der Verbraucherzentrale NRW zeigte bei 15 von 20 Produkten Preisunterschiede von über 100% – je nach Supermarkt.",
        sourceLabel: "Quelle: Verbraucherzentrale NRW, Marktcheck Mai 2023"
      },
      comparison: {
        label: "Vorher vs. Nachher",
        title: "So einfach wird euer Alltag",
        subtitle: "Mit PLANIVIO hat die ganze Familie alles im Blick – auf einen Blick."
      },
      benefitsLabel: "Vorteile",
      benefits: {
        title: "Was du wirklich gewinnst",
        subtitle: "Es geht nicht um perfekte Organisation. Es geht um mehr Zeit für deine Familie.",
        peace: {
          title: "Kopf frei",
          text: "Nie mehr grübeln, ob du etwas vergessen hast."
        },
        save: {
          title: "Geld sparen",
          text: "Überblick über Ausgaben hilft, bewusster einzukaufen."
        },
        time: {
          title: "Zeit gewinnen",
          text: "Weniger Suchen, weniger Abstimmen, mehr Freizeit."
        },
        family: {
          title: "Familie entlasten",
          text: "Verantwortung fair verteilen, alle im Bild."
        },
        stress: {
          title: "Stress reduzieren",
          text: "Struktur gibt Sicherheit und Ruhe."
        },
        focus: {
          title: "Fokus behalten",
          text: "Alles Wichtige an einem Ort – kein App-Hopping mehr."
        }
      },
      trust: {
        germany: "Entwickelt in Deutschland",
        eu: "Server in Deutschland",
        dsgvo: "100% DSGVO-konform",
        noCreditCard: "Keine Kreditkarte"
      },
      scenarios: {
        title: "So verändert Planivio deinen Alltag",
        subtitle: "Echte Situationen, echte Lösungen.",
        before: "Vorher",
        after: "Mit Planivio",
        birthday: {
          title: "Geburtstagsparty planen",
          before: "Wer bringt was? Wann kaufen? Drei Chats, zwei Listen, null Überblick.",
          after: "Termin → Rezept auswählen → Zutaten automatisch auf der Liste. Fertig."
        },
        shopping: {
          title: "Wocheneinkauf",
          before: "Jeder schreibt wo anders. Am Ende fehlt trotzdem die Milch.",
          after: "Eine Liste für alle. Echtzeit-Sync. Nichts vergessen."
        },
        guests: {
          title: "Spontaner Besuch",
          before: "Panik. Was haben wir da? Was können wir kochen?",
          after: "Rezepte durchsuchen → \"Was kann ich kochen?\" → Fertig."
        },
        budget: {
          title: "Monatsende",
          before: "Wo ist das Geld hin? Keine Ahnung.",
          after: "Ausgaben im Blick. Kategorien zeigen, wo gespart werden kann."
        }
      },
      privacy: {
        title: "Deine Daten bleiben in Deutschland.",
        text: "PLANIVIO wird in Deutschland entwickelt und auf deutschen Servern gehostet. Keine Datenweitergabe an Dritte. Keine Werbung. Keine versteckten Geschäftsmodelle.",
        highlight: "Deine Familienrezepte, Einkaufslisten und Termine gehören dir.",
        promise: "Unser Datenschutz-Versprechen",
        euServers: "Server-Standort: Deutschland",
        noThirdParty: "Keine Datenweitergabe an Dritte – niemals",
        noAds: "Keine Nutzung deiner Daten für Werbung",
        encryption: "Verschlüsselung nach Banken-Standard (AES-256)",
        deletable: "Jederzeit vollständige Datenlöschung möglich",
        transparent: "Transparente Datenschutzerklärung auf Deutsch",
        noTracking: "Kein Tracking",
        deleteAnytime: "Jederzeit löschbar",
        exportData: "Daten exportierbar",
        learnMore: "Mehr zum Datenschutz"
      },
      betaInvite: {
        badge: "Begrenzte Plätze",
        title: "Werde Teil der Beta!",
        text: "Teste Planivio kostenlos, gestalte die Zukunft mit und sichere dir exklusive Vorteile.",
        perk1: "Komplett kostenlos während der Beta",
        perk2: "50% Rabatt nach der Beta für 12 Monate",
        perk3: "Direkter Einfluss auf neue Features",
        cta: "Jetzt kostenlos starten"
      },
      features: {
        subtitle: "Alles, was dein Haushalt braucht",
        title: "Module für den kompletten Familienalltag",
        viewAll: "alle Features im Detail",
        calendar: { title: "Kalender", desc: "Alle Termine der Familie im Blick" },
        shopping: { title: "Einkaufslisten", desc: "Smarte Listen mit Kategorien" },
        recipes: { title: "Rezepte", desc: "Rezepte speichern & teilen" },
        tasks: { title: "Aufgaben", desc: "Haushaltsaufgaben organisieren und zuweisen" },
        budget: { title: "Budget", desc: "Finanzen unter Kontrolle" },
        notes: { title: "Notizen", desc: "Zentrale Dokumentation" },
        assistant: { title: "KI Assistent", desc: "Intelligente Hilfe" }
      },
      pricing: {
        subtitle: "Preise",
        title: "Während der Beta ist Planivio komplett kostenlos!",
        availableNow: "Jetzt verfügbar",
        betaAdvantage: "Beta-Tester Vorteil",
        betaDiscount: "Wer jetzt dabei ist, erhält nach der Beta 50% Rabatt auf alle Pläne für 12 Monate!",
        beta: {
          name: "Beta",
          period: "Kostenlos",
          cta: "Jetzt kostenlos starten",
          features: {
            allModules: "Alle verfügbaren Module",
            fiveMembers: "Bis zu 5 Familienmitglieder",
            aiIncluded: "KI-Assistent inklusive",
            emailSupport: "Email Support",
            feedbackInfluence: "Feedback-Einfluss auf Features"
          }
        },
        pro: {
          name: "Pro",
          period: "EUR/Monat",
          periodSoon: "Demnächst",
          cta: "Kommt bald",
          features: {
            extended: "Erweiterte Features",
            twentyFiveMembers: "Bis zu 25 Familienmitglieder",
            prioritySupport: "Priority Support",
            extendedAI: "Erweiterte KI-Features",
            priceComparison: "Preisvergleich Supermärkte"
          }
        },
        family: {
          name: "Familie+",
          period: "EUR/Monat",
          periodSoon: "Demnächst",
          cta: "Kommt bald",
          features: {
            allPro: "Alle Pro Features",
            unlimitedMembers: "Unbegrenzte Mitglieder",
            multiHousehold: "Multi-Haushalt Support",
            apiAccess: "API Zugang",
            premiumSupport: "Premium Support"
          }
        }
      },
      betaPerks: {
        free: { title: "Kostenlos", desc: "Während der Beta komplett gratis" },
        feedback: { title: "Dein Feedback zählt", desc: "Gestalte die Features mit" },
        earlyAdopter: { title: "Early Adopter Vorteile", desc: "Spezielle Konditionen nach Beta" }
      },
      cta: {
        title: "Bereit für weniger Chaos?",
        subtitle: "Starte jetzt – kostenlos und unverbindlich.",
        startButton: "Kostenlos ausprobieren",
        inviteButton: "Freunde einladen",
        inviteHint: "Teile Planivio mit Familie & Freunden – QR-Code zum Ausdrucken!"
      },
      footer: {
        rights: "Alle Rechte vorbehalten.",
        privacy: "Datenschutz",
        terms: "AGB",
        imprint: "Impressum",
        contact: "Kontakt",
        madeIn: "Made with ❤️ in Bavaria"
      }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // ❓ FAQ PAGE - VOLLSTÄNDIG
    // ═══════════════════════════════════════════════════════════════════════
    faq: {
      title: "Häufig gestellte Fragen",
      subtitle: "Finde Antworten auf die häufigsten Fragen zu Planivio",
      searchPlaceholder: "Suche nach Fragen...",
      noResults: "Keine Ergebnisse gefunden",
      tryOther: "Versuche andere Suchbegriffe oder wähle eine andere Kategorie.",
      contactTitle: "Frage nicht dabei?",
      contactSubtitle: "Kontaktiere uns direkt - wir helfen dir gerne weiter!",
      categories: {
        all: "Alle",
        general: "Allgemein",
        beta: "Beta",
        features: "Funktionen",
        privacy: "Datenschutz",
        technical: "Technisch"
      },
      // ===== ALLGEMEIN =====
      general: {
        whatIsHomeM: {
          q: "Was ist Planivio?",
          a: "Planivio ist eine moderne Haushalts-Management-Plattform für Familien. Mit Planivio kannst du Einkaufslisten, Rezepte, Kalendertermine, Budget und vieles mehr an einem Ort verwalten. Alle Familienmitglieder können in Echtzeit zusammenarbeiten."
        },
        forWhom: {
          q: "Für wen ist Planivio gedacht?",
          a: "Planivio richtet sich an Familien und Haushalte, die ihre tägliche Organisation verbessern möchten. Egal ob Paare, Familien mit Kindern oder WGs - Planivio hilft dabei, den Überblick zu behalten und Aufgaben zu koordinieren."
        },
        isFree: {
          q: "Ist Planivio kostenlos?",
          a: "Während der Beta-Phase ist Planivio komplett kostenlos! Du erhältst Zugang zu allen verfügbaren Funktionen ohne Einschränkungen. Nach der Beta werden verschiedene Preispläne angeboten, wobei Beta-Tester 50% Rabatt für 12 Monate erhalten."
        },
        devices: {
          q: "Auf welchen Geräten kann ich Planivio nutzen?",
          a: "Planivio funktioniert auf allen Geräten mit einem modernen Webbrowser: Computer (Windows, Mac, Linux), Tablets (iPad, Android) und Smartphones (iPhone, Android). Du kannst Planivio auch als App auf deinem Homescreen speichern."
        }
      },
      // ===== BETA =====
      beta: {
        whatIsBeta: {
          q: "Was bedeutet 'Beta'?",
          a: "Beta bedeutet, dass Planivio sich noch in der Entwicklung befindet. Du bekommst frühen Zugang zu neuen Funktionen, aber es können noch kleinere Fehler auftreten. Dein Feedback hilft uns, Planivio zu verbessern!"
        },
        howLong: {
          q: "Wie lange dauert die Beta-Phase?",
          a: "Die Beta-Phase ist für 2026 geplant. Das genaue Ende hängt davon ab, wie schnell wir alle geplanten Funktionen fertigstellen und stabilisieren können. Du wirst rechtzeitig informiert."
        },
        benefits: {
          q: "Welche Vorteile habe ich als Beta-Tester?",
          a: "Als Beta-Tester erhältst du: Kostenlose Nutzung während der gesamten Beta-Phase, 50% Rabatt auf alle Pläne für 12 Monate nach der Beta, direkten Einfluss auf neue Features durch dein Feedback, und Early-Adopter-Status mit besonderen Vorteilen."
        },
        feedback: {
          q: "Kann ich Feedback geben?",
          a: "Ja, unbedingt! Nutze den Feedback-Button (💬) unten rechts auf jeder Seite. Du kannst Bugs melden, Feature-Wünsche äußern oder allgemeines Feedback geben. Jede Rückmeldung hilft uns, Planivio besser zu machen."
        },
        dataAfter: {
          q: "Was passiert mit meinen Daten nach der Beta?",
          a: "Deine Daten bleiben erhalten! Wenn du nach der Beta einen kostenpflichtigen Plan wählst, werden alle deine Einkaufslisten, Rezepte, Kalendereinträge etc. übernommen. Nichts geht verloren."
        }
      },
      // ===== FUNKTIONEN =====
      features: {
        modules: {
          q: "Welche Module bietet Planivio?",
          a: "Planivio bietet aktuell: 🛒 Einkaufslisten (mit Kategorien), 🍳 Rezepte (speichern & teilen), 📅 Familienkalender, 💰 Budget-Tracking, 📝 Notizen, ✅ Aufgaben und 🤖 KI-Assistent. Weitere Module sind in Planung."
        },
        aiAssistant: {
          q: "Wie funktioniert der KI-Assistent?",
          a: "Der KI-Assistent hilft dir bei alltäglichen Fragen: 'Was kann ich mit Kartoffeln und Hähnchen kochen?', 'Plane das Abendessen für 4 Personen', oder 'Welche Lebensmittel sollte ich nachkaufen?'. Er lernt deine Vorlieben kennen und gibt personalisierte Tipps."
        },
        multiUser: {
          q: "Können mehrere Personen gleichzeitig nutzen?",
          a: "Ja! Planivio ist für Familien gemacht. Bis zu 5 Familienmitglieder können gleichzeitig auf alle Daten zugreifen. Änderungen werden in Echtzeit synchronisiert - wenn Mama etwas zur Einkaufsliste hinzufügt, sieht Papa es sofort auf seinem Handy."
        },
        recipeToShopping: {
          q: "Kann ich Rezept-Zutaten direkt zur Einkaufsliste hinzufügen?",
          a: "Ja, das ist eine unserer beliebtesten Funktionen! Bei jedem Rezept gibt es einen Button 'Zur Einkaufsliste hinzufügen'. Die Zutaten werden automatisch mit den richtigen Mengen übernommen - du kannst die Portionen vorher anpassen."
        },
        offline: {
          q: "Funktioniert Planivio offline?",
          a: "Aktuell benötigt Planivio eine Internetverbindung. Wir arbeiten an einem Offline-Modus für die Zukunft, damit du auch ohne Internet auf deine Einkaufsliste zugreifen kannst."
        }
      },
      // ===== DATENSCHUTZ =====
      privacy: {
        dataStorage: {
          q: "Wo werden meine Daten gespeichert?",
          a: "Alle Daten werden auf Servern in der EU (Microsoft Azure, Region West Europe) gespeichert. Wir halten uns strikt an die DSGVO und deutsche Datenschutzgesetze. Deine Daten verlassen niemals die EU."
        },
        dataSold: {
          q: "Werden meine Daten verkauft oder für Werbung genutzt?",
          a: "Nein, niemals! Wir verkaufen keine Daten an Dritte und zeigen keine Werbung. Planivio finanziert sich ausschließlich durch Abonnements. Deine Daten gehören dir."
        },
        cookies: {
          q: "Welche Cookies verwendet Planivio?",
          a: "Wir verwenden ausschließlich technisch notwendige Cookies für die Anmeldung (Session-Cookie). Keine Tracking-Cookies, keine Werbe-Cookies, keine Analytics von Drittanbietern."
        },
        export: {
          q: "Kann ich meine Daten exportieren?",
          a: "Ja! Du kannst jederzeit einen Export aller deiner Daten anfordern (Recht auf Datenübertragbarkeit nach DSGVO). Kontaktiere uns unter kontakt@planivio.de."
        },
        deleteAccount: {
          q: "Wie kann ich meinen Account löschen?",
          a: "Du kannst deinen Account jederzeit in den Einstellungen löschen oder uns eine E-Mail an kontakt@planivio.de schicken. Alle deine Daten werden dann innerhalb von 30 Tagen unwiderruflich gelöscht."
        }
      },
      // ===== TECHNISCH =====
      technical: {
        browsers: {
          q: "Welche Browser werden unterstützt?",
          a: "Planivio funktioniert mit allen modernen Browsern: Chrome, Firefox, Safari, Edge (jeweils aktuelle Version). Internet Explorer wird nicht unterstützt."
        },
        installMobile: {
          q: "Wie installiere ich Planivio auf meinem Handy?",
          a: "Öffne Planivio in deinem Browser, tippe auf 'Teilen' (iOS) oder das Menü (Android) und wähle 'Zum Startbildschirm hinzufügen'. Planivio wird dann wie eine App auf deinem Homescreen angezeigt."
        },
        forgotPassword: {
          q: "Ich habe mein Passwort vergessen. Was tun?",
          a: "Klicke auf der Login-Seite auf 'Passwort vergessen'. Du erhältst eine E-Mail mit einem Link zum Zurücksetzen deines Passworts. Der Link ist 24 Stunden gültig."
        },
        notLoading: {
          q: "Die App lädt nicht. Was kann ich tun?",
          a: "Versuche folgende Schritte: 1) Seite neu laden (F5 oder Pull-to-Refresh), 2) Browser-Cache leeren, 3) Anderen Browser probieren, 4) Internetverbindung prüfen. Wenn nichts hilft, kontaktiere uns über das Feedback-Formular."
        },
        nativeApp: {
          q: "Gibt es eine native App für iOS/Android?",
          a: "Aktuell nicht - Planivio ist eine Web-App, die auf allen Geräten funktioniert. Native Apps für iOS und Android sind für die Zukunft geplant. Die Web-App kannst du aber schon jetzt auf deinem Homescreen speichern!"
        }
      }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // ⭐ FEATURES PAGE
    // ═══════════════════════════════════════════════════════════════════════
    features: {
      hero: { title: "Was kann Planivio?", subtitle: "Entdecke alle Module, die deinen Familienalltag einfacher machen.", allAvailable: "Alle Features verfügbar" },
      badge: { allAvailable: "Alle Features verfügbar" },
      nav: { backHome: "Zurück zur Startseite", startNow: "Jetzt kostenlos starten" },
      screenshot: { comingSoon: "Screenshot kommt bald" },
      useCases: { title: "Anwendungsbeispiele" },
      intro: {
        problem: {
          title: "Kennst du das?",
          text: "Termine im Kopf, Einkaufsliste auf dem Zettel, Rezepte im Browser, Budget in Excel, To-Dos im Chat. Du jonglierst täglich 5-7 Apps – und trotzdem vergisst jemand die Milch. Das ist Mental Load. Und in 71% der Haushalte trägt eine Person diese unsichtbare Last allein."
        },
        solution: {
          title: "PLANIVIO ändert das.",
          text: "Eine Plattform. Alles vernetzt. Die KI erkennt Zusammenhänge automatisch: Geburtstag im Kalender → Kuchenrezept → Zutaten auf der Einkaufsliste → bestes Angebot gefunden. Weniger Stress. Mehr Zeit. Für das, was wirklich zählt."
        },
        stats: {
          mentalLoad: "der Haushalte tragen die Last allein",
          apps: "Apps jonglierst du täglich",
          households: "Haushalte in Deutschland"
        },
        flow: { calendar: "Kalender", recipes: "Rezept", shopping: "Einkauf", budget: "Angebot" },
        trust: { euHosting: "EU-Hosting", dsgvo: "DSGVO-konform", madeInGermany: "Made in Germany" }
      },
      cta: { title: "Klingt gut?", subtitle: "Starte jetzt kostenlos und probiere alle Features aus.", feedbackHelps: "Dein Feedback hilft uns, Planivio noch besser zu machen!", startFree: "Kostenlos starten", startNow: "Jetzt kostenlos starten" },
      // Module
      modules: {
        // ✅ NEU: Dashboard Modul
        dashboard: {
          title: "Dashboard",
          tagline: "Deine Kommandozentrale",
          desc: "Das Dashboard ist dein zentraler Anlaufpunkt. Hier siehst du auf einen Blick alle wichtigen Termine, Aufgaben und Einkäufe des Tages.",
          features: {
            overview: "Tagesübersicht mit anstehenden Terminen und Aufgaben",
            quickActions: "Ein-Klick-Zugang zu häufigen Aktionen",
            widgets: "Kompakte Vorschau aller Module",
            stats: "Statistik-Karten mit schnellen Zahlen",
            greeting: "Personalisierte Begrüßung"
          },
          useCases: {
            morning: "Starte jeden Morgen mit einem klaren Überblick über deinen Tag",
            quickAccess: "Schneller Zugriff auf alles Wichtige ohne langes Suchen"
          }
        },
        shopping: {
          title: "Einkaufslisten",
          tagline: "Nie wieder etwas vergessen",
          desc: "Erstelle und teile Einkaufslisten mit deiner Familie. Artikel werden automatisch kategorisiert und synchronisiert.",
          features: {
            categorization: "Automatische Kategorisierung nach Supermarkt-Abteilungen",
            multipleLists: "Mehrere Listen für verschiedene Läden",
            sync: "Echtzeit-Synchronisation mit allen Familienmitgliedern",
            offline: "Offline-Modus für den Einkauf",
            recurring: "Wiederkehrende Artikel automatisch hinzufügen"
          },
          useCases: {
            family: "Die ganze Familie kann Artikel hinzufügen - Papa sieht sofort, was Mama eingetragen hat",
            mobile: "Im Supermarkt einfach abhaken - auch ohne Internet"
          }
        },
        recipes: {
          title: "Rezepte",
          tagline: "Deine Lieblingsrezepte an einem Ort",
          desc: "Speichere Familienrezepte, importiere aus dem Web und füge Zutaten direkt zur Einkaufsliste hinzu.",
          features: {
            create: "Eigene Rezepte erstellen und bearbeiten",
            toShopping: "Zutaten mit einem Klick zur Einkaufsliste",
            categories: "Kategorien und Tags für schnelles Finden",
            portions: "Portionen anpassen - Mengen werden umgerechnet",
            share: "Rezepte mit Familie und Freunden teilen"
          },
          useCases: {
            grandma: "Omas Geheimrezept digital aufbewahren und nie wieder verlieren",
            shopping: "Sonntagsbraten geplant? Alle Zutaten automatisch auf der Einkaufsliste"
          }
        },
        ai: {
          title: "KI-Assistent",
          tagline: "Dein smarter Haushaltshelfer",
          desc: "Frag den KI-Assistenten nach Rezeptideen, Meal-Planning oder Haushaltstipps.",
          features: {
            suggestions: "Rezeptvorschläge basierend auf vorhandenen Zutaten",
            mealPlanning: "Automatische Wochenplanung für Mahlzeiten",
            tips: "Personalisierte Haushaltstipps",
            optimization: "Einkaufslisten-Optimierung nach Supermarkt-Layout",
            natural: "Natürliche Spracheingabe - frag einfach!"
          },
          useCases: {
            ingredients: "\"Was kann ich mit Kartoffeln, Käse und Schinken kochen?\" - sofort Ideen bekommen",
            planning: "\"Plane gesunde Mahlzeiten für die Woche\" - fertig ist der Essensplan"
          }
        },
        calendar: {
          title: "Kalender",
          tagline: "Familie im Blick",
          desc: "Ein gemeinsamer Kalender für alle Termine, Aktivitäten und Erinnerungen der Familie.",
          features: {
            shared: "Gemeinsamer Familienkalender",
            colorCoding: "Farbcodierung pro Familienmitglied",
            reminders: "Erinnerungen per Push-Notification",
            recurring: "Wiederkehrende Termine (Müllabfuhr, Sportverein...)",
            views: "Tages-, Wochen- und Monatsansicht"
          },
          useCases: {
            appointments: "Arzttermine, Elternabende, Sporttraining - alles auf einen Blick",
            birthdays: "Nie wieder einen Geburtstag vergessen"
          }
        },
        budget: {
          title: "Budget",
          tagline: "Finanzen im Griff",
          desc: "Behalte den Überblick über Haushaltsausgaben und spare gezielt für gemeinsame Ziele.",
          features: {
            categorize: "Ausgaben nach Kategorien erfassen",
            budgets: "Monatsbudgets festlegen und überwachen",
            charts: "Übersichtliche Diagramme und Statistiken",
            goals: "Sparziele definieren und verfolgen",
            stats: "Vergleich mit Vormonaten"
          },
          useCases: {
            overview: "Endlich wissen, wohin das Geld jeden Monat geht",
            vacation: "Gemeinsam auf den Sommerurlaub sparen"
          }
        },
        notes: {
          title: "Notizen",
          tagline: "Wichtiges festhalten",
          desc: "Geteilte Notizen für alles, was die Familie wissen sollte.",
          features: {
            shared: "Gemeinsame Notizen für alle",
            folders: "Ordner für bessere Organisation",
            search: "Volltextsuche in allen Notizen",
            attachments: "Bilder und Dateien anhängen",
            pin: "Wichtige Notizen anpinnen"
          },
          useCases: {
            wifi: "WLAN-Passwort, Türcodes, wichtige Nummern - alles griffbereit",
            contacts: "Kontaktdaten von Babysitter, Handwerker, Nachbarn"
          }
        },
        tasks: {
          title: "Aufgaben",
          tagline: "Haushalt organisieren",
          desc: "Verteile Haushaltsaufgaben fair und behalte den Überblick, wer was erledigt.",
          features: {
            create: "Aufgaben erstellen und zuweisen",
            recurring: "Wiederkehrende Aufgaben (wöchentlich, monatlich)",
            assign: "Aufgaben an Familienmitglieder verteilen",
            reminders: "Erinnerungen vor Fälligkeit",
            progress: "Fortschritt und Statistiken einsehen"
          },
          useCases: {
            trash: "\"Müll rausbringen\" - jeden Dienstag automatisch für Papa",
            kids: "Kinder lernen Verantwortung mit eigenen kleinen Aufgaben"
          }
        },
        // ✅ NEU: Haushalt Modul
        household: {
          title: "Haushalt",
          tagline: "Gemeinsam verwalten",
          desc: "Verwalte deinen Haushalt und lade Familienmitglieder ein. Alles unter einem digitalen Dach.",
          features: {
            create: "Haushalt erstellen und einrichten",
            invite: "Mitglieder per Code oder Link einladen",
            roles: "Admin- und Mitglieder-Berechtigungen",
            profiles: "Familienmitglieder-Profile mit Foto und Rolle",
            activity: "Aktivitätsübersicht - wer hat was beigetragen"
          },
          useCases: {
            family: "Die ganze Familie unter einem digitalen Dach versammeln",
            wg: "Auch für WGs perfekt - fair teilen und organisieren"
          }
        }
      }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // 📄 LEGAL PAGES
    // ═══════════════════════════════════════════════════════════════════════
    legal: {
      privacy: {
        title: "Datenschutzerklärung",
        lastUpdated: "Stand",
        date: "Januar 2026",
        toc: "Inhaltsverzeichnis",
        germany: "Deutschland",
        email: "E-Mail",
        imprint: "Impressum",
        imprintLink: "Zum Impressum",
        
        // Sections
        sections: {
          responsible: "Verantwortlicher",
          overview: "Übersicht der Verarbeitungen",
          legal: "Rechtsgrundlagen",
          security: "Sicherheitsmaßnahmen",
          deletion: "Löschung von Daten",
          hosting: "Webhosting",
          auth: "Registrierung und Authentifizierung",
          contact: "Kontaktaufnahme",
          rights: "Ihre Rechte"
        },
        
        // Overview
        overviewText: "Die nachfolgende Übersicht fasst die Arten der verarbeiteten Daten und die Zwecke ihrer Verarbeitung zusammen.",
        dataTypes: "Arten der verarbeiteten Daten",
        dataTypesList: {
          inventory: "Bestandsdaten (z.B. Namen, Adressen)",
          content: "Inhaltsdaten (z.B. Eingaben in Formularen)",
          usage: "Nutzungsdaten (z.B. besuchte Seiten, Zugriffszeiten)",
          meta: "Meta-/Kommunikationsdaten (z.B. IP-Adressen)"
        },
        affectedPersons: "Betroffene Personen",
        affectedPersonsList: {
          users: "Nutzer unserer Plattform"
        },
        purposes: "Zwecke der Verarbeitung",
        purposesList: {
          provision: "Bereitstellung unseres Onlineangebotes",
          contract: "Vertragliche Leistungen und Service",
          security: "Sicherheitsmaßnahmen",
          inquiries: "Kontaktanfragen und Kommunikation"
        },
        
        // Legal basis
        legalText: "Im Folgenden informieren wir Sie über die Rechtsgrundlagen der Datenverarbeitung:",
        legalBasis: {
          consent: "Einwilligung (Art. 6 Abs. 1 S. 1 lit. a DSGVO)",
          consentDesc: "Sie haben Ihre Einwilligung in die Verarbeitung gegeben.",
          contract: "Vertragserfüllung (Art. 6 Abs. 1 S. 1 lit. b DSGVO)",
          contractDesc: "Die Verarbeitung ist zur Erfüllung eines Vertrags erforderlich.",
          legitimate: "Berechtigte Interessen (Art. 6 Abs. 1 S. 1 lit. f DSGVO)",
          legitimateDesc: "Die Verarbeitung erfolgt zur Wahrung unserer berechtigten Interessen."
        },
        legalBasisLabel: "Rechtsgrundlage",
        legitimateInterests: "Berechtigte Interessen (Art. 6 Abs. 1 S. 1 lit. f DSGVO)",
        contractFulfillment: "Vertragserfüllung (Art. 6 Abs. 1 S. 1 lit. b DSGVO)",
        
        // Security
        securityText: "Wir treffen nach Maßgabe der gesetzlichen Vorgaben technische und organisatorische Maßnahmen, um ein dem Risiko angemessenes Schutzniveau zu gewährleisten.",
        securityMeasures: "Zu den Maßnahmen gehören insbesondere",
        securityList: {
          ssl: "SSL/TLS-Verschlüsselung für alle Datenübertragungen",
          passwords: "Sichere Speicherung von Passwörtern (gehasht)",
          updates: "Regelmäßige Sicherheitsupdates",
          access: "Zugriffskontrolle und Berechtigungsmanagement",
          rls: "Row Level Security (RLS) für Datenisolation zwischen Nutzern"
        },
        
        // Deletion
        deletionText1: "Die von uns verarbeiteten Daten werden nach Maßgabe der gesetzlichen Vorgaben gelöscht, sobald deren zur Verarbeitung erlaubten Einwilligungen widerrufen werden oder sonstige Erlaubnisse entfallen.",
        deletionText2: "Sofern die Daten nicht gelöscht werden, weil sie für andere und gesetzlich zulässige Zwecke erforderlich sind, wird deren Verarbeitung auf diese Zwecke beschränkt.",
        deletionText3: "Bei Löschung Ihres Kontos werden alle zugehörigen Daten innerhalb von 30 Tagen gelöscht.",
        
        // Hosting
        hostingText: "Unser Hosting-Anbieter verarbeitet die folgenden Daten im Rahmen der Bereitstellung dieser Website:",
        azureText: "Wir nutzen Microsoft Azure als Cloud-Plattform. Die Server befinden sich in der EU (Region: West Europe).",
        provider: "Anbieter",
        privacyPolicy: "Datenschutzerklärung",
        collectedData: "Erhobene Daten",
        collectedDataList: {
          ip: "IP-Adresse",
          datetime: "Datum und Uhrzeit der Anfrage",
          timezone: "Zeitzonendifferenz zur GMT",
          page: "Angeforderte Seite",
          status: "HTTP-Statuscode",
          size: "Übertragene Datenmenge",
          browser: "Browser und Betriebssystem"
        },
        
        // Auth
        authText: "Zur Registrierung und Anmeldung nutzen wir einen externen Authentifizierungsdienst.",
        authentication: "Authentifizierung",
        logtoText: "Wir nutzen Logto Cloud für die sichere Authentifizierung. Logto speichert Ihre Anmeldedaten verschlüsselt.",
        registrationData: "Bei der Registrierung erhobene Daten",
        registrationDataList: {
          email: "E-Mail-Adresse",
          username: "Benutzername (optional)",
          password: "Passwort (verschlüsselt gespeichert)",
          timestamp: "Zeitstempel der Registrierung"
        },
        accountDeletion: "Kontolöschung",
        accountDeletionText: "Sie können Ihr Konto jederzeit löschen. Senden Sie dazu eine E-Mail an kontakt@planivio.de oder nutzen Sie die Löschfunktion in den Kontoeinstellungen.",
        
        // Contact
        contactText: "Bei der Kontaktaufnahme mit uns werden die Angaben des Nutzers zur Bearbeitung der Kontaktanfrage verarbeitet.",
        processedData: "Verarbeitete Daten",
        contactDataList: "Name, E-Mail-Adresse, Nachrichteninhalt",
        storageDuration: "Speicherdauer",
        storageDurationText: "Die Daten werden gelöscht, sobald sie für die Erreichung des Zweckes ihrer Erhebung nicht mehr erforderlich sind.",
        
        // Cookies
        cookies: {
          title: "Cookies",
          text: "Wir verwenden ausschließlich technisch notwendige Cookies für die Authentifizierung.",
          used: "Verwendete Cookies",
          name: "Name",
          purpose: "Zweck",
          duration: "Dauer",
          notUsed: "Wir verwenden KEINE:",
          noTracking: "Tracking-Cookies",
          noMarketing: "Marketing-Cookies",
          noAnalytics: "Analyse-Cookies (kein Google Analytics o.ä.)",
          noSocial: "Social-Media-Cookies",
          legalBasis: "Berechtigte Interessen – technisch erforderlich für den Betrieb der Anwendung"
        },
        
        // Rights
        rightsText: "Ihnen stehen als Betroffene nach der DSGVO verschiedene Rechte zu:",
        rights: {
          access: "Auskunftsrecht (Art. 15 DSGVO)",
          accessText: "Sie haben das Recht, Auskunft über Ihre bei uns gespeicherten personenbezogenen Daten zu erhalten.",
          rectification: "Recht auf Berichtigung (Art. 16 DSGVO)",
          rectificationText: "Sie haben das Recht auf Berichtigung unrichtiger oder Vervollständigung unvollständiger Daten.",
          erasure: "Recht auf Löschung (Art. 17 DSGVO)",
          erasureText: "Sie haben das Recht auf Löschung Ihrer Daten, sofern keine gesetzlichen Aufbewahrungspflichten bestehen.",
          restriction: "Recht auf Einschränkung (Art. 18 DSGVO)",
          restrictionText: "Sie haben das Recht, die Einschränkung der Verarbeitung Ihrer Daten zu verlangen.",
          portability: "Recht auf Datenübertragbarkeit (Art. 20 DSGVO)",
          portabilityText: "Sie haben das Recht, Ihre Daten in einem gängigen Format zu erhalten.",
          objection: "Widerspruchsrecht (Art. 21 DSGVO)",
          objectionText: "Sie haben das Recht, der Verarbeitung Ihrer Daten zu widersprechen.",
          complaint: "Beschwerderecht",
          complaintText: "Sie haben das Recht, sich bei einer Aufsichtsbehörde zu beschweren:"
        },
        
        // Changes
        changes: {
          title: "Änderungen dieser Datenschutzerklärung",
          text: "Wir behalten uns vor, diese Datenschutzerklärung anzupassen. Die aktuelle Version finden Sie immer auf dieser Seite."
        }
      },
      
      imprint: {
        title: "Impressum",
        germany: "Deutschland",
        email: "E-Mail",
        lastUpdated: "Stand"
      },
      
      terms: {
        title: "Allgemeine Geschäftsbedingungen",
        lastUpdated: "Stand",
        legalNote: "Diese AGB sind nach deutschem Recht verfasst. Bei Übersetzungen gilt im Zweifel die deutsche Fassung.",
        provider: "nachfolgend \"Anbieter\"",
        
        s1: {
          title: "§ 1 Geltungsbereich",
          p1: "Diese Allgemeinen Geschäftsbedingungen gelten für die Nutzung der Planivio-Plattform, angeboten von:",
          p2: "Die AGB gelten für alle Nutzer der Plattform, unabhängig davon, ob sie einen kostenlosen oder kostenpflichtigen Tarif nutzen.",
          p3: "Mit der Registrierung akzeptiert der Nutzer diese AGB.",
          p4: "Abweichende Bedingungen des Nutzers werden nicht anerkannt, es sei denn, der Anbieter stimmt ihrer Geltung ausdrücklich zu."
        },
        s2: {
          title: "§ 2 Vertragsschluss",
          p1: "Die Darstellung der Dienste auf der Website stellt kein rechtlich bindendes Angebot dar.",
          p2: "Durch die Registrierung gibt der Nutzer ein verbindliches Angebot zum Vertragsschluss ab.",
          p3: "Der Vertrag kommt durch die Bestätigung der Registrierung per E-Mail zustande.",
          p4: "Der Vertragstext wird vom Anbieter gespeichert und kann vom Nutzer angefordert werden."
        },
        s3: {
          title: "§ 3 Leistungsbeschreibung",
          p1: "Planivio ist eine webbasierte Plattform zur Haushaltsorganisation für Familien.",
          p2: "Die Plattform umfasst folgende Module:",
          modules: {
            shopping: "Einkaufslisten",
            recipes: "Rezeptverwaltung",
            calendar: "Familienkalender",
            budget: "Budget-Tracking",
            notes: "Notizen",
            tasks: "Aufgaben",
            ai: "KI-Assistent"
          },
          p3: "Der Funktionsumfang kann je nach gewähltem Tarif variieren.",
          p4: "Der Anbieter ist berechtigt, die Dienste weiterzuentwickeln und anzupassen."
        },
        s4: {
          title: "§ 4 Beta-Phase",
          p1: "Während der Beta-Phase ist die Nutzung aller Funktionen kostenlos.",
          p2: "Der Anbieter kann die Beta-Phase jederzeit beenden und kostenpflichtige Tarife einführen.",
          p3: "Beta-Nutzer werden mindestens 30 Tage vor Einführung kostenpflichtiger Tarife informiert.",
          p4: "Es besteht kein Anspruch auf Fortführung der kostenlosen Nutzung nach Ende der Beta-Phase."
        },
        s5: {
          title: "§ 5 Preise und Zahlung",
          p1: "Die aktuellen Preise sind auf der Website einsehbar.",
          p2: "Alle Preise verstehen sich inklusive der gesetzlichen Mehrwertsteuer.",
          p3: "Die Zahlung erfolgt im Voraus. Folgende Zahlungsmethoden werden akzeptiert:",
          payment: {
            card: "Kreditkarte (Visa, Mastercard)",
            sepa: "SEPA-Lastschrift",
            paypal: "PayPal"
          },
          p4: "Bei Zahlungsverzug kann der Zugang zum Dienst eingeschränkt werden.",
          p5: "Der Anbieter behält sich Preisanpassungen vor. Diese werden mindestens 30 Tage im Voraus angekündigt."
        },
        s6: {
          title: "§ 6 Vertragslaufzeit und Kündigung",
          p1: "Der Vertrag wird auf unbestimmte Zeit geschlossen.",
          p2: "Beide Parteien können den Vertrag jederzeit mit einer Frist von 30 Tagen zum Monatsende kündigen.",
          p3: "Die Kündigung kann per E-Mail oder über die Kontoeinstellungen erfolgen.",
          p4: "Das Recht zur außerordentlichen Kündigung aus wichtigem Grund bleibt unberührt."
        },
        s7: {
          title: "§ 7 Nutzungsrechte und Pflichten",
          p1: "Der Nutzer erhält ein nicht-exklusives, nicht übertragbares Recht zur Nutzung der Plattform.",
          p2: "Der Nutzer ist für die Sicherheit seiner Zugangsdaten verantwortlich.",
          p3: "Es ist dem Nutzer untersagt:",
          duties: {
            illegal: "Die Plattform für illegale Zwecke zu nutzen",
            commercial: "Die Plattform für kommerzielle Zwecke ohne Genehmigung zu nutzen",
            impair: "Den Betrieb der Plattform zu beeinträchtigen",
            share: "Zugangsdaten mit Dritten zu teilen",
            reverse: "Die Software zu dekompilieren oder zurückzuentwickeln"
          },
          p4: "Bei Verstößen kann der Zugang gesperrt und der Vertrag außerordentlich gekündigt werden."
        },
        s8: {
          title: "§ 8 Datenschutz",
          p1: "Der Anbieter verarbeitet personenbezogene Daten gemäß der DSGVO.",
          p2a: "Details zur Datenverarbeitung finden sich in der",
          privacyLink: "Datenschutzerklärung",
          p2b: ".",
          p3: "Der Nutzer stimmt der Verarbeitung seiner Daten im Rahmen der Nutzung zu."
        },
        s9: {
          title: "§ 9 Haftung",
          p1: "Der Anbieter haftet unbeschränkt für Schäden aus der Verletzung von Leben, Körper oder Gesundheit.",
          p2: "Für sonstige Schäden haftet der Anbieter nur bei Vorsatz oder grober Fahrlässigkeit.",
          p3: "Die Haftung für leichte Fahrlässigkeit ist auf vorhersehbare, vertragstypische Schäden begrenzt.",
          p4: "Die Haftung nach dem Produkthaftungsgesetz bleibt unberührt.",
          p5: "Während der Beta-Phase wird keine Gewähr für Verfügbarkeit oder Fehlerfreiheit übernommen."
        },
        s10: {
          title: "§ 10 Gewährleistung",
          p1: "Der Anbieter gewährleistet die vertragsgemäße Bereitstellung der Dienste.",
          p2: "Mängel werden innerhalb angemessener Frist behoben.",
          p3: "Bei erheblichen Mängeln hat der Nutzer das Recht zur Minderung oder Kündigung.",
          p4: "Die Gewährleistung umfasst keine Fehler, die durch fehlerhafte Nutzung verursacht wurden."
        },
        s11: {
          title: "§ 11 Änderungen der AGB",
          p1: "Der Anbieter kann diese AGB ändern, soweit dies sachlich gerechtfertigt ist.",
          p2: "Änderungen werden dem Nutzer per E-Mail mitgeteilt.",
          p3: "Widerspricht der Nutzer nicht innerhalb von 30 Tagen, gelten die neuen AGB als akzeptiert.",
          p4: "Auf das Widerspruchsrecht und die Folgen des Schweigens wird bei der Änderungsmitteilung hingewiesen."
        },
        s12: {
          title: "§ 12 Schlussbestimmungen",
          p1: "Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des UN-Kaufrechts.",
          p2: "Gerichtsstand ist Nürnberg, sofern der Nutzer Kaufmann ist.",
          p3: "Sollten einzelne Bestimmungen unwirksam sein, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.",
          p4: "Die EU-Kommission stellt eine Plattform zur Online-Streitbeilegung bereit: https://ec.europa.eu/consumers/odr"
        }
      }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // 📲 INVITE PAGE - VOLLSTÄNDIG
    // ═══════════════════════════════════════════════════════════════════════
    invite: {
      title: "Du bist eingeladen!",
      subtitle: { 
        try: "Probiere", 
        smartApp: "aus – die smarte App für Familienorganisation.", 
        freeBeta: "Jetzt kostenlos in der Beta!" 
      },
      orVisit: "Oder besuche direkt",
      features: { 
        shopping: "Einkaufslisten", 
        recipes: "Rezepte", 
        calendar: "Kalender", 
        ai: "KI-Assistent" 
      },
      freeDuringBeta: "Kostenlos während der Beta",
      buttons: { 
        copyLink: "Link kopieren", 
        copied: "Kopiert!", 
        share: "Teilen", 
        print: "Drucken", 
        email: "E-Mail" 
      },
      printTip: "Tipp: Drucke diese Seite aus und hänge sie an den Kühlschrank!",
      share: {
        title: "Planivio - Haushalts-Manager",
        text: "Probiere Planivio aus – die smarte App für Familienorganisation!",
        whatsappText: "Hey! Probiere mal Planivio aus – eine super App für Familienorganisation:",
        telegramText: "Probiere Planivio aus – die smarte App für Familienorganisation!",
        emailSubject: "Einladung zu Planivio",
        emailBody: "Hey!\n\nIch nutze Planivio für unsere Familienorganisation und wollte es dir empfehlen. Es ist gerade kostenlos in der Beta!\n\nSchau mal hier:"
      }
    }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 🇬🇧 ENGLISH
  // ═══════════════════════════════════════════════════════════════════════════
  en: {
    app: {
      name: "Planivio",
      tagline: "Your intelligent household manager",
      loading: "Loading Planivio...",
    },

    nav: {
      modules: {
        dashboard: "Dashboard",
        calendar: "Calendar",
        assistant: "Assistant",
        notes: "Notes",
        tasks: "Tasks",
        shopping: "Shopping",
        recipes: "Recipes",
        household: "Household",
        budget: "Budget"
      }
    },

    header: {
      logout: "Logout",
      myHousehold: "My Household",
      user: "User",
      settings: "Settings",
      profile: "Profile",
      login: "Login"
    },

    auth: {
      login: "Login",
      logout: "Logout",
      register: "Register",
      getStarted: "Get Started",
      welcomeBack: "Welcome back"
    },

    mobile: {
      swipeHint: "Swipe (80px+) or use arrows to navigate"
    },

    assistant: {
      title: "Planivio Assistant",
      subtitle: "Your intelligent household helper",
      thinking: "Thinking...",
      loading: "Loading...",
      documents: "Documents",
      messages: "Messages",
      welcome: "How can I help you?",
      welcomeHint: "Write a message or upload documents",
      pleaseLogin: "Please log in",
      suggestions: "Suggestions",
      suggestion1: "What can I cook today?",
      suggestion2: "Create a shopping list",
      suggestion3: "Give me household tips",
      placeholder: "Type a message...",
      send: "Send",
      filesSelected: "file(s) selected",
      upload: "Upload",
      attachDocuments: "Upload documents",
      dropDocuments: "Drop documents here",
      documentsUploaded: "document(s) uploaded",
      uploadFailed: "Upload failed",
      clearChat: "Clear chat",
      clearDocuments: "Clear documents",
      ragDocuments: "RAG Documents",
      noResponse: "No response received",
      error: "An error occurred"
    },

    theme: {
      light: "Light",
      dark: "Dark",
      system: "System",
      toggle: "Toggle theme",
      lightMode: "Activate light mode",
      darkMode: "Activate dark mode"
    },

    shopping: {
      title: "Shopping Lists",
      newList: "New List",
      list: "List",
      lists: "Lists",
      fromRecipes: "from recipes",
      createList: "Create List",
      editList: "Edit List",
      deleteList: "Delete List",
      addItem: "Add Item",
      editItem: "Edit Item",
      deleteItem: "Remove Item",
      checkAll: "Check All",
      uncheckAll: "Uncheck All",
      clearChecked: "Clear Checked",
      form: {
        name: "List Name",
        namePlaceholder: "Enter name...",
        description: "Description",
        descriptionPlaceholder: "Optional description...",
        assignedTo: "Assigned To",
        dueDate: "Due Date",
        itemName: "Item Name",
        itemNamePlaceholder: "Enter item...",
        quantity: "Quantity",
        unit: "Unit",
        category: "Category",
        priority: "Priority"
      },
      itemName: "Item name",
      quantity: "Quantity",
      unit: "Unit",
      category: "Category",
      categories: {
        dairy: "Dairy",
        bakery: "Bakery",
        produce: "Fruits & Vegetables",
        meat: "Meat & Fish",
        frozen: "Frozen",
        beverages: "Beverages",
        household: "Household",
        other: "Other"
      },
      units: {
        piece: "pc",
        liter: "L",
        gram: "g",
        kilogram: "kg",
        pack: "pack",
        bottle: "bottle"
      },
      priority: {
        high: "High",
        normal: "Normal",
        medium: "Medium",
        low: "Low"
      },
      status: {
        items: "items",
        checked: "checked",
        remaining: "remaining"
      },
      emptyList: "No lists available",
      noItems: "No items in this list",
      createFirstList: "Create your first shopping list!",
      loading: "Loading shopping lists...",
      tooManyAttempts: "Too many load attempts. Please reload the page.",
      notLoggedIn: "Not logged in. Please log in.",
      notAuthorized: "Not authorized. Please log in again.",
      errorLoading: "Error loading lists",
      errorCreating: "Error creating list",
      errorUpdating: "Error updating list",
      errorDeleting: "Error deleting list",
      deleteConfirm: "Really delete list?",
      deleteItemConfirm: "Delete item?",
      weeklyPurchase: "Weekly Shopping",
      groceriesForWeek: "Groceries for the whole week"
    },

    recipes: {
      title: "Recipes",
      recipesCount: "Recipes",
      createRecipe: "Create Recipe",
      newRecipe: "New Recipe",
      editRecipe: "Edit Recipe",
      addToList: "Add to shopping list",
      toShoppingList: "To Shopping List",
      searchRecipes: "Search recipes",
      viewMode: { cards: "Cards", list: "List" },
      categories: {
        mainDish: "Main Dish",
        appetizer: "Appetizer",
        dessert: "Dessert",
        snack: "Snack",
        breakfast: "Breakfast"
      },
      difficulty: { easy: "Easy", medium: "Medium", hard: "Hard" },
      filters: { allCategories: "All Categories", allDifficulties: "All Difficulties" },
      ingredients: "Ingredients",
      ingredientsList: "Ingredients",
      instructions: "Instructions",
      servings: "Servings",
      minutes: "min",
      prepTime: "Prep time",
      cookTime: "Cook time",
      form: {
        name: "Name",
        namePlaceholder: "Recipe name...",
        description: "Description",
        descriptionPlaceholder: "Short description...",
        category: "Category",
        difficulty: "Difficulty",
        servings: "Servings",
        prepTime: "Prep Time (min)",
        cookTime: "Cook Time (min)",
        ingredients: "Ingredients",
        addIngredient: "Add Ingredient",
        instructions: "Instructions",
        addStep: "Add Step"
      },
      noRecipes: "No recipes available",
      noRecipesYet: "No recipes yet",
      noRecipesFound: "No recipes found",
      createFirstRecipe: "Create your first recipe!",
      tryOtherFilters: "Try other filters",
      loading: "Loading recipes...",
      tooManyAttempts: "Too many load attempts. Please reload the page.",
      notLoggedIn: "Not logged in. Please log in.",
      howManyServings: "How many servings?",
      shoppingListCreated: "Shopping list created",
      errorCreating: "Error creating recipe",
      errorDeleting: "Error deleting recipe",
      errorCreatingShoppingList: "Error creating shopping list",
      confirmDelete: "Delete recipe?"
    },

    calendar: {
      title: "Calendar",
      appointments: "Appointments",
      views: { day: "Day", week: "Week", month: "Month", agenda: "Agenda" },
      today: "Today",
      new: "New",
      export: "Export",
      sync: "Sync",
      enableNotifications: "Enable reminders?",
      notificationHint: "Receive notifications for upcoming appointments",
      activate: "Activate",
      later: "Later",
      notificationsEnabled: "Notifications enabled!",
      notificationsDenied: "Notifications were denied.",
      loading: "Loading calendar...",
      pleaseLogin: "Please log in to see appointments",
      noAppointmentsToExport: "No appointments to export",
      swipeHint: "Swipe to navigate",
      errorLoading: "Error loading appointments",
      errorSaving: "Error saving",
      errorDeleting: "Error deleting",
      confirmDelete: "Really delete appointment?",
      newEvent: "New Event",
      editEvent: "Edit Event",
      eventTitle: "Title",
      eventDate: "Date",
      eventTime: "Time",
      eventDescription: "Description",
      eventLocation: "Location",
      allDay: "All Day",
      reminder: "Reminder",
      recurring: "Recurring",
      recurrence: { none: "No repeat", daily: "Daily", weekly: "Weekly", monthly: "Monthly", yearly: "Yearly" },
      noEvents: "No events"
    },

    budget: {
      title: "Budget & Finances",
      tabs: { overview: "Overview", transactions: "Transactions", categories: "Categories", limits: "Limits" },
      income: "Income",
      expenses: "Expenses",
      balance: "Balance",
      savingsRate: "Savings Rate",
      transaction: "Transaction",
      dateRange: { currentWeek: "Current Week", currentMonth: "Current Month", lastMonth: "Last Month", lastYear: "Last Year", all: "All Time" },
      addTransaction: "Transaction",
      addCategory: "Category",
      addLimit: "Limit",
      noCategories: "No categories yet",
      setupCategoriesHint: "Create 65+ default categories with one click!",
      setupCategories: "Create Default Categories",
      categoriesCreated: "default categories created!",
      noTransactionsInPeriod: "No transactions in selected period",
      noLimits: "No limits defined",
      confirmSetupCategories: "Do you want to create 65+ default categories?",
      confirmDeleteCategory: "Delete category?",
      confirmDeleteTransaction: "Delete transaction?",
      confirmDeleteLimit: "Delete limit?",
      loading: "Loading budget data...",
      pleaseLogin: "Please log in...",
      description: "Description",
      amount: "Amount",
      date: "Date",
      type: "Type",
      category: "Category",
      monthly: "Monthly",
      yearly: "Yearly",
      types: { income: "Income", expense: "Expense", all: "All" }
    },

    notes: {
      title: "Notes",
      notes: "Notes",
      newNote: "New Note",
      editNote: "Edit Note",
      categories: { personal: "Personal", work: "Work", ideas: "Ideas", important: "Important" },
      filters: { allCategories: "All Categories" },
      form: {
        title: "Title",
        titlePlaceholder: "Enter title...",
        content: "Content",
        contentPlaceholder: "Enter note...",
        category: "Category"
      },
      noNotes: "No notes available",
      noResults: "No results found",
      loading: "Loading notes...",
      errorLoading: "Error loading",
      confirmDeleteNote: "Delete note?",
      viewMode: { grid: "Grid", list: "List" }
    },

    tasks: {
      title: "Tasks",
      tasks: "Tasks",
      newTask: "New Task",
      editTask: "Edit Task",
      categories: { personal: "Personal", work: "Work", household: "Household", shopping: "Shopping" },
      priority: { low: "Low", medium: "Medium", high: "High" },
      status: { open: "Open", done: "Done", overdue: "Overdue", all: "All" },
      filters: { allCategories: "All Categories", allPriorities: "All Priorities", allStatus: "All Status" },
      form: {
        title: "Title",
        titlePlaceholder: "Enter task...",
        description: "Description",
        descriptionPlaceholder: "Task details...",
        category: "Category",
        priority: "Priority",
        dueDate: "Due Date",
        dueTime: "Time",
        assignedTo: "Assigned To"
      },
      noTasks: "No tasks available",
      noResults: "No results found",
      loading: "Loading tasks...",
      errorLoading: "Error loading",
      confirmDeleteTask: "Delete task?",
      viewMode: { grid: "Grid", list: "List" },
      stats: { total: "Total", open: "Open", completed: "Completed", highPriority: "High Priority", today: "Due Today" }
    },

    household: {
      title: "Household Management",
      members: "Members",
      tabs: { members: "Members", statistics: "Statistics" },
      inviteMember: "Invite Member",
      newMember: "New Member",
      addMember: "Add Member",
      remove: "Remove",
      settings: "Settings",
      roles: { admin: "Administrator", member: "Member", child: "Child", guest: "Guest" },
      form: { name: "Name", namePlaceholder: "Enter name...", role: "Role", avatar: "Avatar" },
      statistics: "Household Statistics",
      stats: { shoppingLists: "Shopping Lists", notes: "Notes", tasks: "Tasks", recipes: "Recipes", appointments: "Appointments" },
      loading: "Loading household...",
      noMembers: "No members yet",
      errorLoading: "Error loading",
      notLoggedIn: "Not logged in",
      confirmRemoveMember: "Remove member?"
    },

    dashboard: {
      title: "Dashboard",
      welcome: "Welcome",
      welcomeBack: "Welcome, {{name}}!",
      subtitle: "Here is your household overview",
      open: "open",
      today: "Today",
      tomorrow: "Tomorrow",
      balance: "Balance",
      current: "Current",
      widgets: { shopping: "Shopping List", tasks: "Tasks", appointments: "Appointments", budget: "Budget" },
      shoppingList: "Shopping List",
      task: "Task",
      appointment: "Appointment",
      allDay: "All Day",
      of: "of",
      viewAll: "All Lists",
      viewAllTasks: "All Tasks",
      viewAllAppointments: "All Appointments",
      manageBudget: "Manage Budget",
      createBudget: "Create Budget",
      noLists: "No lists",
      noOpenTasks: "No open tasks",
      noAppointments: "No appointments",
      noBudget: "No budget",
      quickAccess: "Quick Access",
      quickActions: "Quick Actions",
      recentActivity: "Recent Activity",
      loading: "Loading Dashboard...",
      retry: "Try Again",
      noToken: "No token received",
      stats: { openTasks: "Open Tasks", shoppingItems: "Shopping Items", upcomingEvents: "Upcoming Events", monthlyBudget: "Monthly Budget" }
    },

    common: {
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      edit: "Edit",
      add: "Add",
      close: "Close",
      confirm: "Confirm",
      back: "Back",
      next: "Next",
      search: "Search",
      filter: "Filter",
      sort: "Sort",
      loading: "Loading...",
      error: "Error",
      success: "Success",
      warning: "Warning",
      info: "Info",
      yes: "Yes",
      no: "No",
      all: "All",
      none: "None",
      required: "Required",
      optional: "Optional"
    },

    pages: {
      landing: { hero: { title: "Your household. Perfectly organized.", subtitle: "Planivio combines shopping lists, recipes, calendar and budget in one app." }, cta: "Start for free" },
      features: { 
        title: "Features",
        nav: {
          backHome: "Back to Home",
          startNow: "Get Started"
        },
        hero: {
          title: "All Features at a Glance",
          subtitle: "Discover how Planivio simplifies your family's daily life.",
          allAvailable: "All modules already available"
        },
        badge: {
          allAvailable: "All modules available"
        },
        screenshot: {
          comingSoon: "Screenshot coming soon"
        },
        useCases: {
          title: "Use Cases"
        },
        intro: {
          problem: {
            title: "Sound familiar?",
            text: "Appointments in your head, shopping list on paper, recipes in browser tabs, budget in Excel, to-dos in chat. You juggle 5-7 apps daily – and someone still forgets the milk. That's mental load. And in 71% of households, one person carries this invisible burden alone."
          },
          solution: {
            title: "PLANIVIO changes that.",
            text: "One platform. Everything connected. AI recognizes patterns automatically: Birthday in calendar → cake recipe → ingredients on shopping list → best deal found. Less stress. More time. For what really matters."
          },
          stats: {
            mentalLoad: "of households carry the burden alone",
            apps: "apps you juggle daily",
            households: "households in Germany"
          },
          flow: {
            calendar: "Calendar",
            recipes: "Recipe",
            shopping: "Shopping",
            budget: "Deal"
          },
          trust: {
            euHosting: "EU Hosting",
            dsgvo: "GDPR Compliant",
            madeInGermany: "Made in Germany"
          }
        },
        cta: {
          title: "Ready for better organization?",
          subtitle: "Start free now and discover all features.",
          feedbackHelps: "Your feedback helps us make Planivio even better!",
          startNow: "Start Free Now"
        },
        modules: {
          shopping: {
            title: "Shopping Lists",
            tagline: "Never forget anything again",
            desc: "Create and share shopping lists with your family. Items are automatically categorized and synchronized.",
            features: {
              categorization: "Automatic categorization by store departments",
              multipleLists: "Multiple lists for different stores",
              sync: "Real-time sync with all family members",
              offline: "Offline mode for shopping",
              recurring: "Automatically add recurring items"
            },
            useCases: {
              family: "The whole family can add items - Dad sees immediately what Mom added",
              mobile: "Check off items in the store - even without internet"
            }
          },
          recipes: {
            title: "Recipes",
            tagline: "Your favorite recipes in one place",
            desc: "Save family recipes, import from the web, and add ingredients directly to your shopping list.",
            features: {
              create: "Create and edit your own recipes",
              toShopping: "Add ingredients to shopping list with one click",
              categories: "Categories and tags for quick finding",
              portions: "Adjust portions - quantities are recalculated",
              share: "Share recipes with family and friends"
            },
            useCases: {
              grandma: "Keep grandma's secret recipe digitally and never lose it again",
              shopping: "Sunday roast planned? All ingredients automatically on the shopping list"
            }
          },
          ai: {
            title: "AI Assistant",
            tagline: "Your smart household helper",
            desc: "Ask the AI assistant for recipe ideas, meal planning, or household tips.",
            features: {
              suggestions: "Recipe suggestions based on available ingredients",
              mealPlanning: "Automatic weekly meal planning",
              tips: "Personalized household tips",
              optimization: "Shopping list optimization by store layout",
              natural: "Natural language input - just ask!"
            },
            useCases: {
              ingredients: "\"What can I cook with potatoes, cheese, and ham?\" - get ideas instantly",
              planning: "\"Plan healthy meals for the week\" - meal plan done"
            }
          },
          calendar: {
            title: "Calendar",
            tagline: "Family at a glance",
            desc: "A shared calendar for all appointments, activities, and family reminders.",
            features: {
              shared: "Shared family calendar",
              colorCoding: "Color coding per family member",
              reminders: "Reminders via push notification",
              recurring: "Recurring events (garbage collection, sports club...)",
              views: "Day, week, and month views"
            },
            useCases: {
              appointments: "Doctor appointments, parent meetings, sports training - all at a glance",
              birthdays: "Never forget a birthday again"
            }
          },
          budget: {
            title: "Budget",
            tagline: "Finances under control",
            desc: "Keep track of household expenses and save specifically for shared goals.",
            features: {
              categorize: "Record expenses by category",
              budgets: "Set and monitor monthly budgets",
              charts: "Clear charts and statistics",
              goals: "Define and track savings goals",
              stats: "Comparison with previous months"
            },
            useCases: {
              overview: "Finally know where the money goes each month",
              vacation: "Save together for the summer vacation"
            }
          },
          notes: {
            title: "Notes",
            tagline: "Keep track of important things",
            desc: "Shared notes for everything the family should know.",
            features: {
              shared: "Shared notes for everyone",
              folders: "Folders for better organization",
              search: "Full-text search in all notes",
              attachments: "Attach images and files",
              pin: "Pin important notes"
            },
            useCases: {
              wifi: "WiFi password, door codes, important numbers - all at hand",
              contacts: "Contact details for babysitter, handyman, neighbors"
            }
          },
          tasks: {
            title: "Tasks",
            tagline: "Organize household",
            desc: "Distribute household tasks fairly and keep track of who does what.",
            features: {
              create: "Create and assign tasks",
              recurring: "Recurring tasks (weekly, monthly)",
              assign: "Assign tasks to family members",
              reminders: "Reminders before due date",
              progress: "View progress and statistics"
            },
            useCases: {
              trash: "\"Take out the trash\" - automatically for Dad every Tuesday",
              kids: "Kids learn responsibility with their own small tasks"
            }
          }
        }
      },
      impressum: { title: "Legal Notice" },
      datenschutz: { title: "Privacy Policy" },
      agb: { title: "Terms of Service" },
      faq: { title: "FAQ" }
    },

    feedback: {
      title: "Feedback",
      placeholder: "Your feedback...",
      submit: "Submit",
      thanks: "Thanks for your feedback!",
      types: { bug: "Report Bug", feature: "Feature Request", general: "General Feedback" }
    },

    cookies: { message: "We use cookies to improve your experience.", accept: "Accept", decline: "Decline", settings: "Settings" },
    email: { verification: { title: "Verify Email", message: "Please verify your email address", resend: "Resend" } },
    language: { select: "Language", de: "Deutsch", en: "English", fr: "Français" },
    time: { today: "Today", yesterday: "Yesterday", tomorrow: "Tomorrow", daysAgo: "{{count}} days ago", hoursAgo: "{{count}} hours ago", minutesAgo: "{{count}} minutes ago", justNow: "Just now" },

    // ✅ v4: LANDING PAGE UPDATED
    landing: {
      betaBanner: { text: "Try it free now – Your feedback shapes the app!" },
      nav: { features: "Features", priceCompare: "Price Compare", privacy: "Privacy", pricing: "Pricing", login: "To App", experience: "Experience PLANIVIO" },
      hero: {
        titlePart1: "Stop the",
        titleGradient: "Mental Chaos.",
        betaBadge: "Beta Launch 2026 – Try it free now!",
        title: "Household Management for the Whole Family",
        lead: "Shopping lists on paper. Appointments in your head. Recipes in three different apps.",
        subtitle: "PLANIVIO brings everything together – for more time with your family.",
        ctaStart: "Start for free",
        ctaFeatures: "How it works"
      },
      stats: {
        apps: "apps a family uses for household & organization",
        oneApp: "app for everything – that's PLANIVIO",
        priceDiff: "price differences between supermarkets possible*",
        euServer: "servers in the EU, 100% GDPR compliant",
        source: "* Consumer advice center NRW, Market check 2023"
      },
      priceCompare: {
        comingSoon: "Coming Soon",
        title: "Find the cheapest supermarket",
        subtitle: "Comparing prices pays off – the differences between supermarkets are often bigger than expected.",
        cheapest: "Cheapest",
        disclaimer: "* Illustrative example of a weekly shop (4 persons). Actual prices vary by product, region and time.",
        sourceText: "The savings potential is real: A market check by the consumer advice center NRW showed price differences of over 100% for 15 of 20 products – depending on the supermarket.",
        sourceLabel: "Source: Consumer advice center NRW, Market check May 2023"
      },
      comparison: {
        label: "Before vs. After",
        title: "Your daily life becomes this easy",
        subtitle: "With PLANIVIO, the whole family has everything in view – at a glance."
      },
      benefitsLabel: "Benefits",
      benefits: {
        title: "What Planivio means for you",
        subtitle: "More than just an app – a new way of life.",
        peace: { title: "Clear Mind", text: "Never worry about forgetting something again." },
        save: { title: "Save Money", text: "Overview of expenses helps you shop more consciously." },
        time: { title: "Save Time", text: "Less searching, less coordinating, more free time." },
        family: { title: "Relieve Family", text: "Distribute responsibilities fairly, everyone stays informed." },
        stress: { title: "Reduce Stress", text: "Structure provides security and calm." },
        focus: { title: "Stay Focused", text: "Everything important in one place – no more app-hopping." }
      },
      trust: { germany: "Developed in Germany", eu: "Servers in Germany", dsgvo: "100% GDPR compliant", noCreditCard: "No credit card" },
      scenarios: {
        title: "Your daily life becomes this easy",
        subtitle: "Real situations, real solutions.",
        before: "Before",
        after: "With Planivio",
        birthday: { title: "Planning a birthday party", before: "Who brings what? When to buy? Three chats, two lists, zero overview.", after: "Appointment → Select recipe → Ingredients automatically on the list. Done." },
        shopping: { title: "Weekly shopping", before: "Everyone writes somewhere else. In the end, the milk is still missing.", after: "One list for everyone. Real-time sync. Nothing forgotten." },
        guests: { title: "Unexpected guests", before: "Panic. What do we have? What can we cook?", after: "Browse recipes → \"What can I cook?\" → Done." },
        budget: { title: "End of month", before: "Where did the money go? No idea.", after: "Expenses in view. Categories show where you can save." }
      },
      privacy: {
        title: "Your data stays in Germany.",
        text: "PLANIVIO is developed in Germany and hosted on German servers. No data sharing with third parties. No advertising. No hidden business models.",
        highlight: "Your family recipes, shopping lists and appointments belong to you.",
        promise: "Our Privacy Promise",
        euServers: "Server location: Germany",
        noThirdParty: "No data sharing with third parties – ever",
        noAds: "No use of your data for advertising",
        encryption: "Bank-grade encryption (AES-256)",
        deletable: "Complete data deletion possible at any time",
        transparent: "Transparent privacy policy",
        noTracking: "No tracking",
        deleteAnytime: "Delete anytime",
        exportData: "Export data",
        learnMore: "Learn more about privacy"
      },
      betaInvite: {
        badge: "Limited spots",
        title: "Join the Beta!",
        text: "Test Planivio for free, shape the future and secure exclusive benefits.",
        perk1: "Completely free during beta",
        perk2: "50% discount after beta for 12 months",
        perk3: "Direct influence on new features",
        cta: "Start free now"
      },
      features: { 
        subtitle: "Everything your household needs", 
        title: "Modules for complete family life", 
        viewAll: "all features in detail", 
        calendar: { title: "Calendar", desc: "Keep track of all family appointments" }, 
        shopping: { title: "Shopping Lists", desc: "Smart lists with categories" }, 
        recipes: { title: "Recipes", desc: "Save & share recipes" }, 
        tasks: { title: "Tasks", desc: "Organize and assign household tasks" },
        budget: { title: "Budget", desc: "Finances under control" }, 
        notes: { title: "Notes", desc: "Central documentation" }, 
        assistant: { title: "AI Assistant", desc: "Intelligent help" } 
      },
      pricing: {
        subtitle: "Pricing",
        title: "During Beta, Planivio is completely free!",
        availableNow: "Available now",
        betaAdvantage: "Beta Tester Advantage",
        betaDiscount: "Join now and get 50% off all plans for 12 months after beta!",
        beta: {
          name: "Beta",
          period: "Free",
          cta: "Start free now",
          features: {
            allModules: "All available modules",
            fiveMembers: "Up to 5 family members",
            aiIncluded: "AI assistant included",
            emailSupport: "Email support",
            feedbackInfluence: "Influence on features"
          }
        },
        pro: {
          name: "Pro",
          period: "EUR/month",
          periodSoon: "Coming soon",
          cta: "Coming soon",
          features: {
            extended: "Extended features",
            twentyFiveMembers: "Up to 25 family members",
            prioritySupport: "Priority support",
            extendedAI: "Extended AI features",
            priceComparison: "Supermarket price comparison"
          }
        },
        family: {
          name: "Family+",
          period: "EUR/month",
          periodSoon: "Coming soon",
          cta: "Coming soon",
          features: {
            allPro: "All Pro features",
            unlimitedMembers: "Unlimited members",
            multiHousehold: "Multi-household support",
            apiAccess: "API access",
            premiumSupport: "Premium support"
          }
        }
      },
      betaPerks: {
        free: { title: "Free", desc: "Completely free during beta" },
        feedback: { title: "Your feedback counts", desc: "Shape the features" },
        earlyAdopter: { title: "Early adopter benefits", desc: "Special conditions after beta" }
      },
      cta: {
        title: "Ready to organize your household?",
        subtitle: "Start free now and join the Planivio community.",
        startButton: "Start free now",
        inviteButton: "Invite friends",
        inviteHint: "Share Planivio with family & friends – printable QR code!"
      },
      footer: { rights: "All rights reserved.", privacy: "Privacy", terms: "Terms", imprint: "Imprint", contact: "Contact", madeIn: "Made with ❤️ in Bavaria" }
    },

    faq: { 
      title: "Frequently Asked Questions", 
      subtitle: "Find answers to the most common questions about Planivio", 
      searchPlaceholder: "Search questions...", 
      noResults: "No results found", 
      tryOther: "Try other search terms or select a different category.",
      contactTitle: "Question not listed?",
      contactSubtitle: "Contact us directly - we're happy to help!",
      categories: { 
        all: "All", 
        general: "General", 
        beta: "Beta", 
        features: "Features", 
        privacy: "Privacy", 
        technical: "Technical" 
      },
      // ===== GENERAL =====
      general: {
        whatIsHomeM: {
          q: "What is Planivio?",
          a: "Planivio is a modern household management platform for families. With Planivio, you can manage shopping lists, recipes, calendar appointments, budget and much more in one place. All family members can collaborate in real-time."
        },
        forWhom: {
          q: "Who is Planivio for?",
          a: "Planivio is designed for families and households who want to improve their daily organization. Whether couples, families with children or shared apartments - Planivio helps you stay organized and coordinate tasks."
        },
        isFree: {
          q: "Is Planivio free?",
          a: "During the beta phase, Planivio is completely free! You get access to all available features without restrictions. After beta, various pricing plans will be offered, with beta testers receiving 50% discount for 12 months."
        },
        devices: {
          q: "On which devices can I use Planivio?",
          a: "Planivio works on all devices with a modern web browser: computers (Windows, Mac, Linux), tablets (iPad, Android) and smartphones (iPhone, Android). You can also save Planivio as an app on your home screen."
        }
      },
      // ===== BETA =====
      beta: {
        whatIsBeta: {
          q: "What does 'Beta' mean?",
          a: "Beta means that Planivio is still in development. You get early access to new features, but there may still be minor bugs. Your feedback helps us improve Planivio!"
        },
        howLong: {
          q: "How long will the beta phase last?",
          a: "The beta phase is planned for 2026. The exact end depends on how quickly we can complete and stabilize all planned features. You will be notified in advance."
        },
        benefits: {
          q: "What benefits do I get as a beta tester?",
          a: "As a beta tester you receive: Free use during the entire beta phase, 50% discount on all plans for 12 months after beta, direct influence on new features through your feedback, and early adopter status with special benefits."
        },
        feedback: {
          q: "Can I give feedback?",
          a: "Yes, absolutely! Use the feedback button (💬) at the bottom right of every page. You can report bugs, make feature requests or give general feedback. Every response helps us make Planivio better."
        },
        dataAfter: {
          q: "What happens to my data after the beta?",
          a: "Your data is preserved! If you choose a paid plan after beta, all your shopping lists, recipes, calendar entries etc. will be transferred. Nothing is lost."
        }
      },
      // ===== FEATURES =====
      features: {
        modules: {
          q: "What modules does Planivio offer?",
          a: "Planivio currently offers: 🛒 Shopping lists (with categories), 🍳 Recipes (save & share), 📅 Family calendar, 💰 Budget tracking, 📝 Notes, ✅ Tasks and 🤖 AI Assistant. More modules are planned."
        },
        aiAssistant: {
          q: "How does the AI assistant work?",
          a: "The AI assistant helps you with everyday questions: 'What can I cook with potatoes and chicken?', 'Plan dinner for 4 people', or 'What groceries should I restock?'. It learns your preferences and gives personalized tips."
        },
        multiUser: {
          q: "Can multiple people use it simultaneously?",
          a: "Yes! Planivio is made for families. Up to 5 family members can access all data simultaneously. Changes are synchronized in real-time - when mom adds something to the shopping list, dad sees it immediately on his phone."
        },
        recipeToShopping: {
          q: "Can I add recipe ingredients directly to the shopping list?",
          a: "Yes, this is one of our most popular features! Each recipe has an 'Add to shopping list' button. The ingredients are automatically transferred with the correct quantities - you can adjust the portions beforehand."
        },
        offline: {
          q: "Does Planivio work offline?",
          a: "Currently, Planivio requires an internet connection. We are working on an offline mode for the future so you can access your shopping list even without internet."
        }
      },
      // ===== PRIVACY =====
      privacy: {
        dataStorage: {
          q: "Where is my data stored?",
          a: "All data is stored on servers in the EU (Microsoft Azure, West Europe region). We strictly comply with GDPR and German data protection laws. Your data never leaves the EU."
        },
        dataSold: {
          q: "Is my data sold or used for advertising?",
          a: "No, never! We don't sell data to third parties and don't show advertising. Planivio is funded exclusively through subscriptions. Your data belongs to you."
        },
        cookies: {
          q: "What cookies does Planivio use?",
          a: "We only use technically necessary cookies for login (session cookie). No tracking cookies, no advertising cookies, no third-party analytics."
        },
        export: {
          q: "Can I export my data?",
          a: "Yes! You can request an export of all your data at any time (right to data portability under GDPR). Contact us at kontakt@planivio.de."
        },
        deleteAccount: {
          q: "How can I delete my account?",
          a: "You can delete your account at any time in the settings or send us an email to kontakt@planivio.de. All your data will then be irrevocably deleted within 30 days."
        }
      },
      // ===== TECHNICAL =====
      technical: {
        browsers: {
          q: "Which browsers are supported?",
          a: "Planivio works with all modern browsers: Chrome, Firefox, Safari, Edge (current versions). Internet Explorer is not supported."
        },
        installMobile: {
          q: "How do I install Planivio on my phone?",
          a: "Open Planivio in your browser, tap 'Share' (iOS) or the menu (Android) and select 'Add to Home Screen'. Planivio will then appear like an app on your home screen."
        },
        forgotPassword: {
          q: "I forgot my password. What should I do?",
          a: "Click 'Forgot password' on the login page. You will receive an email with a link to reset your password. The link is valid for 24 hours."
        },
        notLoading: {
          q: "The app won't load. What can I do?",
          a: "Try these steps: 1) Reload the page (F5 or pull-to-refresh), 2) Clear browser cache, 3) Try a different browser, 4) Check your internet connection. If nothing helps, contact us via the feedback form."
        },
        nativeApp: {
          q: "Is there a native app for iOS/Android?",
          a: "Not currently - Planivio is a web app that works on all devices. Native apps for iOS and Android are planned for the future. However, you can already save the web app to your home screen!"
        }
      }
    },
    features: {
      hero: { title: "What can Planivio do?", subtitle: "Discover all modules that make your family life easier.", allAvailable: "All features available" },
      badge: { allAvailable: "All features available" },
      nav: { backHome: "Back to homepage", startNow: "Start free now" },
      screenshot: { comingSoon: "Screenshot coming soon" },
      useCases: { title: "Use Cases" },
      cta: { title: "Sounds good?", subtitle: "Start free now and try all features.", feedbackHelps: "Your feedback helps us make Planivio even better!", startFree: "Start free", startNow: "Start free now" },
      modules: {
        // ✅ NEW: Dashboard Module
        dashboard: {
          title: "Dashboard",
          tagline: "Your command center",
          desc: "The dashboard is your central hub. See all important appointments, tasks, and shopping items of the day at a glance.",
          features: {
            overview: "Daily overview with upcoming appointments and tasks",
            quickActions: "One-click access to frequent actions",
            widgets: "Compact preview of all modules",
            stats: "Statistics cards with quick numbers",
            greeting: "Personalized greeting"
          },
          useCases: {
            morning: "Start every morning with a clear overview of your day",
            quickAccess: "Quick access to everything important without searching"
          }
        },
        shopping: {
          title: "Shopping Lists",
          tagline: "Never forget anything again",
          desc: "Create and share shopping lists with your family. Items are automatically categorized and synchronized.",
          features: {
            categorization: "Automatic categorization by store departments",
            multipleLists: "Multiple lists for different stores",
            sync: "Real-time sync with all family members",
            offline: "Offline mode for shopping",
            recurring: "Automatically add recurring items"
          },
          useCases: {
            family: "The whole family can add items - Dad sees immediately what Mom added",
            mobile: "Check off items in the store - even without internet"
          }
        },
        recipes: {
          title: "Recipes",
          tagline: "Your favorite recipes in one place",
          desc: "Save family recipes, import from the web, and add ingredients directly to your shopping list.",
          features: {
            create: "Create and edit your own recipes",
            toShopping: "Add ingredients to shopping list with one click",
            categories: "Categories and tags for quick finding",
            portions: "Adjust portions - quantities are recalculated",
            share: "Share recipes with family and friends"
          },
          useCases: {
            grandma: "Keep grandma's secret recipe digitally and never lose it again",
            shopping: "Sunday roast planned? All ingredients automatically on the shopping list"
          }
        },
        ai: {
          title: "AI Assistant",
          tagline: "Your smart household helper",
          desc: "Ask the AI assistant for recipe ideas, meal planning, or household tips.",
          features: {
            suggestions: "Recipe suggestions based on available ingredients",
            mealPlanning: "Automatic weekly meal planning",
            tips: "Personalized household tips",
            optimization: "Shopping list optimization by store layout",
            natural: "Natural language input - just ask!"
          },
          useCases: {
            ingredients: "\"What can I cook with potatoes, cheese, and ham?\" - get ideas instantly",
            planning: "\"Plan healthy meals for the week\" - meal plan done"
          }
        },
        calendar: {
          title: "Calendar",
          tagline: "Family at a glance",
          desc: "A shared calendar for all appointments, activities, and family reminders.",
          features: {
            shared: "Shared family calendar",
            colorCoding: "Color coding per family member",
            reminders: "Reminders via push notification",
            recurring: "Recurring events (garbage collection, sports club...)",
            views: "Day, week, and month views"
          },
          useCases: {
            appointments: "Doctor appointments, parent meetings, sports training - all at a glance",
            birthdays: "Never forget a birthday again"
          }
        },
        budget: {
          title: "Budget",
          tagline: "Finances under control",
          desc: "Keep track of household expenses and save specifically for shared goals.",
          features: {
            categorize: "Record expenses by category",
            budgets: "Set and monitor monthly budgets",
            charts: "Clear charts and statistics",
            goals: "Define and track savings goals",
            stats: "Comparison with previous months"
          },
          useCases: {
            overview: "Finally know where the money goes each month",
            vacation: "Save together for the summer vacation"
          }
        },
        notes: {
          title: "Notes",
          tagline: "Keep track of important things",
          desc: "Shared notes for everything the family should know.",
          features: {
            shared: "Shared notes for everyone",
            folders: "Folders for better organization",
            search: "Full-text search in all notes",
            attachments: "Attach images and files",
            pin: "Pin important notes"
          },
          useCases: {
            wifi: "WiFi password, door codes, important numbers - all at hand",
            contacts: "Contact details for babysitter, handyman, neighbors"
          }
        },
        tasks: {
          title: "Tasks",
          tagline: "Organize household",
          desc: "Distribute household tasks fairly and keep track of who does what.",
          features: {
            create: "Create and assign tasks",
            recurring: "Recurring tasks (weekly, monthly)",
            assign: "Assign tasks to family members",
            reminders: "Reminders before due date",
            progress: "View progress and statistics"
          },
          useCases: {
            trash: "\"Take out the trash\" - automatically for Dad every Tuesday",
            kids: "Kids learn responsibility with their own small tasks"
          }
        },
        // ✅ NEW: Household Module
        household: {
          title: "Household",
          tagline: "Manage together",
          desc: "Manage your household and invite family members. Everything under one digital roof.",
          features: {
            create: "Create and set up household",
            invite: "Invite members via code or link",
            roles: "Admin and member permissions",
            profiles: "Family member profiles with photo and role",
            activity: "Activity overview - who contributed what"
          },
          useCases: {
            family: "Gather the whole family under one digital roof",
            wg: "Perfect for shared apartments too - share and organize fairly"
          }
        }
      }
    },
    legal: {
      privacy: {
        title: "Privacy Policy",
        lastUpdated: "Last updated",
        date: "January 2026",
        toc: "Table of Contents",
        germany: "Germany",
        email: "Email",
        imprint: "Legal Notice",
        imprintLink: "Go to Legal Notice",
        
        sections: {
          responsible: "Data Controller",
          overview: "Overview of Processing",
          legal: "Legal Basis",
          security: "Security Measures",
          deletion: "Data Deletion",
          hosting: "Web Hosting",
          auth: "Registration and Authentication",
          contact: "Contact",
          rights: "Your Rights"
        },
        
        overviewText: "The following overview summarizes the types of data processed and the purposes of processing.",
        dataTypes: "Types of Data Processed",
        dataTypesList: {
          inventory: "Inventory data (e.g., names, addresses)",
          content: "Content data (e.g., form inputs)",
          usage: "Usage data (e.g., pages visited, access times)",
          meta: "Meta/communication data (e.g., IP addresses)"
        },
        affectedPersons: "Affected Persons",
        affectedPersonsList: {
          users: "Users of our platform"
        },
        purposes: "Purposes of Processing",
        purposesList: {
          provision: "Provision of our online services",
          contract: "Contractual services and support",
          security: "Security measures",
          inquiries: "Contact requests and communication"
        },
        
        legalText: "Below we inform you about the legal basis of data processing:",
        legalBasis: {
          consent: "Consent (Art. 6 para. 1 lit. a GDPR)",
          consentDesc: "You have given consent to the processing.",
          contract: "Contract fulfillment (Art. 6 para. 1 lit. b GDPR)",
          contractDesc: "Processing is necessary for contract fulfillment.",
          legitimate: "Legitimate interests (Art. 6 para. 1 lit. f GDPR)",
          legitimateDesc: "Processing is based on our legitimate interests."
        },
        legalBasisLabel: "Legal basis",
        legitimateInterests: "Legitimate interests (Art. 6 para. 1 lit. f GDPR)",
        contractFulfillment: "Contract fulfillment (Art. 6 para. 1 lit. b GDPR)",
        
        securityText: "We take technical and organizational measures to ensure an appropriate level of protection.",
        securityMeasures: "These measures include",
        securityList: {
          ssl: "SSL/TLS encryption for all data transfers",
          passwords: "Secure storage of passwords (hashed)",
          updates: "Regular security updates",
          access: "Access control and authorization management",
          rls: "Row Level Security (RLS) for data isolation between users"
        },
        
        deletionText1: "The data we process is deleted as soon as consent is revoked or other permissions expire.",
        deletionText2: "If data is not deleted because it is required for other legally permissible purposes, processing is restricted.",
        deletionText3: "When you delete your account, all associated data will be deleted within 30 days.",
        
        hostingText: "Our hosting provider processes the following data to provide this website:",
        azureText: "We use Microsoft Azure as our cloud platform. Servers are located in the EU (Region: West Europe).",
        provider: "Provider",
        privacyPolicy: "Privacy Policy",
        collectedData: "Data Collected",
        collectedDataList: {
          ip: "IP address",
          datetime: "Date and time of request",
          timezone: "Time zone difference from GMT",
          page: "Requested page",
          status: "HTTP status code",
          size: "Data volume transferred",
          browser: "Browser and operating system"
        },
        
        authText: "We use an external authentication service for registration and login.",
        authentication: "Authentication",
        logtoText: "We use Logto Cloud for secure authentication. Logto stores your login data encrypted.",
        registrationData: "Data collected during registration",
        registrationDataList: {
          email: "Email address",
          username: "Username (optional)",
          password: "Password (stored encrypted)",
          timestamp: "Registration timestamp"
        },
        accountDeletion: "Account Deletion",
        accountDeletionText: "You can delete your account at any time. Send an email to kontakt@planivio.de or use the delete function in account settings.",
        
        contactText: "When contacting us, user data is processed to handle the inquiry.",
        processedData: "Processed data",
        contactDataList: "Name, email address, message content",
        storageDuration: "Storage duration",
        storageDurationText: "Data is deleted once it is no longer needed for the purpose of collection.",
        
        cookies: {
          title: "Cookies",
          text: "We only use technically necessary cookies for authentication.",
          used: "Cookies Used",
          name: "Name",
          purpose: "Purpose",
          duration: "Duration",
          notUsed: "We do NOT use:",
          noTracking: "Tracking cookies",
          noMarketing: "Marketing cookies",
          noAnalytics: "Analytics cookies (no Google Analytics etc.)",
          noSocial: "Social media cookies",
          legalBasis: "Legitimate interests - technically required for application operation"
        },
        
        rightsText: "As a data subject, you have the following rights under GDPR:",
        rights: {
          access: "Right of Access (Art. 15 GDPR)",
          accessText: "You have the right to obtain information about your personal data stored with us.",
          rectification: "Right to Rectification (Art. 16 GDPR)",
          rectificationText: "You have the right to rectification of inaccurate or completion of incomplete data.",
          erasure: "Right to Erasure (Art. 17 GDPR)",
          erasureText: "You have the right to erasure of your data, unless legal retention obligations exist.",
          restriction: "Right to Restriction (Art. 18 GDPR)",
          restrictionText: "You have the right to request restriction of processing of your data.",
          portability: "Right to Data Portability (Art. 20 GDPR)",
          portabilityText: "You have the right to receive your data in a common format.",
          objection: "Right to Object (Art. 21 GDPR)",
          objectionText: "You have the right to object to the processing of your data.",
          complaint: "Right to Complain",
          complaintText: "You have the right to lodge a complaint with a supervisory authority:"
        },
        
        changes: {
          title: "Changes to this Privacy Policy",
          text: "We reserve the right to adapt this privacy policy. The current version is always available on this page."
        }
      },
      imprint: {
        title: "Legal Notice",
        germany: "Germany",
        email: "Email",
        lastUpdated: "Last updated"
      },
      terms: {
        title: "Terms and Conditions",
        lastUpdated: "Last updated",
        legalNote: "These Terms are written under German law. In case of translated versions, the German version prevails.",
        provider: "hereinafter \"Provider\"",
        
        s1: { title: "§ 1 Scope", p1: "These Terms and Conditions apply to the use of the Planivio platform, offered by:", p2: "The Terms apply to all users, regardless of whether they use a free or paid plan.", p3: "By registering, the user accepts these Terms.", p4: "Deviating conditions of the user are not recognized unless the Provider expressly agrees." },
        s2: { title: "§ 2 Contract Conclusion", p1: "The presentation of services on the website does not constitute a legally binding offer.", p2: "By registering, the user submits a binding offer to conclude a contract.", p3: "The contract is concluded upon confirmation of registration by email.", p4: "The contract text is stored by the Provider and can be requested by the user." },
        s3: { title: "§ 3 Service Description", p1: "Planivio is a web-based platform for household organization for families.", p2: "The platform includes the following modules:", modules: { shopping: "Shopping lists", recipes: "Recipe management", calendar: "Family calendar", budget: "Budget tracking", notes: "Notes", tasks: "Tasks", ai: "AI Assistant" }, p3: "The scope of functions may vary depending on the selected plan.", p4: "The Provider is entitled to further develop and adapt the services." },
        s4: { title: "§ 4 Beta Phase", p1: "During the beta phase, all features are free to use.", p2: "The Provider may end the beta phase at any time and introduce paid plans.", p3: "Beta users will be informed at least 30 days before paid plans are introduced.", p4: "There is no entitlement to continued free use after the beta phase." },
        s5: { title: "§ 5 Prices and Payment", p1: "Current prices are available on the website.", p2: "All prices include statutory VAT.", p3: "Payment is made in advance. The following payment methods are accepted:", payment: { card: "Credit card (Visa, Mastercard)", sepa: "SEPA direct debit", paypal: "PayPal" }, p4: "In case of payment default, access to the service may be restricted.", p5: "The Provider reserves the right to adjust prices. These will be announced at least 30 days in advance." },
        s6: { title: "§ 6 Contract Duration and Termination", p1: "The contract is concluded for an indefinite period.", p2: "Both parties may terminate the contract at any time with 30 days notice to the end of the month.", p3: "Termination can be made by email or via account settings.", p4: "The right to extraordinary termination for good cause remains unaffected." },
        s7: { title: "§ 7 Usage Rights and Obligations", p1: "The user receives a non-exclusive, non-transferable right to use the platform.", p2: "The user is responsible for the security of their access data.", p3: "The user is prohibited from:", duties: { illegal: "Using the platform for illegal purposes", commercial: "Using the platform commercially without permission", impair: "Impairing the operation of the platform", share: "Sharing access data with third parties", reverse: "Decompiling or reverse engineering the software" }, p4: "In case of violations, access may be suspended and the contract terminated." },
        s8: { title: "§ 8 Data Protection", p1: "The Provider processes personal data in accordance with GDPR.", p2a: "Details on data processing can be found in the", privacyLink: "Privacy Policy", p2b: ".", p3: "The user consents to the processing of their data within the scope of use." },
        s9: { title: "§ 9 Liability", p1: "The Provider is fully liable for damages arising from injury to life, body or health.", p2: "For other damages, the Provider is only liable for intent or gross negligence.", p3: "Liability for slight negligence is limited to foreseeable, contract-typical damages.", p4: "Liability under the Product Liability Act remains unaffected.", p5: "During the beta phase, no guarantee is given for availability or freedom from errors." },
        s10: { title: "§ 10 Warranty", p1: "The Provider guarantees the contractual provision of services.", p2: "Defects will be remedied within a reasonable time.", p3: "In case of significant defects, the user has the right to reduction or termination.", p4: "The warranty does not cover errors caused by improper use." },
        s11: { title: "§ 11 Changes to Terms", p1: "The Provider may change these Terms if objectively justified.", p2: "Changes will be communicated to the user by email.", p3: "If the user does not object within 30 days, the new Terms are deemed accepted.", p4: "The right to object and the consequences of silence will be pointed out in the change notification." },
        s12: { title: "§ 12 Final Provisions", p1: "German law applies, excluding the UN Convention on Contracts.", p2: "Place of jurisdiction is Nuremberg if the user is a merchant.", p3: "Should individual provisions be invalid, the validity of the remaining provisions remains unaffected.", p4: "The EU Commission provides a platform for online dispute resolution: https://ec.europa.eu/consumers/odr" }
      }
    },
    invite: {
      title: "You're Invited!",
      subtitle: { 
        try: "Try", 
        smartApp: "– the smart app for family organization.", 
        freeBeta: "Free now during beta!" 
      },
      orVisit: "Or visit directly",
      features: { 
        shopping: "Shopping Lists", 
        recipes: "Recipes", 
        calendar: "Calendar", 
        ai: "AI Assistant" 
      },
      freeDuringBeta: "Free during beta",
      buttons: { 
        copyLink: "Copy Link", 
        copied: "Copied!", 
        share: "Share", 
        print: "Print", 
        email: "Email" 
      },
      printTip: "Tip: Print this page and hang it on your fridge!",
      share: {
        title: "Planivio - Household Manager",
        text: "Try Planivio – the smart app for family organization!",
        whatsappText: "Hey! Check out Planivio – a great app for family organization:",
        telegramText: "Try Planivio – the smart app for family organization!",
        emailSubject: "Invitation to Planivio",
        emailBody: "Hey!\n\nI'm using Planivio for our family organization and wanted to recommend it to you. It's currently free in beta!\n\nCheck it out here:"
      }
    }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 🇫🇷 FRANÇAIS
  // ═══════════════════════════════════════════════════════════════════════════
  fr: {
    app: {
      name: "Planivio",
      tagline: "Votre gestionnaire de maison intelligent",
      loading: "Chargement de Planivio...",
    },

    nav: {
      modules: {
        dashboard: "Tableau de bord",
        calendar: "Calendrier",
        assistant: "Assistant",
        notes: "Notes",
        tasks: "Tâches",
        shopping: "Courses",
        recipes: "Recettes",
        household: "Foyer",
        budget: "Budget"
      }
    },

    header: {
      logout: "Déconnexion",
      myHousehold: "Mon foyer",
      user: "Utilisateur",
      settings: "Paramètres",
      profile: "Profil",
      login: "Connexion"
    },

    auth: {
      login: "Connexion",
      logout: "Déconnexion",
      register: "S'inscrire",
      getStarted: "Commencer",
      welcomeBack: "Bon retour"
    },

    mobile: {
      swipeHint: "Glissez (80px+) ou utilisez les flèches pour naviguer"
    },

    assistant: {
      title: "Assistant Planivio",
      subtitle: "Votre assistant domestique intelligent",
      thinking: "Réflexion en cours...",
      loading: "Chargement...",
      documents: "Documents",
      messages: "Messages",
      welcome: "Comment puis-je vous aider?",
      welcomeHint: "Écrivez un message ou téléchargez des documents",
      pleaseLogin: "Veuillez vous connecter",
      suggestions: "Suggestions",
      suggestion1: "Que puis-je cuisiner aujourd'hui?",
      suggestion2: "Créer une liste de courses",
      suggestion3: "Donnez-moi des conseils ménagers",
      placeholder: "Tapez un message...",
      send: "Envoyer",
      filesSelected: "fichier(s) sélectionné(s)",
      upload: "Télécharger",
      attachDocuments: "Télécharger des documents",
      dropDocuments: "Déposez les documents ici",
      documentsUploaded: "document(s) téléchargé(s)",
      uploadFailed: "Échec du téléchargement",
      clearChat: "Effacer le chat",
      clearDocuments: "Effacer les documents",
      ragDocuments: "Documents RAG",
      noResponse: "Aucune réponse reçue",
      error: "Une erreur s'est produite"
    },

    theme: { light: "Clair", dark: "Sombre", system: "Système", toggle: "Changer de thème", lightMode: "Activer le mode clair", darkMode: "Activer le mode sombre" },

    shopping: {
      title: "Listes de courses",
      newList: "Nouvelle liste",
      list: "Liste",
      lists: "Listes",
      fromRecipes: "des recettes",
      createList: "Créer une liste",
      editList: "Modifier la liste",
      deleteList: "Supprimer la liste",
      addItem: "Ajouter un article",
      editItem: "Modifier l'article",
      deleteItem: "Supprimer l'article",
      checkAll: "Tout cocher",
      uncheckAll: "Tout décocher",
      clearChecked: "Effacer cochés",
      form: { name: "Nom de la liste", namePlaceholder: "Entrer le nom...", description: "Description", descriptionPlaceholder: "Description optionnelle...", assignedTo: "Attribué à", dueDate: "Date d'échéance", itemName: "Nom de l'article", itemNamePlaceholder: "Entrer l'article...", quantity: "Quantité", unit: "Unité", category: "Catégorie", priority: "Priorité" },
      itemName: "Nom de l'article",
      quantity: "Quantité",
      unit: "Unité",
      category: "Catégorie",
      categories: { dairy: "Produits laitiers", bakery: "Boulangerie", produce: "Fruits & Légumes", meat: "Viande & Poisson", frozen: "Surgelés", beverages: "Boissons", household: "Ménage", other: "Autre" },
      units: { piece: "pièce", liter: "L", gram: "g", kilogram: "kg", pack: "paquet", bottle: "bouteille" },
      priority: { high: "Haute", normal: "Normale", medium: "Moyenne", low: "Basse" },
      status: { items: "articles", checked: "cochés", remaining: "restants" },
      emptyList: "Aucune liste disponible",
      noItems: "Aucun article dans cette liste",
      createFirstList: "Créez votre première liste de courses!",
      loading: "Chargement des listes...",
      tooManyAttempts: "Trop de tentatives. Veuillez recharger la page.",
      notLoggedIn: "Non connecté. Veuillez vous connecter.",
      notAuthorized: "Non autorisé. Veuillez vous reconnecter.",
      errorLoading: "Erreur lors du chargement",
      errorCreating: "Erreur lors de la création",
      errorUpdating: "Erreur lors de la mise à jour",
      errorDeleting: "Erreur lors de la suppression",
      deleteConfirm: "Vraiment supprimer la liste?",
      deleteItemConfirm: "Supprimer l'article?",
      weeklyPurchase: "Courses hebdomadaires",
      groceriesForWeek: "Provisions pour la semaine"
    },

    recipes: {
      title: "Recettes",
      recipesCount: "Recettes",
      createRecipe: "Créer une recette",
      newRecipe: "Nouvelle recette",
      editRecipe: "Modifier la recette",
      addToList: "Ajouter à la liste",
      toShoppingList: "Vers la liste de courses",
      searchRecipes: "Rechercher des recettes",
      viewMode: { cards: "Cartes", list: "Liste" },
      categories: { mainDish: "Plat principal", appetizer: "Entrée", dessert: "Dessert", snack: "En-cas", breakfast: "Petit-déjeuner" },
      difficulty: { easy: "Facile", medium: "Moyen", hard: "Difficile" },
      filters: { allCategories: "Toutes les catégories", allDifficulties: "Toutes les difficultés" },
      ingredients: "Ingrédients",
      ingredientsList: "Ingrédients",
      instructions: "Préparation",
      servings: "Portions",
      minutes: "min",
      prepTime: "Temps de préparation",
      cookTime: "Temps de cuisson",
      form: { name: "Nom", namePlaceholder: "Nom de la recette...", description: "Description", descriptionPlaceholder: "Courte description...", category: "Catégorie", difficulty: "Difficulté", servings: "Portions", prepTime: "Temps de préparation (min)", cookTime: "Temps de cuisson (min)", ingredients: "Ingrédients", addIngredient: "Ajouter un ingrédient", instructions: "Instructions", addStep: "Ajouter une étape" },
      noRecipes: "Aucune recette disponible",
      noRecipesYet: "Pas encore de recettes",
      noRecipesFound: "Aucune recette trouvée",
      createFirstRecipe: "Créez votre première recette!",
      tryOtherFilters: "Essayez d'autres filtres",
      loading: "Chargement des recettes...",
      tooManyAttempts: "Trop de tentatives. Veuillez recharger la page.",
      notLoggedIn: "Non connecté. Veuillez vous connecter.",
      howManyServings: "Pour combien de portions?",
      shoppingListCreated: "Liste de courses créée",
      errorCreating: "Erreur lors de la création",
      errorDeleting: "Erreur lors de la suppression",
      errorCreatingShoppingList: "Erreur lors de la création de la liste de courses",
      confirmDelete: "Supprimer la recette?"
    },

    calendar: {
      title: "Calendrier",
      appointments: "Rendez-vous",
      views: { day: "Jour", week: "Semaine", month: "Mois", agenda: "Agenda" },
      today: "Aujourd'hui",
      new: "Nouveau",
      export: "Exporter",
      sync: "Sync",
      enableNotifications: "Activer les rappels?",
      notificationHint: "Recevoir des notifications pour les rendez-vous à venir",
      activate: "Activer",
      later: "Plus tard",
      notificationsEnabled: "Notifications activées!",
      notificationsDenied: "Notifications refusées.",
      loading: "Chargement du calendrier...",
      pleaseLogin: "Veuillez vous connecter pour voir les rendez-vous",
      noAppointmentsToExport: "Aucun rendez-vous à exporter",
      swipeHint: "Glissez pour naviguer",
      errorLoading: "Erreur lors du chargement des rendez-vous",
      errorSaving: "Erreur lors de l'enregistrement",
      errorDeleting: "Erreur lors de la suppression",
      confirmDelete: "Vraiment supprimer le rendez-vous?",
      newEvent: "Nouvel événement",
      editEvent: "Modifier l'événement",
      eventTitle: "Titre",
      eventDate: "Date",
      eventTime: "Heure",
      eventDescription: "Description",
      eventLocation: "Lieu",
      allDay: "Toute la journée",
      reminder: "Rappel",
      recurring: "Récurrent",
      recurrence: { none: "Pas de répétition", daily: "Quotidien", weekly: "Hebdomadaire", monthly: "Mensuel", yearly: "Annuel" },
      noEvents: "Aucun événement"
    },

    budget: {
      title: "Budget & Finances",
      tabs: { overview: "Aperçu", transactions: "Transactions", categories: "Catégories", limits: "Limites" },
      income: "Revenus",
      expenses: "Dépenses",
      balance: "Solde",
      savingsRate: "Taux d'épargne",
      transaction: "Transaction",
      dateRange: { currentWeek: "Semaine actuelle", currentMonth: "Mois actuel", lastMonth: "Mois dernier", lastYear: "Année dernière", all: "Tout le temps" },
      addTransaction: "Transaction",
      addCategory: "Catégorie",
      addLimit: "Limite",
      noCategories: "Aucune catégorie pour le moment",
      setupCategoriesHint: "Créez 65+ catégories par défaut en un clic!",
      setupCategories: "Créer les catégories par défaut",
      categoriesCreated: "catégories par défaut créées!",
      noTransactionsInPeriod: "Aucune transaction dans la période sélectionnée",
      noLimits: "Aucune limite définie",
      confirmSetupCategories: "Voulez-vous créer 65+ catégories par défaut?",
      confirmDeleteCategory: "Supprimer la catégorie?",
      confirmDeleteTransaction: "Supprimer la transaction?",
      confirmDeleteLimit: "Supprimer la limite?",
      loading: "Chargement des données budget...",
      pleaseLogin: "Veuillez vous connecter...",
      description: "Description",
      amount: "Montant",
      date: "Date",
      type: "Type",
      category: "Catégorie",
      monthly: "Mensuel",
      yearly: "Annuel",
      types: { income: "Revenu", expense: "Dépense", all: "Tous" }
    },

    notes: {
      title: "Notes",
      notes: "Notes",
      newNote: "Nouvelle note",
      editNote: "Modifier la note",
      categories: { personal: "Personnel", work: "Travail", ideas: "Idées", important: "Important" },
      filters: { allCategories: "Toutes les catégories" },
      form: { title: "Titre", titlePlaceholder: "Entrer le titre...", content: "Contenu", contentPlaceholder: "Entrer une note...", category: "Catégorie" },
      noNotes: "Aucune note disponible",
      noResults: "Aucun résultat trouvé",
      loading: "Chargement des notes...",
      errorLoading: "Erreur de chargement",
      confirmDeleteNote: "Supprimer la note?",
      viewMode: { grid: "Grille", list: "Liste" }
    },

    tasks: {
      title: "Tâches",
      tasks: "Tâches",
      newTask: "Nouvelle tâche",
      editTask: "Modifier la tâche",
      categories: { personal: "Personnel", work: "Travail", household: "Ménage", shopping: "Courses" },
      priority: { low: "Basse", medium: "Moyenne", high: "Haute" },
      status: { open: "Ouverte", done: "Terminée", overdue: "En retard", all: "Toutes" },
      filters: { allCategories: "Toutes les catégories", allPriorities: "Toutes les priorités", allStatus: "Tous les status" },
      form: { title: "Titre", titlePlaceholder: "Entrer la tâche...", description: "Description", descriptionPlaceholder: "Détails de la tâche...", category: "Catégorie", priority: "Priorité", dueDate: "Date d'échéance", dueTime: "Heure", assignedTo: "Attribué à" },
      noTasks: "Aucune tâche disponible",
      noResults: "Aucun résultat trouvé",
      loading: "Chargement des tâches...",
      errorLoading: "Erreur de chargement",
      confirmDeleteTask: "Supprimer la tâche?",
      viewMode: { grid: "Grille", list: "Liste" },
      stats: { total: "Total", open: "Ouvertes", completed: "Terminées", highPriority: "Haute priorité", today: "Aujourd'hui" }
    },

    household: {
      title: "Gestion du foyer",
      members: "Membres",
      tabs: { members: "Membres", statistics: "Statistiques" },
      inviteMember: "Inviter un membre",
      newMember: "Nouveau membre",
      addMember: "Ajouter un membre",
      remove: "Supprimer",
      settings: "Paramètres",
      roles: { admin: "Administrateur", member: "Membre", child: "Enfant", guest: "Invité" },
      form: { name: "Nom", namePlaceholder: "Entrer le nom...", role: "Rôle", avatar: "Avatar" },
      statistics: "Statistiques du foyer",
      stats: { shoppingLists: "Listes de courses", notes: "Notes", tasks: "Tâches", recipes: "Recettes", appointments: "Rendez-vous" },
      loading: "Chargement du foyer...",
      noMembers: "Aucun membre pour le moment",
      errorLoading: "Erreur de chargement",
      notLoggedIn: "Non connecté",
      confirmRemoveMember: "Supprimer le membre?"
    },

    dashboard: {
      title: "Tableau de bord",
      welcome: "Bienvenue",
      welcomeBack: "Bienvenue, {{name}}!",
      subtitle: "Voici l'aperçu de votre foyer",
      open: "ouverts",
      today: "Aujourd'hui",
      tomorrow: "Demain",
      balance: "Solde",
      current: "Actuel",
      widgets: { shopping: "Liste de courses", tasks: "Tâches", appointments: "Rendez-vous", budget: "Budget" },
      shoppingList: "Liste de courses",
      task: "Tâche",
      appointment: "Rendez-vous",
      allDay: "Toute la journée",
      of: "sur",
      viewAll: "Toutes les listes",
      viewAllTasks: "Toutes les tâches",
      viewAllAppointments: "Tous les rendez-vous",
      manageBudget: "Gérer le budget",
      createBudget: "Créer un budget",
      noLists: "Aucune liste",
      noOpenTasks: "Aucune tâche ouverte",
      noAppointments: "Aucun rendez-vous",
      noBudget: "Pas de budget",
      quickAccess: "Accès rapide",
      quickActions: "Actions rapides",
      recentActivity: "Activité récente",
      loading: "Chargement du tableau de bord...",
      retry: "Réessayer",
      noToken: "Aucun jeton reçu",
      stats: { openTasks: "Tâches ouvertes", shoppingItems: "Articles à acheter", upcomingEvents: "Événements à venir", monthlyBudget: "Budget mensuel" }
    },

    common: {
      save: "Enregistrer",
      cancel: "Annuler",
      delete: "Supprimer",
      edit: "Modifier",
      add: "Ajouter",
      close: "Fermer",
      confirm: "Confirmer",
      back: "Retour",
      next: "Suivant",
      search: "Rechercher",
      filter: "Filtrer",
      sort: "Trier",
      loading: "Chargement...",
      error: "Erreur",
      success: "Succès",
      warning: "Attention",
      info: "Info",
      yes: "Oui",
      no: "Non",
      all: "Tout",
      none: "Aucun",
      required: "Obligatoire",
      optional: "Optionnel"
    },

    pages: {
      landing: { hero: { title: "Votre foyer. Parfaitement organisé.", subtitle: "Planivio réunit listes de courses, recettes, calendrier et budget dans une seule application." }, cta: "Commencer gratuitement" },
      features: { 
        title: "Fonctionnalités",
        nav: {
          backHome: "Retour à l'accueil",
          startNow: "Commencer"
        },
        hero: {
          title: "Toutes les fonctionnalités en un coup d'œil",
          subtitle: "Découvrez comment Planivio simplifie le quotidien de votre famille.",
          allAvailable: "Tous les modules déjà disponibles"
        },
        badge: {
          allAvailable: "Tous les modules disponibles"
        },
        screenshot: {
          comingSoon: "Capture d'écran à venir"
        },
        useCases: {
          title: "Cas d'utilisation"
        },
        intro: {
          problem: {
            title: "Ça vous dit quelque chose ?",
            text: "Rendez-vous dans la tête, liste de courses sur papier, recettes dans le navigateur, budget dans Excel, to-dos dans le chat. Vous jonglez avec 5-7 apps chaque jour – et quelqu'un oublie quand même le lait. C'est la charge mentale. Et dans 71% des foyers, une seule personne porte ce fardeau invisible."
          },
          solution: {
            title: "PLANIVIO change la donne.",
            text: "Une plateforme. Tout connecté. L'IA reconnaît les liens automatiquement : Anniversaire au calendrier → recette de gâteau → ingrédients sur la liste → meilleure offre trouvée. Moins de stress. Plus de temps. Pour ce qui compte vraiment."
          },
          stats: {
            mentalLoad: "des foyers portent le fardeau seuls",
            apps: "apps que vous jonglez chaque jour",
            households: "foyers en Allemagne"
          },
          flow: {
            calendar: "Calendrier",
            recipes: "Recette",
            shopping: "Courses",
            budget: "Offre"
          },
          trust: {
            euHosting: "Hébergement UE",
            dsgvo: "Conforme RGPD",
            madeInGermany: "Made in Germany"
          }
        },
        cta: {
          title: "Prêt pour une meilleure organisation ?",
          subtitle: "Commencez gratuitement et découvrez toutes les fonctionnalités.",
          feedbackHelps: "Vos commentaires nous aident à améliorer Planivio !",
          startNow: "Commencer gratuitement"
        },
        modules: {
          shopping: {
            title: "Listes de courses",
            tagline: "Ne plus jamais rien oublier",
            desc: "Créez et partagez des listes de courses avec votre famille. Les articles sont automatiquement catégorisés et synchronisés.",
            features: {
              categorization: "Catégorisation automatique par rayons",
              multipleLists: "Plusieurs listes pour différents magasins",
              sync: "Synchronisation en temps réel avec tous les membres de la famille",
              offline: "Mode hors ligne pour faire les courses",
              recurring: "Ajouter automatiquement les articles récurrents"
            },
            useCases: {
              family: "Toute la famille peut ajouter des articles - Papa voit immédiatement ce que Maman a ajouté",
              mobile: "Cochez les articles dans le magasin - même sans internet"
            }
          },
          recipes: {
            title: "Recettes",
            tagline: "Vos recettes préférées en un seul endroit",
            desc: "Enregistrez les recettes familiales, importez depuis le web et ajoutez les ingrédients directement à votre liste de courses.",
            features: {
              create: "Créer et modifier vos propres recettes",
              toShopping: "Ajouter les ingrédients à la liste de courses en un clic",
              categories: "Catégories et tags pour une recherche rapide",
              portions: "Ajuster les portions - les quantités sont recalculées",
              share: "Partager les recettes avec la famille et les amis"
            },
            useCases: {
              grandma: "Conserver la recette secrète de grand-mère numériquement et ne jamais la perdre",
              shopping: "Rôti du dimanche prévu ? Tous les ingrédients automatiquement sur la liste de courses"
            }
          },
          ai: {
            title: "Assistant IA",
            tagline: "Votre aide ménager intelligent",
            desc: "Demandez à l'assistant IA des idées de recettes, une planification des repas ou des conseils ménagers.",
            features: {
              suggestions: "Suggestions de recettes basées sur les ingrédients disponibles",
              mealPlanning: "Planification automatique des repas de la semaine",
              tips: "Conseils ménagers personnalisés",
              optimization: "Optimisation de la liste de courses selon le plan du magasin",
              natural: "Saisie en langage naturel - demandez simplement !"
            },
            useCases: {
              ingredients: "\"Que puis-je cuisiner avec des pommes de terre, du fromage et du jambon ?\" - obtenez des idées instantanément",
              planning: "\"Planifie des repas sains pour la semaine\" - plan de repas terminé"
            }
          },
          calendar: {
            title: "Calendrier",
            tagline: "Famille en vue",
            desc: "Un calendrier partagé pour tous les rendez-vous, activités et rappels de la famille.",
            features: {
              shared: "Calendrier familial partagé",
              colorCoding: "Code couleur par membre de la famille",
              reminders: "Rappels par notification push",
              recurring: "Événements récurrents (collecte des ordures, club de sport...)",
              views: "Vues jour, semaine et mois"
            },
            useCases: {
              appointments: "Rendez-vous médicaux, réunions de parents, entraînements sportifs - tout en un coup d'œil",
              birthdays: "Ne plus jamais oublier un anniversaire"
            }
          },
          budget: {
            title: "Budget",
            tagline: "Finances sous contrôle",
            desc: "Gardez une trace des dépenses du ménage et épargnez spécifiquement pour des objectifs communs.",
            features: {
              categorize: "Enregistrer les dépenses par catégorie",
              budgets: "Définir et surveiller les budgets mensuels",
              charts: "Graphiques et statistiques clairs",
              goals: "Définir et suivre les objectifs d'épargne",
              stats: "Comparaison avec les mois précédents"
            },
            useCases: {
              overview: "Enfin savoir où va l'argent chaque mois",
              vacation: "Épargner ensemble pour les vacances d'été"
            }
          },
          notes: {
            title: "Notes",
            tagline: "Garder une trace des choses importantes",
            desc: "Notes partagées pour tout ce que la famille devrait savoir.",
            features: {
              shared: "Notes partagées pour tout le monde",
              folders: "Dossiers pour une meilleure organisation",
              search: "Recherche plein texte dans toutes les notes",
              attachments: "Joindre des images et des fichiers",
              pin: "Épingler les notes importantes"
            },
            useCases: {
              wifi: "Mot de passe WiFi, codes de porte, numéros importants - tout à portée de main",
              contacts: "Coordonnées de la baby-sitter, de l'artisan, des voisins"
            }
          },
          tasks: {
            title: "Tâches",
            tagline: "Organiser le ménage",
            desc: "Répartissez équitablement les tâches ménagères et gardez une trace de qui fait quoi.",
            features: {
              create: "Créer et attribuer des tâches",
              recurring: "Tâches récurrentes (hebdomadaires, mensuelles)",
              assign: "Attribuer des tâches aux membres de la famille",
              reminders: "Rappels avant l'échéance",
              progress: "Voir les progrès et les statistiques"
            },
            useCases: {
              trash: "\"Sortir les poubelles\" - automatiquement pour Papa chaque mardi",
              kids: "Les enfants apprennent la responsabilité avec leurs propres petites tâches"
            }
          }
        }
      },
      impressum: { title: "Mentions légales" },
      datenschutz: { title: "Confidentialité" },
      agb: { title: "CGU" },
      faq: { title: "FAQ" }
    },

    feedback: { title: "Retour", placeholder: "Votre retour...", submit: "Envoyer", thanks: "Merci pour votre retour!", types: { bug: "Signaler un bug", feature: "Demande de fonctionnalité", general: "Retour général" } },
    cookies: { message: "Nous utilisons des cookies pour améliorer votre expérience.", accept: "Accepter", decline: "Refuser", settings: "Paramètres" },
    email: { verification: { title: "Vérifier l'email", message: "Veuillez vérifier votre adresse email", resend: "Renvoyer" } },
    language: { select: "Langue", de: "Deutsch", en: "English", fr: "Français" },
    time: { today: "Aujourd'hui", yesterday: "Hier", tomorrow: "Demain", daysAgo: "il y a {{count}} jours", hoursAgo: "il y a {{count}} heures", minutesAgo: "il y a {{count}} minutes", justNow: "À l'instant" },

    // ✅ v4: LANDING PAGE UPDATED
    landing: {
      betaBanner: { text: "Nous développons activement PLANIVIO – vos retours nous aident !" },
      nav: { features: "Fonctionnalités", priceCompare: "Comparaison de prix", privacy: "Confidentialité", pricing: "Tarifs", login: "Vers l'app", experience: "Découvrez PLANIVIO" },
      hero: { titlePart1: "Fini le", titleGradient: "chaos dans la tête.", betaBadge: "Bêta – Essayez gratuitement maintenant", title: "Gestion de maison pour toute la famille", lead: "Listes de courses sur papier. Rendez-vous en tête. Recettes dans trois applis différentes.", subtitle: "PLANIVIO rassemble tout en un seul endroit – pour plus de temps en famille.", ctaStart: "Tester la bêta gratuitement", ctaFeatures: "Comment ça marche" },
      stats: { apps: "applis qu'une famille utilise pour le ménage & l'organisation", oneApp: "appli pour tout – c'est PLANIVIO", priceDiff: "différences de prix entre supermarchés possibles*", euServer: "Serveurs en Allemagne, 100% conforme RGPD", source: "* Centre de consommateurs NRW, Étude de marché 2023" },
      priceCompare: {
        comingSoon: "Bientôt disponible",
        title: "Trouvez le supermarché le moins cher",
        subtitle: "Comparer les prix vaut le coup – les différences entre supermarchés sont souvent plus grandes qu'on ne le pense.",
        cheapest: "Le moins cher",
        disclaimer: "* Représentation exemplaire de courses hebdomadaires (4 personnes). Les prix réels varient selon le produit, la région et le moment.",
        sourceText: "Le potentiel d'économies est réel : une étude du centre de consommateurs NRW a montré des différences de prix de plus de 100% pour 15 produits sur 20.",
        sourceLabel: "Source : Centre de consommateurs NRW, Étude de marché mai 2023"
      },
      comparison: {
        label: "Avant vs. Après",
        title: "Votre quotidien devient si simple",
        subtitle: "Avec PLANIVIO, toute la famille a tout en vue – d'un seul coup d'œil."
      },
      benefitsLabel: "Avantages",
      benefits: {
        title: "Ce que vous gagnez vraiment",
        subtitle: "Il ne s'agit pas d'organisation parfaite. Il s'agit de plus de temps pour votre famille.",
        peace: { title: "Esprit tranquille", text: "Plus de \"Ai-je pensé à tout ?\". PLANIVIO se souvient pour vous." },
        save: { title: "Économiser de l'argent", text: "Un aperçu des dépenses aide à acheter plus consciemment." },
        time: { title: "Gagner du temps", text: "Moins de recherche, moins de coordination, plus de temps libre." },
        family: { title: "Vrai temps en famille", text: "Moins de stress organisationnel. Plus de soirées jeux et sorties." },
        stress: { title: "Moins de disputes", text: "Plus de discussions sur les courses oubliées ou les rendez-vous manqués." },
        focus: { title: "Fierté de vous", text: "Le sentiment d'avoir tout sous contrôle – sans que ce soit un travail." }
      },
      trust: { germany: "Développé en Allemagne", eu: "Serveurs en Allemagne", dsgvo: "100% conforme RGPD", noCreditCard: "Pas de carte de crédit" },
      scenarios: {
        title: "Votre quotidien devient si simple",
        subtitle: "Avec PLANIVIO, toute la famille a tout en vue – d'un seul coup d'œil.",
        before: "AVANT",
        after: "AVEC PLANIVIO",
        birthday: { title: "Courses", before: "\"Le lait est sur la liste ?\" – \"Quelle liste ?\" – \"Celle sur le frigo !\" – \"Il n'y en a pas !\"", after: "Une liste de courses partagée. Chacun voit en temps réel ce qui manque." },
        shopping: { title: "Cuisine", before: "\"Qu'est-ce qu'on mange ?\" – Chaque jour la même question. Pas d'idées.", after: "Recettes sauvegardées, menu de la semaine. Ingrédients en un clic sur la liste." },
        guests: { title: "Calendrier", before: "\"C'était quand la réunion parents-profs ?\" – Chacun son propre calendrier.", after: "Un calendrier familial. Tout le monde voit tous les rendez-vous." }
      },
      privacy: {
        title: "Vos données restent en Allemagne.",
        text: "PLANIVIO est développé en Allemagne et hébergé sur des serveurs allemands. Aucune transmission de données à des tiers. Pas de publicité.",
        highlight: "Vos recettes, listes de courses et rendez-vous vous appartiennent.",
        promise: "Notre promesse de confidentialité",
        euServers: "Serveurs en Allemagne",
        noThirdParty: "Aucune transmission à des tiers – jamais",
        noAds: "Pas d'utilisation de vos données pour la publicité",
        encryption: "Chiffrement standard bancaire (AES-256)",
        deletable: "Suppression complète possible à tout moment",
        transparent: "Politique de confidentialité transparente",
        noTracking: "Pas de tracking",
        deleteAnytime: "Supprimable à tout moment",
        exportData: "Données exportables",
        learnMore: "En savoir plus sur la confidentialité"
      },
      betaInvite: {
        badge: "Places limitées",
        title: "Rejoignez les premiers utilisateurs",
        text: "PLANIVIO est nouveau – nous cherchons des familles pour améliorer l'app.",
        perk1: "Accès gratuit",
        perk1text: "Les testeurs bêta ont accès gratuit à toutes les fonctionnalités.",
        perk2: "Vos retours comptent",
        perk2text: "Aidez-nous à construire PLANIVIO exactement comme vous en avez besoin.",
        perk3: "Accès anticipé",
        perk3text: "Soyez là avant tout le monde.",
        cta: "Commencer gratuitement"
      },
      features: { 
        subtitle: "Tout ce dont votre foyer a besoin", 
        title: "Modules pour la vie familiale complète", 
        viewAll: "toutes les fonctionnalités en détail", 
        calendar: { title: "Calendrier", desc: "Gardez un œil sur tous les rendez-vous" }, 
        shopping: { title: "Listes de courses", desc: "Listes intelligentes avec catégories" }, 
        recipes: { title: "Recettes", desc: "Enregistrer & partager des recettes" }, 
        tasks: { title: "Tâches", desc: "Organiser et attribuer les tâches ménagères" },
        budget: { title: "Budget", desc: "Finances sous contrôle" }, 
        notes: { title: "Notes", desc: "Documentation centrale" }, 
        assistant: { title: "Assistant IA", desc: "Aide intelligente" } 
      },
      pricing: {
        subtitle: "Tarifs",
        title: "Pendant la Beta, Planivio est entièrement gratuit!",
        availableNow: "Disponible maintenant",
        betaAdvantage: "Avantage Beta Testeur",
        betaDiscount: "Rejoignez maintenant et obtenez 50% de réduction sur tous les plans pendant 12 mois après la beta!",
        beta: {
          name: "Beta",
          period: "Gratuit",
          cta: "Commencer gratuitement",
          features: {
            allModules: "Tous les modules disponibles",
            fiveMembers: "Jusqu'à 5 membres de famille",
            aiIncluded: "Assistant IA inclus",
            emailSupport: "Support par email",
            feedbackInfluence: "Influence sur les fonctionnalités"
          }
        },
        pro: {
          name: "Pro",
          period: "EUR/mois",
          periodSoon: "Bientôt",
          cta: "Bientôt disponible",
          features: {
            extended: "Fonctionnalités étendues",
            twentyFiveMembers: "Jusqu'à 25 membres de famille",
            prioritySupport: "Support prioritaire",
            extendedAI: "Fonctionnalités IA étendues",
            priceComparison: "Comparaison prix supermarchés"
          }
        },
        family: {
          name: "Famille+",
          period: "EUR/mois",
          periodSoon: "Bientôt",
          cta: "Bientôt disponible",
          features: {
            allPro: "Toutes les fonctionnalités Pro",
            unlimitedMembers: "Membres illimités",
            multiHousehold: "Support multi-foyers",
            apiAccess: "Accès API",
            premiumSupport: "Support premium"
          }
        }
      },
      betaPerks: {
        free: { title: "Gratuit", desc: "Entièrement gratuit pendant la beta" },
        feedback: { title: "Votre avis compte", desc: "Façonnez les fonctionnalités" },
        earlyAdopter: { title: "Avantages early adopter", desc: "Conditions spéciales après la beta" }
      },
      cta: {
        title: "Prêt à organiser votre foyer?",
        subtitle: "Commencez gratuitement et rejoignez la communauté Planivio.",
        startButton: "Commencer gratuitement",
        inviteButton: "Inviter des amis",
        inviteHint: "Partagez Planivio avec famille & amis – QR code imprimable!"
      },
      footer: { rights: "Tous droits réservés.", privacy: "Confidentialité", terms: "CGU", imprint: "Mentions légales", contact: "Contact", madeIn: "Fait avec ❤️ en Bavière" }
    },

    faq: { 
      title: "Questions Fréquentes", 
      subtitle: "Trouvez les réponses aux questions les plus courantes sur Planivio", 
      searchPlaceholder: "Rechercher des questions...", 
      noResults: "Aucun résultat trouvé",
      tryOther: "Essayez d'autres termes de recherche ou sélectionnez une autre catégorie.",
      contactTitle: "Question non listée ?",
      contactSubtitle: "Contactez-nous directement - nous sommes là pour vous aider !",
      categories: { 
        all: "Tout", 
        general: "Général", 
        beta: "Bêta", 
        features: "Fonctionnalités", 
        privacy: "Confidentialité", 
        technical: "Technique" 
      },
      // ===== GÉNÉRAL =====
      general: {
        whatIsHomeM: {
          q: "Qu'est-ce que Planivio ?",
          a: "Planivio est une plateforme moderne de gestion de foyer pour les familles. Avec Planivio, vous pouvez gérer les listes de courses, les recettes, les rendez-vous du calendrier, le budget et bien plus encore en un seul endroit. Tous les membres de la famille peuvent collaborer en temps réel."
        },
        forWhom: {
          q: "À qui s'adresse Planivio ?",
          a: "Planivio s'adresse aux familles et aux foyers qui souhaitent améliorer leur organisation quotidienne. Que ce soit des couples, des familles avec enfants ou des colocations - Planivio vous aide à rester organisé et à coordonner les tâches."
        },
        isFree: {
          q: "Planivio est-il gratuit ?",
          a: "Pendant la phase bêta, Planivio est entièrement gratuit ! Vous avez accès à toutes les fonctionnalités disponibles sans restrictions. Après la bêta, différents plans tarifaires seront proposés, les testeurs bêta bénéficiant de 50% de réduction pendant 12 mois."
        },
        devices: {
          q: "Sur quels appareils puis-je utiliser Planivio ?",
          a: "Planivio fonctionne sur tous les appareils avec un navigateur web moderne : ordinateurs (Windows, Mac, Linux), tablettes (iPad, Android) et smartphones (iPhone, Android). Vous pouvez également enregistrer Planivio comme une application sur votre écran d'accueil."
        }
      },
      // ===== BÊTA =====
      beta: {
        whatIsBeta: {
          q: "Que signifie 'Bêta' ?",
          a: "Bêta signifie que Planivio est encore en développement. Vous obtenez un accès anticipé aux nouvelles fonctionnalités, mais il peut encore y avoir des bugs mineurs. Vos retours nous aident à améliorer Planivio !"
        },
        howLong: {
          q: "Combien de temps durera la phase bêta ?",
          a: "La phase bêta est prévue pour 2026. La date de fin exacte dépend de la rapidité avec laquelle nous pouvons compléter et stabiliser toutes les fonctionnalités prévues. Vous serez informé à l'avance."
        },
        benefits: {
          q: "Quels avantages ai-je en tant que testeur bêta ?",
          a: "En tant que testeur bêta, vous recevez : Utilisation gratuite pendant toute la phase bêta, 50% de réduction sur tous les plans pendant 12 mois après la bêta, influence directe sur les nouvelles fonctionnalités grâce à vos retours, et statut d'early adopter avec des avantages spéciaux."
        },
        feedback: {
          q: "Puis-je donner mon avis ?",
          a: "Oui, absolument ! Utilisez le bouton de feedback (💬) en bas à droite de chaque page. Vous pouvez signaler des bugs, faire des demandes de fonctionnalités ou donner un avis général. Chaque retour nous aide à améliorer Planivio."
        },
        dataAfter: {
          q: "Qu'advient-il de mes données après la bêta ?",
          a: "Vos données sont conservées ! Si vous choisissez un plan payant après la bêta, toutes vos listes de courses, recettes, entrées de calendrier etc. seront transférées. Rien n'est perdu."
        }
      },
      // ===== FONCTIONNALITÉS =====
      features: {
        modules: {
          q: "Quels modules Planivio propose-t-il ?",
          a: "Planivio propose actuellement : 🛒 Listes de courses (avec catégories), 🍳 Recettes (enregistrer & partager), 📅 Calendrier familial, 💰 Suivi du budget, 📝 Notes, ✅ Tâches et 🤖 Assistant IA. D'autres modules sont prévus."
        },
        aiAssistant: {
          q: "Comment fonctionne l'assistant IA ?",
          a: "L'assistant IA vous aide avec les questions quotidiennes : 'Que puis-je cuisiner avec des pommes de terre et du poulet ?', 'Planifie le dîner pour 4 personnes', ou 'Quelles courses devrais-je racheter ?'. Il apprend vos préférences et donne des conseils personnalisés."
        },
        multiUser: {
          q: "Plusieurs personnes peuvent-elles l'utiliser simultanément ?",
          a: "Oui ! Planivio est fait pour les familles. Jusqu'à 5 membres de la famille peuvent accéder à toutes les données simultanément. Les modifications sont synchronisées en temps réel - quand maman ajoute quelque chose à la liste de courses, papa le voit immédiatement sur son téléphone."
        },
        recipeToShopping: {
          q: "Puis-je ajouter les ingrédients des recettes directement à la liste de courses ?",
          a: "Oui, c'est l'une de nos fonctionnalités les plus populaires ! Chaque recette a un bouton 'Ajouter à la liste de courses'. Les ingrédients sont automatiquement transférés avec les bonnes quantités - vous pouvez ajuster les portions au préalable."
        },
        offline: {
          q: "Planivio fonctionne-t-il hors ligne ?",
          a: "Actuellement, Planivio nécessite une connexion internet. Nous travaillons sur un mode hors ligne pour l'avenir afin que vous puissiez accéder à votre liste de courses même sans internet."
        }
      },
      // ===== CONFIDENTIALITÉ =====
      privacy: {
        dataStorage: {
          q: "Où mes données sont-elles stockées ?",
          a: "Toutes les données sont stockées sur des serveurs dans l'UE (Microsoft Azure, région Europe de l'Ouest). Nous respectons strictement le RGPD et les lois allemandes sur la protection des données. Vos données ne quittent jamais l'UE."
        },
        dataSold: {
          q: "Mes données sont-elles vendues ou utilisées pour la publicité ?",
          a: "Non, jamais ! Nous ne vendons pas de données à des tiers et n'affichons pas de publicité. Planivio est financé exclusivement par les abonnements. Vos données vous appartiennent."
        },
        cookies: {
          q: "Quels cookies Planivio utilise-t-il ?",
          a: "Nous n'utilisons que des cookies techniquement nécessaires pour la connexion (cookie de session). Pas de cookies de suivi, pas de cookies publicitaires, pas d'analytics tiers."
        },
        export: {
          q: "Puis-je exporter mes données ?",
          a: "Oui ! Vous pouvez demander une exportation de toutes vos données à tout moment (droit à la portabilité des données selon le RGPD). Contactez-nous à kontakt@planivio.de."
        },
        deleteAccount: {
          q: "Comment puis-je supprimer mon compte ?",
          a: "Vous pouvez supprimer votre compte à tout moment dans les paramètres ou nous envoyer un email à kontakt@planivio.de. Toutes vos données seront alors supprimées de manière irrévocable dans les 30 jours."
        }
      },
      // ===== TECHNIQUE =====
      technical: {
        browsers: {
          q: "Quels navigateurs sont pris en charge ?",
          a: "Planivio fonctionne avec tous les navigateurs modernes : Chrome, Firefox, Safari, Edge (versions actuelles). Internet Explorer n'est pas pris en charge."
        },
        installMobile: {
          q: "Comment installer Planivio sur mon téléphone ?",
          a: "Ouvrez Planivio dans votre navigateur, appuyez sur 'Partager' (iOS) ou le menu (Android) et sélectionnez 'Ajouter à l'écran d'accueil'. Planivio apparaîtra alors comme une application sur votre écran d'accueil."
        },
        forgotPassword: {
          q: "J'ai oublié mon mot de passe. Que faire ?",
          a: "Cliquez sur 'Mot de passe oublié' sur la page de connexion. Vous recevrez un email avec un lien pour réinitialiser votre mot de passe. Le lien est valide pendant 24 heures."
        },
        notLoading: {
          q: "L'application ne se charge pas. Que puis-je faire ?",
          a: "Essayez ces étapes : 1) Recharger la page (F5 ou tirer pour actualiser), 2) Vider le cache du navigateur, 3) Essayer un autre navigateur, 4) Vérifier votre connexion internet. Si rien ne fonctionne, contactez-nous via le formulaire de feedback."
        },
        nativeApp: {
          q: "Existe-t-il une application native pour iOS/Android ?",
          a: "Pas actuellement - Planivio est une application web qui fonctionne sur tous les appareils. Des applications natives pour iOS et Android sont prévues pour l'avenir. Cependant, vous pouvez déjà enregistrer l'application web sur votre écran d'accueil !"
        }
      }
    },
    features: {
      hero: { title: "Que peut faire Planivio?", subtitle: "Découvrez tous les modules qui facilitent votre vie de famille.", allAvailable: "Toutes les fonctionnalités disponibles" },
      badge: { allAvailable: "Toutes les fonctionnalités disponibles" },
      nav: { backHome: "Retour à l'accueil", startNow: "Commencer gratuitement" },
      screenshot: { comingSoon: "Capture d'écran à venir" },
      useCases: { title: "Cas d'utilisation" },
      cta: { title: "Ça vous dit?", subtitle: "Commencez gratuitement et essayez toutes les fonctionnalités.", feedbackHelps: "Vos commentaires nous aident à améliorer Planivio!", startFree: "Commencer gratuitement", startNow: "Commencer gratuitement" },
      modules: {
        // ✅ NOUVEAU: Module Dashboard
        dashboard: {
          title: "Tableau de bord",
          tagline: "Votre centre de commande",
          desc: "Le tableau de bord est votre hub central. Voyez d'un coup d'œil tous les rendez-vous, tâches et courses importants de la journée.",
          features: {
            overview: "Aperçu quotidien avec les rendez-vous et tâches à venir",
            quickActions: "Accès en un clic aux actions fréquentes",
            widgets: "Aperçu compact de tous les modules",
            stats: "Cartes de statistiques avec chiffres rapides",
            greeting: "Salutation personnalisée"
          },
          useCases: {
            morning: "Commencez chaque matin avec un aperçu clair de votre journée",
            quickAccess: "Accès rapide à tout ce qui est important sans chercher"
          }
        },
        shopping: {
          title: "Listes de courses",
          tagline: "Ne plus jamais rien oublier",
          desc: "Créez et partagez des listes de courses avec votre famille. Les articles sont automatiquement catégorisés et synchronisés.",
          features: {
            categorization: "Catégorisation automatique par rayons",
            multipleLists: "Plusieurs listes pour différents magasins",
            sync: "Synchronisation en temps réel avec tous les membres de la famille",
            offline: "Mode hors ligne pour faire les courses",
            recurring: "Ajouter automatiquement les articles récurrents"
          },
          useCases: {
            family: "Toute la famille peut ajouter des articles - Papa voit immédiatement ce que Maman a ajouté",
            mobile: "Cochez les articles dans le magasin - même sans internet"
          }
        },
        recipes: {
          title: "Recettes",
          tagline: "Vos recettes préférées en un seul endroit",
          desc: "Enregistrez les recettes familiales, importez depuis le web et ajoutez les ingrédients directement à votre liste de courses.",
          features: {
            create: "Créer et modifier vos propres recettes",
            toShopping: "Ajouter les ingrédients à la liste de courses en un clic",
            categories: "Catégories et tags pour une recherche rapide",
            portions: "Ajuster les portions - les quantités sont recalculées",
            share: "Partager les recettes avec la famille et les amis"
          },
          useCases: {
            grandma: "Conserver la recette secrète de grand-mère numériquement et ne jamais la perdre",
            shopping: "Rôti du dimanche prévu? Tous les ingrédients automatiquement sur la liste de courses"
          }
        },
        ai: {
          title: "Assistant IA",
          tagline: "Votre aide ménager intelligent",
          desc: "Demandez à l'assistant IA des idées de recettes, une planification des repas ou des conseils ménagers.",
          features: {
            suggestions: "Suggestions de recettes basées sur les ingrédients disponibles",
            mealPlanning: "Planification automatique des repas de la semaine",
            tips: "Conseils ménagers personnalisés",
            optimization: "Optimisation de la liste de courses selon le plan du magasin",
            natural: "Saisie en langage naturel - demandez simplement!"
          },
          useCases: {
            ingredients: "\"Que puis-je cuisiner avec des pommes de terre, du fromage et du jambon?\" - obtenez des idées instantanément",
            planning: "\"Planifie des repas sains pour la semaine\" - plan de repas terminé"
          }
        },
        calendar: {
          title: "Calendrier",
          tagline: "Famille en vue",
          desc: "Un calendrier partagé pour tous les rendez-vous, activités et rappels de la famille.",
          features: {
            shared: "Calendrier familial partagé",
            colorCoding: "Code couleur par membre de la famille",
            reminders: "Rappels par notification push",
            recurring: "Événements récurrents (collecte des ordures, club de sport...)",
            views: "Vues jour, semaine et mois"
          },
          useCases: {
            appointments: "Rendez-vous médicaux, réunions de parents, entraînements sportifs - tout en un coup d'œil",
            birthdays: "Ne plus jamais oublier un anniversaire"
          }
        },
        budget: {
          title: "Budget",
          tagline: "Finances sous contrôle",
          desc: "Gardez une trace des dépenses du ménage et épargnez spécifiquement pour des objectifs communs.",
          features: {
            categorize: "Enregistrer les dépenses par catégorie",
            budgets: "Définir et surveiller les budgets mensuels",
            charts: "Graphiques et statistiques clairs",
            goals: "Définir et suivre les objectifs d'épargne",
            stats: "Comparaison avec les mois précédents"
          },
          useCases: {
            overview: "Enfin savoir où va l'argent chaque mois",
            vacation: "Épargner ensemble pour les vacances d'été"
          }
        },
        notes: {
          title: "Notes",
          tagline: "Garder une trace des choses importantes",
          desc: "Notes partagées pour tout ce que la famille devrait savoir.",
          features: {
            shared: "Notes partagées pour tout le monde",
            folders: "Dossiers pour une meilleure organisation",
            search: "Recherche plein texte dans toutes les notes",
            attachments: "Joindre des images et des fichiers",
            pin: "Épingler les notes importantes"
          },
          useCases: {
            wifi: "Mot de passe WiFi, codes de porte, numéros importants - tout à portée de main",
            contacts: "Coordonnées de la baby-sitter, de l'artisan, des voisins"
          }
        },
        tasks: {
          title: "Tâches",
          tagline: "Organiser le ménage",
          desc: "Répartissez équitablement les tâches ménagères et gardez une trace de qui fait quoi.",
          features: {
            create: "Créer et attribuer des tâches",
            recurring: "Tâches récurrentes (hebdomadaires, mensuelles)",
            assign: "Attribuer des tâches aux membres de la famille",
            reminders: "Rappels avant l'échéance",
            progress: "Voir les progrès et les statistiques"
          },
          useCases: {
            trash: "\"Sortir les poubelles\" - automatiquement pour Papa chaque mardi",
            kids: "Les enfants apprennent la responsabilité avec leurs propres petites tâches"
          }
        },
        // ✅ NOUVEAU: Module Foyer
        household: {
          title: "Foyer",
          tagline: "Gérer ensemble",
          desc: "Gérez votre foyer et invitez les membres de la famille. Tout sous un même toit numérique.",
          features: {
            create: "Créer et configurer le foyer",
            invite: "Inviter des membres par code ou lien",
            roles: "Autorisations administrateur et membre",
            profiles: "Profils des membres de la famille avec photo et rôle",
            activity: "Aperçu des activités - qui a contribué à quoi"
          },
          useCases: {
            family: "Rassembler toute la famille sous un même toit numérique",
            wg: "Parfait aussi pour les colocations - partager et organiser équitablement"
          }
        }
      }
    },
    legal: {
      privacy: {
        title: "Politique de confidentialité",
        lastUpdated: "Dernière mise à jour",
        date: "Janvier 2026",
        toc: "Table des matières",
        germany: "Allemagne",
        email: "E-mail",
        imprint: "Mentions légales",
        imprintLink: "Vers les mentions légales",
        
        sections: {
          responsible: "Responsable",
          overview: "Aperçu des traitements",
          legal: "Base juridique",
          security: "Mesures de sécurité",
          deletion: "Suppression des données",
          hosting: "Hébergement web",
          auth: "Inscription et authentification",
          contact: "Contact",
          rights: "Vos droits"
        },
        
        overviewText: "L'aperçu suivant résume les types de données traitées et les finalités du traitement.",
        dataTypes: "Types de données traitées",
        dataTypesList: {
          inventory: "Données d'inventaire (ex: noms, adresses)",
          content: "Données de contenu (ex: saisies de formulaires)",
          usage: "Données d'utilisation (ex: pages visitées, heures d'accès)",
          meta: "Métadonnées/données de communication (ex: adresses IP)"
        },
        affectedPersons: "Personnes concernées",
        affectedPersonsList: {
          users: "Utilisateurs de notre plateforme"
        },
        purposes: "Finalités du traitement",
        purposesList: {
          provision: "Fourniture de nos services en ligne",
          contract: "Services contractuels et assistance",
          security: "Mesures de sécurité",
          inquiries: "Demandes de contact et communication"
        },
        
        legalText: "Ci-dessous, nous vous informons des bases juridiques du traitement des données:",
        legalBasis: {
          consent: "Consentement (Art. 6 par. 1 let. a RGPD)",
          consentDesc: "Vous avez donné votre consentement au traitement.",
          contract: "Exécution du contrat (Art. 6 par. 1 let. b RGPD)",
          contractDesc: "Le traitement est nécessaire à l'exécution du contrat.",
          legitimate: "Intérêts légitimes (Art. 6 par. 1 let. f RGPD)",
          legitimateDesc: "Le traitement est basé sur nos intérêts légitimes."
        },
        legalBasisLabel: "Base juridique",
        legitimateInterests: "Intérêts légitimes (Art. 6 par. 1 let. f RGPD)",
        contractFulfillment: "Exécution du contrat (Art. 6 par. 1 let. b RGPD)",
        
        securityText: "Nous prenons des mesures techniques et organisationnelles pour assurer un niveau de protection approprié.",
        securityMeasures: "Ces mesures comprennent",
        securityList: {
          ssl: "Cryptage SSL/TLS pour tous les transferts de données",
          passwords: "Stockage sécurisé des mots de passe (hachés)",
          updates: "Mises à jour de sécurité régulières",
          access: "Contrôle d'accès et gestion des autorisations",
          rls: "Row Level Security (RLS) pour l'isolation des données entre utilisateurs"
        },
        
        deletionText1: "Les données que nous traitons sont supprimées dès que le consentement est révoqué ou que d'autres autorisations expirent.",
        deletionText2: "Si les données ne sont pas supprimées car elles sont nécessaires à d'autres fins légalement autorisées, le traitement est limité.",
        deletionText3: "Lorsque vous supprimez votre compte, toutes les données associées seront supprimées dans les 30 jours.",
        
        hostingText: "Notre hébergeur traite les données suivantes pour fournir ce site web:",
        azureText: "Nous utilisons Microsoft Azure comme plateforme cloud. Les serveurs sont situés dans l'UE (Région: Europe de l'Ouest).",
        provider: "Fournisseur",
        privacyPolicy: "Politique de confidentialité",
        collectedData: "Données collectées",
        collectedDataList: {
          ip: "Adresse IP",
          datetime: "Date et heure de la demande",
          timezone: "Décalage horaire par rapport à GMT",
          page: "Page demandée",
          status: "Code de statut HTTP",
          size: "Volume de données transférées",
          browser: "Navigateur et système d'exploitation"
        },
        
        authText: "Nous utilisons un service d'authentification externe pour l'inscription et la connexion.",
        authentication: "Authentification",
        logtoText: "Nous utilisons Logto Cloud pour une authentification sécurisée. Logto stocke vos données de connexion de manière cryptée.",
        registrationData: "Données collectées lors de l'inscription",
        registrationDataList: {
          email: "Adresse e-mail",
          username: "Nom d'utilisateur (optionnel)",
          password: "Mot de passe (stocké crypté)",
          timestamp: "Horodatage de l'inscription"
        },
        accountDeletion: "Suppression de compte",
        accountDeletionText: "Vous pouvez supprimer votre compte à tout moment. Envoyez un e-mail à kontakt@planivio.de ou utilisez la fonction de suppression dans les paramètres du compte.",
        
        contactText: "Lors d'un contact avec nous, les données de l'utilisateur sont traitées pour traiter la demande.",
        processedData: "Données traitées",
        contactDataList: "Nom, adresse e-mail, contenu du message",
        storageDuration: "Durée de stockage",
        storageDurationText: "Les données sont supprimées dès qu'elles ne sont plus nécessaires à la finalité de la collecte.",
        
        cookies: {
          title: "Cookies",
          text: "Nous utilisons uniquement des cookies techniquement nécessaires pour l'authentification.",
          used: "Cookies utilisés",
          name: "Nom",
          purpose: "Finalité",
          duration: "Durée",
          notUsed: "Nous n'utilisons PAS:",
          noTracking: "Cookies de suivi",
          noMarketing: "Cookies marketing",
          noAnalytics: "Cookies d'analyse (pas de Google Analytics etc.)",
          noSocial: "Cookies de réseaux sociaux",
          legalBasis: "Intérêts légitimes - techniquement requis pour le fonctionnement de l'application"
        },
        
        rightsText: "En tant que personne concernée, vous disposez des droits suivants en vertu du RGPD:",
        rights: {
          access: "Droit d'accès (Art. 15 RGPD)",
          accessText: "Vous avez le droit d'obtenir des informations sur vos données personnelles stockées chez nous.",
          rectification: "Droit de rectification (Art. 16 RGPD)",
          rectificationText: "Vous avez le droit de rectifier des données inexactes ou de compléter des données incomplètes.",
          erasure: "Droit à l'effacement (Art. 17 RGPD)",
          erasureText: "Vous avez le droit à l'effacement de vos données, sauf si des obligations légales de conservation existent.",
          restriction: "Droit à la limitation (Art. 18 RGPD)",
          restrictionText: "Vous avez le droit de demander la limitation du traitement de vos données.",
          portability: "Droit à la portabilité (Art. 20 RGPD)",
          portabilityText: "Vous avez le droit de recevoir vos données dans un format courant.",
          objection: "Droit d'opposition (Art. 21 RGPD)",
          objectionText: "Vous avez le droit de vous opposer au traitement de vos données.",
          complaint: "Droit de réclamation",
          complaintText: "Vous avez le droit de déposer une plainte auprès d'une autorité de contrôle:"
        },
        
        changes: {
          title: "Modifications de cette politique de confidentialité",
          text: "Nous nous réservons le droit d'adapter cette politique de confidentialité. La version actuelle est toujours disponible sur cette page."
        }
      },
      imprint: {
        title: "Mentions légales",
        germany: "Allemagne",
        email: "E-mail",
        lastUpdated: "Dernière mise à jour"
      },
      terms: {
        title: "Conditions générales",
        lastUpdated: "Dernière mise à jour",
        legalNote: "Ces conditions sont rédigées selon le droit allemand. En cas de traduction, la version allemande prévaut.",
        provider: "ci-après \"Fournisseur\"",
        
        s1: { title: "§ 1 Champ d'application", p1: "Ces conditions générales s'appliquent à l'utilisation de la plateforme Planivio, proposée par:", p2: "Les conditions s'appliquent à tous les utilisateurs, qu'ils utilisent une offre gratuite ou payante.", p3: "En s'inscrivant, l'utilisateur accepte ces conditions.", p4: "Les conditions divergentes de l'utilisateur ne sont pas reconnues sauf accord express du Fournisseur." },
        s2: { title: "§ 2 Conclusion du contrat", p1: "La présentation des services sur le site ne constitue pas une offre juridiquement contraignante.", p2: "En s'inscrivant, l'utilisateur soumet une offre contraignante de conclure un contrat.", p3: "Le contrat est conclu lors de la confirmation de l'inscription par e-mail.", p4: "Le texte du contrat est conservé par le Fournisseur et peut être demandé par l'utilisateur." },
        s3: { title: "§ 3 Description des services", p1: "Planivio est une plateforme web d'organisation du foyer pour les familles.", p2: "La plateforme comprend les modules suivants:", modules: { shopping: "Listes de courses", recipes: "Gestion de recettes", calendar: "Calendrier familial", budget: "Suivi du budget", notes: "Notes", tasks: "Tâches", ai: "Assistant IA" }, p3: "L'étendue des fonctions peut varier selon le forfait choisi.", p4: "Le Fournisseur est autorisé à développer et adapter les services." },
        s4: { title: "§ 4 Phase bêta", p1: "Pendant la phase bêta, toutes les fonctionnalités sont gratuites.", p2: "Le Fournisseur peut mettre fin à la phase bêta à tout moment et introduire des forfaits payants.", p3: "Les utilisateurs bêta seront informés au moins 30 jours avant l'introduction des forfaits payants.", p4: "Il n'y a aucun droit à la poursuite de l'utilisation gratuite après la phase bêta." },
        s5: { title: "§ 5 Prix et paiement", p1: "Les prix actuels sont disponibles sur le site.", p2: "Tous les prix incluent la TVA légale.", p3: "Le paiement est effectué à l'avance. Les méthodes de paiement suivantes sont acceptées:", payment: { card: "Carte de crédit (Visa, Mastercard)", sepa: "Prélèvement SEPA", paypal: "PayPal" }, p4: "En cas de retard de paiement, l'accès au service peut être restreint.", p5: "Le Fournisseur se réserve le droit d'ajuster les prix. Ceux-ci seront annoncés au moins 30 jours à l'avance." },
        s6: { title: "§ 6 Durée du contrat et résiliation", p1: "Le contrat est conclu pour une durée indéterminée.", p2: "Les deux parties peuvent résilier le contrat à tout moment avec un préavis de 30 jours pour la fin du mois.", p3: "La résiliation peut être faite par e-mail ou via les paramètres du compte.", p4: "Le droit de résiliation extraordinaire pour motif grave reste inchangé." },
        s7: { title: "§ 7 Droits d'utilisation et obligations", p1: "L'utilisateur reçoit un droit non exclusif et non transférable d'utiliser la plateforme.", p2: "L'utilisateur est responsable de la sécurité de ses données d'accès.", p3: "Il est interdit à l'utilisateur de:", duties: { illegal: "Utiliser la plateforme à des fins illégales", commercial: "Utiliser la plateforme commercialement sans autorisation", impair: "Nuire au fonctionnement de la plateforme", share: "Partager les données d'accès avec des tiers", reverse: "Décompiler ou rétro-ingénierer le logiciel" }, p4: "En cas de violation, l'accès peut être suspendu et le contrat résilié." },
        s8: { title: "§ 8 Protection des données", p1: "Le Fournisseur traite les données personnelles conformément au RGPD.", p2a: "Les détails sur le traitement des données se trouvent dans la", privacyLink: "Politique de confidentialité", p2b: ".", p3: "L'utilisateur consent au traitement de ses données dans le cadre de l'utilisation." },
        s9: { title: "§ 9 Responsabilité", p1: "Le Fournisseur est entièrement responsable des dommages résultant d'atteintes à la vie, au corps ou à la santé.", p2: "Pour les autres dommages, le Fournisseur n'est responsable qu'en cas de dol ou de négligence grave.", p3: "La responsabilité pour négligence légère est limitée aux dommages prévisibles et typiques du contrat.", p4: "La responsabilité selon la loi sur la responsabilité des produits reste inchangée.", p5: "Pendant la phase bêta, aucune garantie n'est donnée pour la disponibilité ou l'absence d'erreurs." },
        s10: { title: "§ 10 Garantie", p1: "Le Fournisseur garantit la fourniture contractuelle des services.", p2: "Les défauts seront corrigés dans un délai raisonnable.", p3: "En cas de défauts importants, l'utilisateur a le droit à une réduction ou à une résiliation.", p4: "La garantie ne couvre pas les erreurs causées par une utilisation incorrecte." },
        s11: { title: "§ 11 Modifications des conditions", p1: "Le Fournisseur peut modifier ces conditions si cela est objectivement justifié.", p2: "Les modifications seront communiquées à l'utilisateur par e-mail.", p3: "Si l'utilisateur ne s'oppose pas dans les 30 jours, les nouvelles conditions sont réputées acceptées.", p4: "Le droit d'opposition et les conséquences du silence seront indiqués dans la notification de modification." },
        s12: { title: "§ 12 Dispositions finales", p1: "Le droit allemand s'applique, à l'exclusion de la Convention des Nations Unies sur les contrats.", p2: "Le lieu de juridiction est Nuremberg si l'utilisateur est commerçant.", p3: "Si des dispositions individuelles sont invalides, la validité des autres dispositions reste inchangée.", p4: "La Commission européenne fournit une plateforme de règlement des litiges en ligne: https://ec.europa.eu/consumers/odr" }
      }
    },
    invite: {
      title: "Vous êtes invité!",
      subtitle: { 
        try: "Essayez", 
        smartApp: "– l'application intelligente pour l'organisation familiale.", 
        freeBeta: "Gratuit maintenant pendant la bêta!" 
      },
      orVisit: "Ou visitez directement",
      features: { 
        shopping: "Listes de courses", 
        recipes: "Recettes", 
        calendar: "Calendrier", 
        ai: "Assistant IA" 
      },
      freeDuringBeta: "Gratuit pendant la bêta",
      buttons: { 
        copyLink: "Copier le lien", 
        copied: "Copié!", 
        share: "Partager", 
        print: "Imprimer", 
        email: "E-mail" 
      },
      printTip: "Conseil : Imprimez cette page et accrochez-la sur votre réfrigérateur !",
      share: {
        title: "Planivio - Gestionnaire de foyer",
        text: "Essayez Planivio – l'application intelligente pour l'organisation familiale !",
        whatsappText: "Salut ! Découvre Planivio – une super application pour l'organisation familiale :",
        telegramText: "Essayez Planivio – l'application intelligente pour l'organisation familiale !",
        emailSubject: "Invitation à Planivio",
        emailBody: "Salut !\n\nJ'utilise Planivio pour notre organisation familiale et je voulais te le recommander. C'est actuellement gratuit en bêta !\n\nRegarde ici :"
      }
    }
  }
};

// ===========================
// SUPPORTED LANGUAGES
// ===========================
export const LANGUAGES = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' }
];

const STORAGE_KEY = 'planivio_language';
const DEFAULT_LANGUAGE = 'de';

// ===========================
// CONTEXT
// ===========================
const I18nContext = createContext(null);

// ===========================
// HELPER: Get nested value
// ===========================
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
};

// ===========================
// HELPER: Interpolate variables
// ===========================
const interpolate = (text, variables = {}) => {
  if (!text || typeof text !== 'string') return text;
  
  return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return variables[key] !== undefined ? variables[key] : match;
  });
};

// ===========================
// HELPER: Detect browser language
// ===========================
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  const shortLang = browserLang.split('-')[0].toLowerCase();
  
  const supported = LANGUAGES.find(l => l.code === shortLang);
  return supported ? shortLang : DEFAULT_LANGUAGE;
};

// ===========================
// PROVIDER
// ===========================
export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && translations[stored]) {
      return stored;
    }
    return detectBrowserLanguage();
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
    console.log(`🌍 Language set to: ${language}`);
  }, [language]);

  const t = useCallback((key, variables = {}) => {
    const value = getNestedValue(translations[language], key);
    
    if (value === null) {
      const fallback = getNestedValue(translations[DEFAULT_LANGUAGE], key);
      if (fallback === null) {
        console.warn(`⚠️ Missing translation: ${key}`);
        return key;
      }
      return interpolate(fallback, variables);
    }
    
    return interpolate(value, variables);
  }, [language]);

  const setLanguage = useCallback((langCode) => {
    if (translations[langCode]) {
      setLanguageState(langCode);
    } else {
      console.warn(`⚠️ Unsupported language: ${langCode}`);
    }
  }, []);

  const currentLanguage = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];

  const value = {
    language,
    setLanguage,
    t,
    currentLanguage,
    languages: LANGUAGES
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

// ===========================
// HOOK
// ===========================
export function useTranslation() {
  const context = useContext(I18nContext);
  
  if (!context) {
    throw new Error('useTranslation must be used within I18nProvider');
  }
  
  return context;
}

export default I18nContext;